// GENERATED FILE - do not edit.
// Produced by packages/client/src/cli/generate.ts from packages/api/openapi.json.
// Run `pnpm generate` to regenerate.

import type * as S from './types.js';
import { request, type RequestOptions } from '../runtime.js';

export interface AccountAssetsArgs {
  path: { accountId: string };
}

export type AccountAssetsResult = {
  assets: Array<S.CustomerAsset>;
};

/** What this customer has, where, and its warranty */
export function accountAssets(args: AccountAssetsArgs, options?: RequestOptions): Promise<AccountAssetsResult> {
  return request<AccountAssetsResult>({
    operationId: "accountAssets",
    method: "GET",
    path: "/accounts/{accountId}/assets",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface AccountHealthArgs {
  path: { accountId: string };
}

export type AccountHealthResult = {
  health: (S.AccountHealth | null);
};

/** A customer's health, and why */
export function accountHealth(args: AccountHealthArgs, options?: RequestOptions): Promise<AccountHealthResult> {
  return request<AccountHealthResult>({
    operationId: "accountHealth",
    method: "GET",
    path: "/accounts/{accountId}/health",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface AccountOnboardingArgs {
  path: { accountId: string };
}

export type AccountOnboardingResult = {
  plan: (S.OnboardingPlan | null);
};

/** A customer's onboarding checklist */
export function accountOnboarding(args: AccountOnboardingArgs, options?: RequestOptions): Promise<AccountOnboardingResult> {
  return request<AccountOnboardingResult>({
    operationId: "accountOnboarding",
    method: "GET",
    path: "/accounts/{accountId}/onboarding",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface AccountPartnershipArgs {
  path: { accountId: string };
}

export type AccountPartnershipResult = {
  partner: boolean;
  partnerSince: (string | null);
  protectionDays: number;
  contacts: Array<{
    contactId: string;
    name: string;
  }>;
};

/** Whether a company is a partner, and who a partner link can go to */
export function accountPartnership(args: AccountPartnershipArgs, options?: RequestOptions): Promise<AccountPartnershipResult> {
  return request<AccountPartnershipResult>({
    operationId: "accountPartnership",
    method: "GET",
    path: "/accounts/{accountId}/partner",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface AccountPreferencesArgs {
  path: { accountId: string };
}

export type AccountPreferencesResult = {
  preferences: S.AccountPreferences;
  effective: S.EffectivePreferences;
};

/** What a company prefers, and what applies once defaults fill the gaps */
export function accountPreferences(args: AccountPreferencesArgs, options?: RequestOptions): Promise<AccountPreferencesResult> {
  return request<AccountPreferencesResult>({
    operationId: "accountPreferences",
    method: "GET",
    path: "/accounts/{accountId}/preferences",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface AccountTargetListsArgs {
  path: { accountId: string };
}

export type AccountTargetListsResult = {
  lists: Array<{
    listId: string;
    name: string;
    tier: number;
  }>;
};

/** The target lists this company is on, and its tier on each */
export function accountTargetLists(args: AccountTargetListsArgs, options?: RequestOptions): Promise<AccountTargetListsResult> {
  return request<AccountTargetListsResult>({
    operationId: "accountTargetLists",
    method: "GET",
    path: "/accounts/{accountId}/target-lists",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface AccountTerritoryArgs {
  path: { accountId: string };
}

export type AccountTerritoryResult = {
  current: (string | null);
  moves: Array<{
    at: string;
    fromName: (string | null);
    toName: (string | null);
    fromOwner: (string | null);
    toOwner: (string | null);
    reason: "created" | "changed" | "rebalanced";
  }>;
};

/** The territory a company is in, and every move it has made */
export function accountTerritory(args: AccountTerritoryArgs, options?: RequestOptions): Promise<AccountTerritoryResult> {
  return request<AccountTerritoryResult>({
    operationId: "accountTerritory",
    method: "GET",
    path: "/accounts/{accountId}/territory",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface AcknowledgeEventsArgs {
  path: { subscriberId: string };
  body: {
  seq: number;
};
}

export type AcknowledgeEventsResult = S.EventSubscriber;

/** Everything up to this sequence number is handled */
export function acknowledgeEvents(args: AcknowledgeEventsArgs, options?: RequestOptions): Promise<AcknowledgeEventsResult> {
  return request<AcknowledgeEventsResult>({
    operationId: "acknowledgeEvents",
    method: "POST",
    path: "/developer/stream/subscribers/{subscriberId}/ack",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ActivityOutcomesArgs {
  query?: { days?: number };
}

export type ActivityOutcomesResult = {
  days: number;
  closed: {
    won: number;
    lost: number;
    winRate: (number | null);
  };
  minDeals: number;
  types: Array<{
    type: string;
    label: string;
    withIt: {
      won: number;
      lost: number;
      winRate: (number | null);
    };
    withoutIt: {
      won: number;
      lost: number;
      winRate: (number | null);
    };
    medianBeforeWin: (number | null);
    medianBeforeLoss: (number | null);
    enough: boolean;
  }>;
};

/** For each kind of activity, the win rate of closed deals that had it beside those that did not */
export function activityOutcomes(args: ActivityOutcomesArgs, options?: RequestOptions): Promise<ActivityOutcomesResult> {
  return request<ActivityOutcomesResult>({
    operationId: "activityOutcomes",
    method: "GET",
    path: "/analytics/activity-outcomes",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface AddAccountAddressArgs {
  path: { accountId: string };
  body: {
  kind: "billing" | "shipping" | "site" | "branch";
  label?: (string | null);
  line1: string;
  line2?: (string | null);
  city?: (string | null);
  region?: (string | null);
  postcode?: (string | null);
  country: string;
  makeDefault?: boolean;
};
}

export type AddAccountAddressResult = {
  address: S.AccountAddress;
};

/** Add an address */
export function addAccountAddress(args: AddAccountAddressArgs, options?: RequestOptions): Promise<AddAccountAddressResult> {
  return request<AddAccountAddressResult>({
    operationId: "addAccountAddress",
    method: "POST",
    path: "/accounts/{accountId}/addresses",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AddAssetArgs {
  body: {
  folderId: (string | null);
  kind: "image" | "document" | "snippet" | "template";
  name: string;
  tags: Array<string>;
  filename?: (string | null);
  contentType?: (string | null);
  contentBase64?: (string | null);
  body?: (string | null);
  blocks?: (Array<S.ContentBlock> | null);
};
}

export type AddAssetResult = S.Asset;

/** Add an image, document, snippet or template to the library */
export function addAsset(args: AddAssetArgs, options?: RequestOptions): Promise<AddAssetResult> {
  return request<AddAssetResult>({
    operationId: "addAsset",
    method: "POST",
    path: "/assets",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AddBillingStageArgs {
  path: { orderId: string };
  body: {
  label: string;
  percent: string;
};
}

export type AddBillingStageResult = {
  stages: Array<S.BillingStage>;
  plannedPercent: string;
};

/** Add a stage to the payment plan */
export function addBillingStage(args: AddBillingStageArgs, options?: RequestOptions): Promise<AddBillingStageResult> {
  return request<AddBillingStageResult>({
    operationId: "addBillingStage",
    method: "POST",
    path: "/sales-orders/{orderId}/billing-plan",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AddCurrencyArgs {
  body: {
  code: string;
};
}

export type AddCurrencyResult = {
  currencies: Array<string>;
};

/** Start using another currency */
export function addCurrency(args: AddCurrencyArgs, options?: RequestOptions): Promise<AddCurrencyResult> {
  return request<AddCurrencyResult>({
    operationId: "addCurrency",
    method: "POST",
    path: "/setup/currencies",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AddIpAllowlistEntryArgs {
  body: {
  cidr: string;
  label: string;
};
}

export type AddIpAllowlistEntryResult = S.IpAllowlistEntry;

/** Allow a network; refused if it would shut you out */
export function addIpAllowlistEntry(args: AddIpAllowlistEntryArgs, options?: RequestOptions): Promise<AddIpAllowlistEntryResult> {
  return request<AddIpAllowlistEntryResult>({
    operationId: "addIpAllowlistEntry",
    method: "POST",
    path: "/setup/ip-allowlist",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AddRelationshipArgs {
  path: { accountId: string };
  body: {
  fromContactId: string;
  toContactId: string;
  kind: "reports-to" | "dotted-line" | "influences";
  note?: (string | null);
};
}

export type AddRelationshipResult = {
  line: {
    relationshipId: string;
    fromContactId: string;
    toContactId: string;
    kind: "reports-to" | "dotted-line" | "influences";
    note: (string | null);
  };
};

/** Record a reporting line, a dotted line, or influence */
export function addRelationship(args: AddRelationshipArgs, options?: RequestOptions): Promise<AddRelationshipResult> {
  return request<AddRelationshipResult>({
    operationId: "addRelationship",
    method: "POST",
    path: "/accounts/{accountId}/relationships",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AddSendingDomainArgs {
  body: {
  domain: string;
};
}

export type AddSendingDomainResult = {
  domains: Array<{
    domainId: string;
    domain: string;
    status: "pending" | "verified" | "failed";
    lastCheckedAt: (string | null);
    records: Array<{
      recordId: string;
      kind: "spf" | "dkim" | "dmarc" | "return-path";
      host: string;
      recordType: "TXT" | "CNAME" | "MX";
      expectedValue: string;
      observedValue: (string | null);
      verified: boolean;
      checkedAt: (string | null);
      explanation: string;
    }>;
  }>;
};

/** Add a domain to send from; returns the four records to publish */
export function addSendingDomain(args: AddSendingDomainArgs, options?: RequestOptions): Promise<AddSendingDomainResult> {
  return request<AddSendingDomainResult>({
    operationId: "addSendingDomain",
    method: "POST",
    path: "/marketing/sending-domains",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AdjustForecastArgs {
  body: {
  userId: string;
  periodStart: string;
  periodEnd: string;
  amount: string;
  note: string;
};
}

export type AdjustForecastResult = S.ForecastSubmission;

/** A manager's view of somebody's number, held beside it */
export function adjustForecast(args: AdjustForecastArgs, options?: RequestOptions): Promise<AdjustForecastResult> {
  return request<AdjustForecastResult>({
    operationId: "adjustForecast",
    method: "PUT",
    path: "/forecast/submissions/adjustment",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AdvanceDunningArgs {
  path: { invoiceId: string };
  body: {
  on?: (string | null);
};
}

export type AdvanceDunningResult = {
  outcome: S.DunningOutcome;
};

/** Climb one invoice ladder by one rung */
export function advanceDunning(args: AdvanceDunningArgs, options?: RequestOptions): Promise<AdvanceDunningResult> {
  return request<AdvanceDunningResult>({
    operationId: "advanceDunning",
    method: "POST",
    path: "/invoices/{invoiceId}/dunning",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AmendSalesOrderLineArgs {
  path: { orderId: string; lineId: string };
  body: {
  quantity: (string | null);
  unitPrice: (string | null);
  reason: string;
};
}

export type AmendSalesOrderLineResult = {
  order: S.SalesOrder;
};

/** Change a line, with the reason recorded */
export function amendSalesOrderLine(args: AmendSalesOrderLineArgs, options?: RequestOptions): Promise<AmendSalesOrderLineResult> {
  return request<AmendSalesOrderLineResult>({
    operationId: "amendSalesOrderLine",
    method: "POST",
    path: "/sales-orders/{orderId}/lines/{lineId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AnswerQualificationCriterionArgs {
  path: { objectKey: string; recordId: string };
  body: {
  frameworkId: string;
  criterion: string;
  state: "met" | "not-met" | "refused" | "unknown";
  evidence: (string | null);
};
}

export type AnswerQualificationCriterionResult = {
  qualification: S.Qualification;
};

/** Answer one criterion, with what it is based on */
export function answerQualificationCriterion(args: AnswerQualificationCriterionArgs, options?: RequestOptions): Promise<AnswerQualificationCriterionResult> {
  return request<AnswerQualificationCriterionResult>({
    operationId: "answerQualificationCriterion",
    method: "POST",
    path: "/records/{objectKey}/{recordId}/qualification",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AnswerSurveyArgs {
  body: {
  token: string;
  score: number;
  comment?: (string | null);
};
}

export type AnswerSurveyResult = {
  answered: boolean;
  reason: ("expired" | "already" | null);
};

/** The customer answers - once */
export function answerSurvey(args: AnswerSurveyArgs, options?: RequestOptions): Promise<AnswerSurveyResult> {
  return request<AnswerSurveyResult>({
    operationId: "answerSurvey",
    method: "POST",
    path: "/public/survey/answer",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AnswerVisitFormArgs {
  path: { visitId: string };
  body: {
  formId: string;
  answers: {
    [key: string]: (string | number | boolean | null);
  };
};
}

export type AnswerVisitFormResult = {
  answered: S.AnsweredVisitForm;
};

/** Fill in a checklist at a visit */
export function answerVisitForm(args: AnswerVisitFormArgs, options?: RequestOptions): Promise<AnswerVisitFormResult> {
  return request<AnswerVisitFormResult>({
    operationId: "answerVisitForm",
    method: "POST",
    path: "/visits/{visitId}/forms",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ApplyConfigurationArgs {
  body: {
  bundle: S.ConfigBundle;
  selected: Array<string>;
};
}

export type ApplyConfigurationResult = {
  applied: Array<{
    kind: string;
    key: string;
  }>;
  skipped: Array<{
    kind: string;
    key: string;
    reason: string;
  }>;
};

/** Apply the items somebody chose */
export function applyConfiguration(args: ApplyConfigurationArgs, options?: RequestOptions): Promise<ApplyConfigurationResult> {
  return request<ApplyConfigurationResult>({
    operationId: "applyConfiguration",
    method: "POST",
    path: "/setup/environments/apply",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ApplyImportArgs {
  path: { runId: string };
  body: {
  rows: Array<{
    [key: string]: string;
  }>;
};
}

export type ApplyImportResult = {
  runId: string;
  created: number;
  failed: number;
  reversibleUntil: string;
};

/** Write the rows a dry run approved */
export function applyImport(args: ApplyImportArgs, options?: RequestOptions): Promise<ApplyImportResult> {
  return request<ApplyImportResult>({
    operationId: "applyImport",
    method: "POST",
    path: "/setup/imports/{runId}/apply",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ApplyRebalanceArgs {
  body: Record<string, never>;
}

export type ApplyRebalanceResult = {
  moved: number;
};

/** Move every company to the territory it belongs in, keeping the history */
export function applyRebalance(args: ApplyRebalanceArgs, options?: RequestOptions): Promise<ApplyRebalanceResult> {
  return request<ApplyRebalanceResult>({
    operationId: "applyRebalance",
    method: "POST",
    path: "/setup/territories/rebalance",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ApplyTagArgs {
  path: { objectKey: string; recordId: string };
  body: {
  label: string;
};
}

export type ApplyTagResult = {
  tags: Array<S.Tag>;
};

/** Put a tag on a record, by label */
export function applyTag(args: ApplyTagArgs, options?: RequestOptions): Promise<ApplyTagResult> {
  return request<ApplyTagResult>({
    operationId: "applyTag",
    method: "POST",
    path: "/records/{objectKey}/{recordId}/tags",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ApproveCommissionStatementArgs {
  body: {
  userId: string;
  periodStart: string;
  periodEnd: string;
};
}

export type ApproveCommissionStatementResult = S.CommissionCalculation;

/** Approve a period's statement, freezing it */
export function approveCommissionStatement(args: ApproveCommissionStatementArgs, options?: RequestOptions): Promise<ApproveCommissionStatementResult> {
  return request<ApproveCommissionStatementResult>({
    operationId: "approveCommissionStatement",
    method: "POST",
    path: "/commission/statement/approve",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ArchiveProductArgs {
  path: { productId: string };
}

export type ArchiveProductResult = {
  product: S.Product;
};

/** Stop selling a product without removing it */
export function archiveProduct(args: ArchiveProductArgs, options?: RequestOptions): Promise<ArchiveProductResult> {
  return request<ArchiveProductResult>({
    operationId: "archiveProduct",
    method: "POST",
    path: "/products/{productId}/archive",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ArchiveQuoteTemplateArgs {
  path: { templateId: string };
  body: Record<string, never>;
}

export type ArchiveQuoteTemplateResult = {
  archived: boolean;
};

/** Stop offering a quotation template */
export function archiveQuoteTemplate(args: ArchiveQuoteTemplateArgs, options?: RequestOptions): Promise<ArchiveQuoteTemplateResult> {
  return request<ArchiveQuoteTemplateResult>({
    operationId: "archiveQuoteTemplate",
    method: "POST",
    path: "/quote-templates/{templateId}/archive",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AskDataArgs {
  body: {
  question: string;
};
}

export type AskDataResult = {
  question: string;
  answeredBy: "model" | "keywords";
  interpretation: string;
  spec: S.ReportSpec;
  result: S.ReportResult;
  modelProblem: (string | null);
};

/** A question in words, answered as a report, with how it was understood */
export function askData(args: AskDataArgs, options?: RequestOptions): Promise<AskDataResult> {
  return request<AskDataResult>({
    operationId: "askData",
    method: "POST",
    path: "/reports/ask",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AssetUsageArgs {
  path: { assetId: string };
}

export type AssetUsageResult = {
  uses: Array<S.AssetUse>;
};

/** Every email and page that uses an asset */
export function assetUsage(args: AssetUsageArgs, options?: RequestOptions): Promise<AssetUsageResult> {
  return request<AssetUsageResult>({
    operationId: "assetUsage",
    method: "GET",
    path: "/assets/{assetId}/usage",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface AssignCaseArgs {
  path: { caseId: string };
  body: {
  userId: (string | null);
};
}

export type AssignCaseResult = {
  assigned: boolean;
};

/** Give a case to somebody, or take it back */
export function assignCase(args: AssignCaseArgs, options?: RequestOptions): Promise<AssignCaseResult> {
  return request<AssignCaseResult>({
    operationId: "assignCase",
    method: "POST",
    path: "/cases/{caseId}/assignee",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AssignCommissionPlanArgs {
  body: {
  userId: string;
  planId: (string | null);
};
}

export type AssignCommissionPlanResult = {
  plans: Array<S.CommissionPlan>;
};

/** Put a person on a commission plan, or take them off */
export function assignCommissionPlan(args: AssignCommissionPlanArgs, options?: RequestOptions): Promise<AssignCommissionPlanResult> {
  return request<AssignCommissionPlanResult>({
    operationId: "assignCommissionPlan",
    method: "POST",
    path: "/commission/assignments",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AssignJobArgs {
  body: {
  visitId: string;
  technicianId: (string | null);
};
}

export type AssignJobResult = S.FieldJob;

/** Send a technician, or take them off the job */
export function assignJob(args: AssignJobArgs, options?: RequestOptions): Promise<AssignJobResult> {
  return request<AssignJobResult>({
    operationId: "assignJob",
    method: "POST",
    path: "/field-jobs/assign",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface AttachmentVersionsArgs {
  path: { attachmentId: string };
}

export type AttachmentVersionsResult = {
  versions: Array<S.Attachment>;
};

/** Every version of one file, newest first */
export function attachmentVersions(args: AttachmentVersionsArgs, options?: RequestOptions): Promise<AttachmentVersionsResult> {
  return request<AttachmentVersionsResult>({
    operationId: "attachmentVersions",
    method: "GET",
    path: "/files/{attachmentId}/versions",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface AttributionReportArgs {
  query: { modelA: "first" | "last" | "linear" | "u-shaped" | "time-decay"; modelB: "first" | "last" | "linear" | "u-shaped" | "time-decay"; from?: string; to?: string; dimension: "utm_campaign" | "utm_source" | "utm_medium" };
}

export type AttributionReportResult = S.AttributionReport;

/** Won revenue credited across touches, by two models side by side */
export function attributionReport(args: AttributionReportArgs, options?: RequestOptions): Promise<AttributionReportResult> {
  return request<AttributionReportResult>({
    operationId: "attributionReport",
    method: "GET",
    path: "/marketing/attribution",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface AuditTrailArgs {
  query?: { objectKey?: string; actorId?: string; actorType?: "user" | "agent" | "system"; since?: string; until?: string; limit?: number; before?: string };
}

export type AuditTrailResult = {
  entries: Array<S.AuditRow>;
};

/** Who changed what, across the workspace */
export function auditTrail(args: AuditTrailArgs, options?: RequestOptions): Promise<AuditTrailResult> {
  return request<AuditTrailResult>({
    operationId: "auditTrail",
    method: "GET",
    path: "/settings/audit",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type AutomationUsageResult = {
  limits: {
    runsPerHour: number;
    stepsPerRun: number;
    externalCallsPerHour: number;
  };
  runsLastHour: number;
  externalCallsLastHour: number;
  runShare: number;
  callShare: number;
};

/** Automation runs and outside calls this hour, against the workspace’s limits */
export function automationUsage(options?: RequestOptions): Promise<AutomationUsageResult> {
  return request<AutomationUsageResult>({
    operationId: "automationUsage",
    method: "GET",
    path: "/automation/usage",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type BackupStateResult = S.BackupState;

/** Whether this workspace has a backup, and when it was taken */
export function backupState(options?: RequestOptions): Promise<BackupStateResult> {
  return request<BackupStateResult>({
    operationId: "backupState",
    method: "GET",
    path: "/setup/backups",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface BestSendTimeArgs {
  path: { objectKey: "lead" | "contact"; recordId: string };
}

export type BestSendTimeResult = {
  at: string;
  basis: "person" | "workspace" | "none";
  hour: (number | null);
  clicks: number;
  opens: number;
  reason: string;
};

/** The hour this person usually engages with email, and how sure that is */
export function bestSendTime(args: BestSendTimeArgs, options?: RequestOptions): Promise<BestSendTimeResult> {
  return request<BestSendTimeResult>({
    operationId: "bestSendTime",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/send-time",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface BillingPlanArgs {
  path: { orderId: string };
}

export type BillingPlanResult = {
  stages: Array<S.BillingStage>;
  plannedPercent: string;
};

/** The order's payment plan: its stages and what has been billed or requested against each */
export function billingPlan(args: BillingPlanArgs, options?: RequestOptions): Promise<BillingPlanResult> {
  return request<BillingPlanResult>({
    operationId: "billingPlan",
    method: "GET",
    path: "/sales-orders/{orderId}/billing-plan",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface BookDealViewingArgs {
  path: { dealId: string };
  body: {
  startsAt: string;
  endsAt: string;
  location?: (string | null);
  purpose?: (string | null);
  ownerId?: (string | null);
};
}

export type BookDealViewingResult = {
  viewing: S.Viewing;
};

/** Book a viewing for this deal */
export function bookDealViewing(args: BookDealViewingArgs, options?: RequestOptions): Promise<BookDealViewingResult> {
  return request<BookDealViewingResult>({
    operationId: "bookDealViewing",
    method: "POST",
    path: "/deals/{dealId}/viewings",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface BookFromPageArgs {
  path: { workspace: string; slug: string };
  body: {
  startsAt: string;
  name: string;
  email: string;
  note?: (string | null);
};
}

export type BookFromPageResult = {
  booked: boolean;
  reason: ("taken" | "not-offered" | "missing" | null);
  startsAt: (string | null);
  endsAt: (string | null);
  calendarToken: (string | null);
};

/** Book one of the times the page offered */
export function bookFromPage(args: BookFromPageArgs, options?: RequestOptions): Promise<BookFromPageResult> {
  return request<BookFromPageResult>({
    operationId: "bookFromPage",
    method: "POST",
    path: "/public/booking-pages/{workspace}/{slug}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface BookLeadViewingArgs {
  path: { leadId: string };
  body: {
  startsAt: string;
  endsAt: string;
  location?: (string | null);
  purpose?: (string | null);
  ownerId?: (string | null);
};
}

export type BookLeadViewingResult = {
  viewing: S.Viewing;
};

/** Book a viewing for this lead */
export function bookLeadViewing(args: BookLeadViewingArgs, options?: RequestOptions): Promise<BookLeadViewingResult> {
  return request<BookLeadViewingResult>({
    operationId: "bookLeadViewing",
    method: "POST",
    path: "/leads/{leadId}/viewings",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface BookSlotArgs {
  body: {
  token: string;
  userId: string;
  startsAt: string;
  endsAt: string;
  note?: (string | null);
};
}

export type BookSlotResult = {
  bookingId: (string | null);
  taken: boolean;
  refusal: (S.PortalRefusal | null);
};

/** Take a time */
export function bookSlot(args: BookSlotArgs, options?: RequestOptions): Promise<BookSlotResult> {
  return request<BookSlotResult>({
    operationId: "bookSlot",
    method: "POST",
    path: "/portal/booking",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export type BrandingSettingsResult = S.BrandingSettings;

/** Read the workspace’s branding */
export function brandingSettings(options?: RequestOptions): Promise<BrandingSettingsResult> {
  return request<BrandingSettingsResult>({
    operationId: "brandingSettings",
    method: "GET",
    path: "/settings/branding",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface BuildCallListArgs {
  body: {
  name: string;
  objectKey: "contact" | "lead";
  recordIds: Array<string>;
  builtFrom: (string | null);
};
}

export type BuildCallListResult = {
  list: S.CallList;
};

/** Turn a set of people into a list to ring, in the order given */
export function buildCallList(args: BuildCallListArgs, options?: RequestOptions): Promise<BuildCallListResult> {
  return request<BuildCallListResult>({
    operationId: "buildCallList",
    method: "POST",
    path: "/call-lists",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface BulkJobErrorsArgs {
  path: { jobId: string };
}

export type BulkJobErrorsResult = {
  fileName: string;
  csv: string;
  rows: number;
};

/** The error file, as CSV */
export function bulkJobErrors(args: BulkJobErrorsArgs, options?: RequestOptions): Promise<BulkJobErrorsResult> {
  return request<BulkJobErrorsResult>({
    operationId: "bulkJobErrors",
    method: "GET",
    path: "/bulk/jobs/{jobId}/errors",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface CalculateCommissionArgs {
  query: { userId: string; periodStart: string; periodEnd: string };
}

export type CalculateCommissionResult = S.CommissionCalculation;

/** What a person has earned in a period, deal by deal and band by band */
export function calculateCommission(args: CalculateCommissionArgs, options?: RequestOptions): Promise<CalculateCommissionResult> {
  return request<CalculateCommissionResult>({
    operationId: "calculateCommission",
    method: "GET",
    path: "/commission/statement",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface CampaignAttributionArgs {
  path: { campaignId: string };
  query: { modelA: "first" | "last" | "linear" | "u-shaped" | "time-decay"; modelB: "first" | "last" | "linear" | "u-shaped" | "time-decay"; from?: string; to?: string };
}

export type CampaignAttributionResult = {
  trackingCode: (string | null);
  report: (S.AttributionReport | null);
};

/** One campaign's credited revenue, by two models, through its tracking code */
export function campaignAttribution(args: CampaignAttributionArgs, options?: RequestOptions): Promise<CampaignAttributionResult> {
  return request<CampaignAttributionResult>({
    operationId: "campaignAttribution",
    method: "GET",
    path: "/campaigns/{campaignId}/attribution",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export interface CampaignBudgetArgs {
  path: { campaignId: string };
}

export type CampaignBudgetResult = S.CampaignBudget;

/** Planned against actual spend, with the lines and the campaigns under it */
export function campaignBudget(args: CampaignBudgetArgs, options?: RequestOptions): Promise<CampaignBudgetResult> {
  return request<CampaignBudgetResult>({
    operationId: "campaignBudget",
    method: "GET",
    path: "/campaigns/{campaignId}/budget",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface CampaignEconomicsArgs {
  query: { from: string; to: string; model?: "first" | "last" | "linear" | "u-shaped" | "time-decay" };
}

export type CampaignEconomicsResult = {
  currency: string;
  model: "first" | "last" | "linear" | "u-shaped" | "time-decay";
  campaigns: Array<S.CampaignEconomics>;
  unattributedDeals: number;
};

/** Cost per won deal, by campaign */
export function campaignEconomics(args: CampaignEconomicsArgs, options?: RequestOptions): Promise<CampaignEconomicsResult> {
  return request<CampaignEconomicsResult>({
    operationId: "campaignEconomics",
    method: "GET",
    path: "/revenue/unit-economics/campaigns",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface CampaignInfluenceArgs {
  query: { periodStart: string; periodEnd: string };
}

export type CampaignInfluenceResult = {
  periodStart: string;
  periodEnd: string;
  currency: string;
  wonDeals: number;
  wonValue: string;
  otherCurrency: number;
  campaigns: Array<{
    campaignId: string;
    name: string;
    reachedDeals: number;
    reachedValue: string;
    engagedDeals: number;
    engagedValue: string;
  }>;
};

/** Which campaigns reached - and engaged - the people on deals won in a period, before they were won */
export function campaignInfluence(args: CampaignInfluenceArgs, options?: RequestOptions): Promise<CampaignInfluenceResult> {
  return request<CampaignInfluenceResult>({
    operationId: "campaignInfluence",
    method: "GET",
    path: "/analytics/campaign-influence",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface CancelBulkJobArgs {
  path: { jobId: string };
}

export type CancelBulkJobResult = S.BulkJob;

/** Stop a job */
export function cancelBulkJob(args: CancelBulkJobArgs, options?: RequestOptions): Promise<CancelBulkJobResult> {
  return request<CancelBulkJobResult>({
    operationId: "cancelBulkJob",
    method: "POST",
    path: "/bulk/jobs/{jobId}/cancel",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface CancelEventArgs {
  path: { eventId: string };
  body: Record<string, never>;
}

export type CancelEventResult = S.CalendarEvent;

/** Call a meeting off */
export function cancelEvent(args: CancelEventArgs, options?: RequestOptions): Promise<CancelEventResult> {
  return request<CancelEventResult>({
    operationId: "cancelEvent",
    method: "POST",
    path: "/events/{eventId}/cancel",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CancelEventRegistrationArgs {
  path: { registrationId: string };
  body: Record<string, never>;
}

export type CancelEventRegistrationResult = {
  registrations: Array<S.EventRegistration>;
};

/** Take somebody off an event; the longest-waiting person gets the place */
export function cancelEventRegistration(args: CancelEventRegistrationArgs, options?: RequestOptions): Promise<CancelEventRegistrationResult> {
  return request<CancelEventRegistrationResult>({
    operationId: "cancelEventRegistration",
    method: "POST",
    path: "/event-registrations/{registrationId}/cancel",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CancelRestoreArgs {
  path: { restoreId: string };
}

export type CancelRestoreResult = {
  ok: true;
};

/** Abandon a previewed restore */
export function cancelRestore(args: CancelRestoreArgs, options?: RequestOptions): Promise<CancelRestoreResult> {
  return request<CancelRestoreResult>({
    operationId: "cancelRestore",
    method: "POST",
    path: "/setup/restores/{restoreId}/cancel",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface CancelSalesOrderArgs {
  path: { orderId: string };
  body: {
  reason: string;
};
}

export type CancelSalesOrderResult = {
  order: S.SalesOrder;
};

/** Cancel an order, saying why */
export function cancelSalesOrder(args: CancelSalesOrderArgs, options?: RequestOptions): Promise<CancelSalesOrderResult> {
  return request<CancelSalesOrderResult>({
    operationId: "cancelSalesOrder",
    method: "POST",
    path: "/sales-orders/{orderId}/cancel",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CapacityPlanArgs {
  query: { periodStart: string; periodEnd: string; coverage?: number };
}

export type CapacityPlanResult = {
  periodStart: string;
  periodEnd: string;
  currency: string;
  coverageWanted: number;
  people: Array<{
    userId: string;
    name: string;
    target: (string | null);
    won: string;
    remaining: (string | null);
    openPipeline: string;
    coverage: (number | null);
    short: boolean;
  }>;
  territories: Array<{
    territoryId: string;
    name: string;
    accounts: number;
    people: number;
    accountsPerPerson: (number | null);
    uncovered: boolean;
  }>;
  headcount: {
    teamTarget: string;
    perPersonPerQuarter: (string | null);
    quartersOfHistory: number;
    needed: (number | null);
    have: number;
  };
  otherCurrency: number;
};

/** Pipeline coverage per person, people per territory, and the headcount the quota needs */
export function capacityPlan(args: CapacityPlanArgs, options?: RequestOptions): Promise<CapacityPlanResult> {
  return request<CapacityPlanResult>({
    operationId: "capacityPlan",
    method: "GET",
    path: "/forecast/capacity",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface CaptureAtEventArgs {
  body: {
  eventId: string;
  clientRef: string;
  method: "badge" | "form";
  firstName?: (string | null);
  lastName?: (string | null);
  email?: (string | null);
  phone?: (string | null);
  company?: (string | null);
  title?: (string | null);
  interest?: ("hot" | "warm" | "cold" | null);
  note?: (string | null);
  capturedAt?: (string | null);
};
}

export type CaptureAtEventResult = {
  captureId: string;
  duplicate: boolean;
  leadId: (string | null);
  contactId: (string | null);
  created: boolean;
};

/** Somebody met at an event: a lead, or the meeting on the person already known */
export function captureAtEvent(args: CaptureAtEventArgs, options?: RequestOptions): Promise<CaptureAtEventResult> {
  return request<CaptureAtEventResult>({
    operationId: "captureAtEvent",
    method: "POST",
    path: "/event-captures",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CaptureFieldOrderArgs {
  body: {
  dealId: string;
  contactId: string;
  lines: Array<{
    productId: string;
    quantity: string;
  }>;
};
}

export type CaptureFieldOrderResult = {
  order: S.SalesOrder;
  quoteNumber: string;
  shortLines: Array<{
    productId: string;
    name: string;
    quantity: string;
    available: string;
  }>;
};

/** Take an order on site: priced from the list, agreed by a named contact, confirmed at once */
export function captureFieldOrder(args: CaptureFieldOrderArgs, options?: RequestOptions): Promise<CaptureFieldOrderResult> {
  return request<CaptureFieldOrderResult>({
    operationId: "captureFieldOrder",
    method: "POST",
    path: "/field-orders",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CaptureForecastSnapshotArgs {
  body: {
  fiscalYear: number;
  periodKind: "month" | "quarter" | "year";
};
}

export type CaptureForecastSnapshotResult = {
  weekStart: string;
  rowsTaken: number;
  written: number;
  firstThisWeek: boolean;
};

/** Record this week's forecast, once */
export function captureForecastSnapshot(args: CaptureForecastSnapshotArgs, options?: RequestOptions): Promise<CaptureForecastSnapshotResult> {
  return request<CaptureForecastSnapshotResult>({
    operationId: "captureForecastSnapshot",
    method: "POST",
    path: "/forecast/snapshots",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export type CapturePipelineSnapshotResult = {
  pipelines: number;
};

/** Record what every pipeline looks like today */
export function capturePipelineSnapshot(options?: RequestOptions): Promise<CapturePipelineSnapshotResult> {
  return request<CapturePipelineSnapshotResult>({
    operationId: "capturePipelineSnapshot",
    method: "POST",
    path: "/pipelines/snapshots",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface CaseAssetArgs {
  path: { caseId: string };
}

export type CaseAssetResult = {
  asset: (S.CustomerAsset | null);
  choices: Array<S.CustomerAsset>;
};

/** The asset a case is about, and whether it is under warranty */
export function caseAsset(args: CaseAssetArgs, options?: RequestOptions): Promise<CaseAssetResult> {
  return request<CaseAssetResult>({
    operationId: "caseAsset",
    method: "GET",
    path: "/cases/{caseId}/asset",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface CaseFromConversationArgs {
  path: { conversationId: string };
  body: {
  subject?: (string | null);
};
}

export type CaseFromConversationResult = {
  caseId: string;
  number: string;
  created: boolean;
};

/** Turn this chat into a case, with its transcript */
export function caseFromConversation(args: CaseFromConversationArgs, options?: RequestOptions): Promise<CaseFromConversationResult> {
  return request<CaseFromConversationResult>({
    operationId: "caseFromConversation",
    method: "POST",
    path: "/chat/conversations/{conversationId}/case",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CaseJobsArgs {
  path: { caseId: string };
}

export type CaseJobsResult = {
  jobs: Array<S.FieldJob>;
};

/** A case's on-site jobs */
export function caseJobs(args: CaseJobsArgs, options?: RequestOptions): Promise<CaseJobsResult> {
  return request<CaseJobsResult>({
    operationId: "caseJobs",
    method: "GET",
    path: "/cases/{caseId}/jobs",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ChangePlanArgs {
  body: {
  plan: string;
  acknowledgeLosing?: number;
};
}

export type ChangePlanResult = S.PlanChangePreview;

/** Move this workspace to another plan */
export function changePlan(args: ChangePlanArgs, options?: RequestOptions): Promise<ChangePlanResult> {
  return request<ChangePlanResult>({
    operationId: "changePlan",
    method: "POST",
    path: "/setup/plan",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CheckFlowArgs {
  path: { flowId: string };
  body: {
  graph: S.FlowGraph;
  palette: "automation" | "journey" | "chatbot" | "approval";
};
}

export type CheckFlowResult = {
  problems: Array<S.FlowProblem>;
};

/** What would stop this flow being activated */
export function checkFlow(args: CheckFlowArgs, options?: RequestOptions): Promise<CheckFlowResult> {
  return request<CheckFlowResult>({
    operationId: "checkFlow",
    method: "POST",
    path: "/flows/{flowId}/check",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CheckInToVisitArgs {
  path: { visitId: string };
  body: {
  locationConsent: boolean;
  latitude: (number | null);
  longitude: (number | null);
  accuracyM: (number | null);
};
}

export type CheckInToVisitResult = {
  visit: S.PlannedVisit;
  distanceFromAccountM: (number | null);
  accuracyM: (number | null);
};

/** Record that you arrived, with your position if you agree to send it */
export function checkInToVisit(args: CheckInToVisitArgs, options?: RequestOptions): Promise<CheckInToVisitResult> {
  return request<CheckInToVisitResult>({
    operationId: "checkInToVisit",
    method: "POST",
    path: "/visits/{visitId}/arrive",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ChurnByMonthArgs {
  query: { from: string; to: string };
}

export type ChurnByMonthResult = {
  months: Array<S.ChurnRates>;
};

/** Churn month by month, so a trend is visible */
export function churnByMonth(args: ChurnByMonthArgs, options?: RequestOptions): Promise<ChurnByMonthResult> {
  return request<ChurnByMonthResult>({
    operationId: "churnByMonth",
    method: "GET",
    path: "/revenue/churn/monthly",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ChurnedAccountsArgs {
  query: { from: string; to: string };
}

export type ChurnedAccountsResult = {
  accounts: Array<S.ChurnedAccount>;
};

/** What a window lost, biggest first */
export function churnedAccounts(args: ChurnedAccountsArgs, options?: RequestOptions): Promise<ChurnedAccountsResult> {
  return request<ChurnedAccountsResult>({
    operationId: "churnedAccounts",
    method: "GET",
    path: "/revenue/churn/accounts",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ChurnRatesArgs {
  query: { from: string; to: string };
}

export type ChurnRatesResult = {
  rates: S.ChurnRates;
};

/** Logo churn and revenue churn for a window */
export function churnRates(args: ChurnRatesArgs, options?: RequestOptions): Promise<ChurnRatesResult> {
  return request<ChurnRatesResult>({
    operationId: "churnRates",
    method: "GET",
    path: "/revenue/churn",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ClaimConversationArgs {
  path: { conversationId: string };
}

export type ClaimConversationResult = {
  claimed: boolean;
};

/** Take a waiting conversation */
export function claimConversation(args: ClaimConversationArgs, options?: RequestOptions): Promise<ClaimConversationResult> {
  return request<ClaimConversationResult>({
    operationId: "claimConversation",
    method: "POST",
    path: "/chat/conversations/{conversationId}/claim",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ClearForecastAdjustmentArgs {
  query: { userId: string; periodStart: string; periodEnd: string };
}

export type ClearForecastAdjustmentResult = S.ForecastSubmission;

/** Take back an adjustment, leaving the submitted figure standing */
export function clearForecastAdjustment(args: ClearForecastAdjustmentArgs, options?: RequestOptions): Promise<ClearForecastAdjustmentResult> {
  return request<ClearForecastAdjustmentResult>({
    operationId: "clearForecastAdjustment",
    method: "DELETE",
    path: "/forecast/submissions/adjustment",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface CloneDealArgs {
  path: { dealId: string };
  body: {
  name: string;
  accountId: (string | null);
  closeDate: (string | null);
};
}

export type CloneDealResult = {
  dealId: string;
  copied: {
    contacts: number;
    team: number;
    competitors: number;
  };
};

/** Start a deal from the shape of this one */
export function cloneDeal(args: CloneDealArgs, options?: RequestOptions): Promise<CloneDealResult> {
  return request<CloneDealResult>({
    operationId: "cloneDeal",
    method: "POST",
    path: "/deals/{dealId}/clone",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CloseMarketingEventArgs {
  path: { eventId: string };
  body: Record<string, never>;
}

export type CloseMarketingEventResult = {
  attended: number;
  noShow: number;
  attendedStarted: number;
  noShowStarted: number;
};

/** Close an event: no-shows are marked and each path starts its follow-up journey */
export function closeMarketingEvent(args: CloseMarketingEventArgs, options?: RequestOptions): Promise<CloseMarketingEventResult> {
  return request<CloseMarketingEventResult>({
    operationId: "closeMarketingEvent",
    method: "POST",
    path: "/marketing-events/{eventId}/close",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CoachingViewArgs {
  query?: { days?: number };
}

export type CoachingViewResult = {
  days: number;
  currency: string;
  rows: Array<{
    userId: string;
    name: string;
    activity: {
      call: number;
      email: number;
      meeting: number;
      task: number;
    };
    won: number;
    lost: number;
    wonValue: string;
    winRate: (number | null);
    weekly: Array<number>;
  }>;
  median: {
    activity: {
      call: number;
      email: number;
      meeting: number;
      task: number;
    };
    won: number;
    winRate: (number | null);
  };
};

/** Each rep’s activity and outcomes beside the team median, for coaching conversations */
export function coachingView(args: CoachingViewArgs, options?: RequestOptions): Promise<CoachingViewResult> {
  return request<CoachingViewResult>({
    operationId: "coachingView",
    method: "GET",
    path: "/analytics/coaching",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface CohortAnalysisArgs {
  query?: { basis?: "first-purchase" | "created"; grain?: "month" | "quarter"; periods?: number };
}

export type CohortAnalysisResult = {
  basis: "first-purchase" | "created";
  grain: "month" | "quarter";
  currency: string;
  periods: number;
  rows: Array<{
    cohort: string;
    size: number;
    retained: Array<number>;
    retentionShare: Array<number>;
    revenue: Array<string>;
    expansion: Array<(number | null)>;
  }>;
  otherCurrencyDeals: number;
};

/** Companies grouped by when they first bought (or were added), and how many bought again each period after */
export function cohortAnalysis(args: CohortAnalysisArgs, options?: RequestOptions): Promise<CohortAnalysisResult> {
  return request<CohortAnalysisResult>({
    operationId: "cohortAnalysis",
    method: "GET",
    path: "/analytics/cohorts",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface CommitRestoreArgs {
  path: { restoreId: string };
}

export type CommitRestoreResult = S.RestoreResult;

/** Do the restore that was previewed */
export function commitRestore(args: CommitRestoreArgs, options?: RequestOptions): Promise<CommitRestoreResult> {
  return request<CommitRestoreResult>({
    operationId: "commitRestore",
    method: "POST",
    path: "/setup/restores/{restoreId}/commit",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ComplaintsRegisterArgs {
  query?: { days?: number };
}

export type ComplaintsRegisterResult = {
  policy: {
    acknowledgeDays: number;
    respondDays: number;
  };
  complaints: Array<{
    caseId: string;
    number: string;
    subject: string;
    category: string;
    complaintAt: string;
    acknowledgeDue: string;
    acknowledgedAt: (string | null);
    respondDue: string;
    finalResponseAt: (string | null);
    acknowledgeBreached: boolean;
    respondBreached: boolean;
  }>;
};

/** Every formal complaint and whether its deadlines were met */
export function complaintsRegister(args: ComplaintsRegisterArgs, options?: RequestOptions): Promise<ComplaintsRegisterResult> {
  return request<ComplaintsRegisterResult>({
    operationId: "complaintsRegister",
    method: "GET",
    path: "/complaints",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface CompleteAccessRequestArgs {
  path: { requestId: string };
}

export type CompleteAccessRequestResult = {
  request: S.DsrRequest;
  export: S.SubjectExport;
};

/** Answer an access request, and record what the export contained */
export function completeAccessRequest(args: CompleteAccessRequestArgs, options?: RequestOptions): Promise<CompleteAccessRequestResult> {
  return request<CompleteAccessRequestResult>({
    operationId: "completeAccessRequest",
    method: "POST",
    path: "/setup/privacy/requests/{requestId}/export",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface CompleteErasureRequestArgs {
  path: { requestId: string };
  body: {
  acknowledged: Array<{
    table: string;
    count: number;
  }>;
};
}

export type CompleteErasureRequestResult = S.DsrRequest;

/** Carry out an erasure, against the confirmation that was read */
export function completeErasureRequest(args: CompleteErasureRequestArgs, options?: RequestOptions): Promise<CompleteErasureRequestResult> {
  return request<CompleteErasureRequestResult>({
    operationId: "completeErasureRequest",
    method: "POST",
    path: "/setup/privacy/requests/{requestId}/erase",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CompleteJobArgs {
  body: {
  visitId: string;
  note: string;
};
}

export type CompleteJobResult = S.FieldJob;

/** The technician reports the job done; the report goes on the case */
export function completeJob(args: CompleteJobArgs, options?: RequestOptions): Promise<CompleteJobResult> {
  return request<CompleteJobResult>({
    operationId: "completeJob",
    method: "POST",
    path: "/field-jobs/complete",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CompleteNextStepArgs {
  path: { stepId: string };
  body: {
  outcome: string;
  note?: (string | null);
  next: S.NextStepDecision;
};
}

export type CompleteNextStepResult = {
  completedId: string;
  scheduledId: (string | null);
  recordName: string;
  relatedType: string;
  relatedId: string;
};

/** Complete a step and decide what happens next, in one operation */
export function completeNextStep(args: CompleteNextStepArgs, options?: RequestOptions): Promise<CompleteNextStepResult> {
  return request<CompleteNextStepResult>({
    operationId: "completeNextStep",
    method: "POST",
    path: "/work/{stepId}/complete",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CompleteOnboardingArgs {
  body: {
  industry: string;
  teamSize: string;
  sells: string;
  cycle: string;
  channels: Array<string>;
};
}

export type CompleteOnboardingResult = {
  pack: string;
  packLabel: string;
  items: Array<S.AppliedItem>;
  applied: number;
  skipped: number;
  waiting: number;
};

/** Apply an industry starter pack from the wizard answers */
export function completeOnboarding(args: CompleteOnboardingArgs, options?: RequestOptions): Promise<CompleteOnboardingResult> {
  return request<CompleteOnboardingResult>({
    operationId: "completeOnboarding",
    method: "POST",
    path: "/onboarding",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export type ComplianceEvidenceResult = {
  generatedAt: string;
  workspace: string;
  controls: Array<{
    key: string;
    status: "met" | "partial" | "not-met" | "hosting";
    evidence: string;
    frameworks: Array<string>;
  }>;
};

/** Security controls with measured status and evidence, mapped to SOC 2 and ISO 27001 */
export function complianceEvidence(options?: RequestOptions): Promise<ComplianceEvidenceResult> {
  return request<ComplianceEvidenceResult>({
    operationId: "complianceEvidence",
    method: "GET",
    path: "/setup/compliance-evidence",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ConfirmConsentArgs {
  body: {
  token: string;
};
}

export type ConfirmConsentResult = {
  outcome: "confirmed" | "already-confirmed" | "expired" | "invalid";
  wording: (string | null);
};

/** The person confirms, by the link in their email, that they meant it */
export function confirmConsent(args: ConfirmConsentArgs, options?: RequestOptions): Promise<ConfirmConsentResult> {
  return request<ConfirmConsentResult>({
    operationId: "confirmConsent",
    method: "POST",
    path: "/portal/consent/confirm",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ConfirmSalesOrderArgs {
  body: {
  quoteId: string;
};
}

export type ConfirmSalesOrderResult = {
  order: S.SalesOrder;
};

/** Turn an accepted quote into an order */
export function confirmSalesOrder(args: ConfirmSalesOrderArgs, options?: RequestOptions): Promise<ConfirmSalesOrderResult> {
  return request<ConfirmSalesOrderResult>({
    operationId: "confirmSalesOrder",
    method: "POST",
    path: "/sales-orders",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ConsentEvidenceArgs {
  path: { objectKey: string; recordId: string };
}

export type ConsentEvidenceResult = {
  evidence: Array<S.ConsentEvidence>;
};

/** Every consent a person gave or withdrew, with the words shown and where it came from */
export function consentEvidence(args: ConsentEvidenceArgs, options?: RequestOptions): Promise<ConsentEvidenceResult> {
  return request<ConsentEvidenceResult>({
    operationId: "consentEvidence",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/consent-evidence",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ConsoleSetPlanArgs {
  path: { workspace: string };
  body: {
  plan: string;
  acknowledgeLosing?: number;
};
}

export type ConsoleSetPlanResult = S.ConsoleTenant;

/** Move a workspace to another plan */
export function consoleSetPlan(args: ConsoleSetPlanArgs, options?: RequestOptions): Promise<ConsoleSetPlanResult> {
  return request<ConsoleSetPlanResult>({
    operationId: "consoleSetPlan",
    method: "PUT",
    path: "/console/tenants/{workspace}/plan",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ConsoleSignInArgs {
  body: {
  email: string;
  password: string;
};
}

export type ConsoleSignInResult = S.ConsoleSignIn;

/** Sign a staff member in to the internal console */
export function consoleSignIn(args: ConsoleSignInArgs, options?: RequestOptions): Promise<ConsoleSignInResult> {
  return request<ConsoleSignInResult>({
    operationId: "consoleSignIn",
    method: "POST",
    path: "/console/sign-in",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export type ConsoleSignOutResult = {
  ok: true;
};

/** End a console session */
export function consoleSignOut(options?: RequestOptions): Promise<ConsoleSignOutResult> {
  return request<ConsoleSignOutResult>({
    operationId: "consoleSignOut",
    method: "POST",
    path: "/console/sign-out",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ConsoleStartVisitArgs {
  path: { workspace: string };
  body: {
  userId: string;
  reason: string;
};
}

export type ConsoleStartVisitResult = S.ConsoleVisit;

/** Go into a workspace as one of its people */
export function consoleStartVisit(args: ConsoleStartVisitArgs, options?: RequestOptions): Promise<ConsoleStartVisitResult> {
  return request<ConsoleStartVisitResult>({
    operationId: "consoleStartVisit",
    method: "POST",
    path: "/console/tenants/{workspace}/visit",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ConsoleTenantArgs {
  path: { workspace: string };
}

export type ConsoleTenantResult = S.ConsoleTenantDetail;

/** One workspace: health, usage, consent and who has visited */
export function consoleTenant(args: ConsoleTenantArgs, options?: RequestOptions): Promise<ConsoleTenantResult> {
  return request<ConsoleTenantResult>({
    operationId: "consoleTenant",
    method: "GET",
    path: "/console/tenants/{workspace}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ConsoleTenantsArgs {
  query?: { search?: string };
}

export type ConsoleTenantsResult = S.ConsoleTenants;

/** Every workspace, with whether support has been let in */
export function consoleTenants(args: ConsoleTenantsArgs, options?: RequestOptions): Promise<ConsoleTenantsResult> {
  return request<ConsoleTenantsResult>({
    operationId: "consoleTenants",
    method: "GET",
    path: "/console/tenants",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ConsoleWhoAmIResult = S.ConsoleMe;

/** Who is signed in to the console */
export function consoleWhoAmI(options?: RequestOptions): Promise<ConsoleWhoAmIResult> {
  return request<ConsoleWhoAmIResult>({
    operationId: "consoleWhoAmI",
    method: "GET",
    path: "/console/me",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ContactPreferencesArgs {
  path: { contactId: string };
}

export type ContactPreferencesResult = {
  effective: S.EffectivePreferences;
};

/** What applies to a person, from their own preferences and their company’s */
export function contactPreferences(args: ContactPreferencesArgs, options?: RequestOptions): Promise<ContactPreferencesResult> {
  return request<ContactPreferencesResult>({
    operationId: "contactPreferences",
    method: "GET",
    path: "/contacts/{contactId}/preferences",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ContactReferralsArgs {
  path: { contactId: string };
}

export type ContactReferralsResult = S.ReferrerSummary;

/** Who this contact has referred, what it came to, and the rewards */
export function contactReferrals(args: ContactReferralsArgs, options?: RequestOptions): Promise<ContactReferralsResult> {
  return request<ContactReferralsResult>({
    operationId: "contactReferrals",
    method: "GET",
    path: "/contacts/{contactId}/referrals",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ConvertLeadArgs {
  path: { leadId: string };
  body: {
  accountId?: (string | null);
  createDeal?: boolean;
  dealName?: (string | null);
  dealAmount?: (string | null);
  pipelineId?: (string | null);
};
}

export type ConvertLeadResult = {
  leadId: string;
  accountId: string;
  contactId: string;
  dealId: (string | null);
  reversibleUntil: string;
};

/** Create a company, a person and a deal from a lead */
export function convertLead(args: ConvertLeadArgs, options?: RequestOptions): Promise<ConvertLeadResult> {
  return request<ConvertLeadResult>({
    operationId: "convertLead",
    method: "POST",
    path: "/leads/{leadId}/convert",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ConvertProformaArgs {
  path: { proformaId: string };
}

export type ConvertProformaResult = {
  proforma: S.Proforma;
  invoiceId: string;
};

/** The pro-forma was paid: raise the invoice for exactly what it requested */
export function convertProforma(args: ConvertProformaArgs, options?: RequestOptions): Promise<ConvertProformaResult> {
  return request<ConvertProformaResult>({
    operationId: "convertProforma",
    method: "POST",
    path: "/proformas/{proformaId}/convert",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface CountSegmentNowArgs {
  path: { segmentId: string };
}

export type CountSegmentNowResult = {
  memberCount: number;
  countedAt: string;
  sample: Array<{
    id: string;
    label: string;
  }>;
};

/** Count a saved audience and remember the number */
export function countSegmentNow(args: CountSegmentNowArgs, options?: RequestOptions): Promise<CountSegmentNowResult> {
  return request<CountSegmentNowResult>({
    operationId: "countSegmentNow",
    method: "POST",
    path: "/segments/{segmentId}/count",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface CreateApiTokenArgs {
  body: {
  name: string;
  scopes: Array<string>;
  expiresAt?: (string | null);
};
}

export type CreateApiTokenResult = {
  token: S.ApiToken;
  secret: string;
};

/** Issue a token, and show its secret once */
export function createApiToken(args: CreateApiTokenArgs, options?: RequestOptions): Promise<CreateApiTokenResult> {
  return request<CreateApiTokenResult>({
    operationId: "createApiToken",
    method: "POST",
    path: "/setup/api-tokens",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateAssetFolderArgs {
  body: {
  name: string;
  parentId: (string | null);
  editorRoleId: (string | null);
};
}

export type CreateAssetFolderResult = S.AssetFolder;

/** Add a folder, optionally one only a role may change */
export function createAssetFolder(args: CreateAssetFolderArgs, options?: RequestOptions): Promise<CreateAssetFolderResult> {
  return request<CreateAssetFolderResult>({
    operationId: "createAssetFolder",
    method: "POST",
    path: "/assets/folders",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateBiFeedArgs {
  body: {
  objectKey: string;
};
}

export type CreateBiFeedResult = {
  feed: S.BiFeed;
  url: string;
  connect: {
    [key: string]: string;
  };
};

/** A BI feed for one object; its address is shown once, with a line for each BI tool */
export function createBiFeed(args: CreateBiFeedArgs, options?: RequestOptions): Promise<CreateBiFeedResult> {
  return request<CreateBiFeedResult>({
    operationId: "createBiFeed",
    method: "POST",
    path: "/data-export/feeds",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateCaseArgs {
  body: {
  subject: string;
  contactId?: (string | null);
  accountId?: (string | null);
  priority?: "low" | "normal" | "high" | "urgent";
  source?: "manual" | "email" | "portal" | "chat" | "phone";
  body?: (string | null);
};
}

export type CreateCaseResult = {
  caseId: string;
  number: string;
};

/** Raise a case */
export function createCase(args: CreateCaseArgs, options?: RequestOptions): Promise<CreateCaseResult> {
  return request<CreateCaseResult>({
    operationId: "createCase",
    method: "POST",
    path: "/cases",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateContactArgs {
  body: {
  firstName?: string;
  lastName?: string;
  title?: (string | null);
  accountId?: (string | null);
  ownerId?: (string | null);
  email?: (string | null);
  phone?: (string | null);
};
}

export type CreateContactResult = {
  id: string;
};

/** Add a contact */
export function createContact(args: CreateContactArgs, options?: RequestOptions): Promise<CreateContactResult> {
  return request<CreateContactResult>({
    operationId: "createContact",
    method: "POST",
    path: "/contacts",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateCustomFieldArgs {
  path: { objectKey: string };
  body: {
  fieldKey: string;
  label: string;
  type: "text" | "textarea" | "email" | "phone" | "url" | "number" | "decimal" | "currency" | "percent" | "date" | "datetime" | "boolean" | "picklist" | "multipicklist" | "lookup" | "user" | "file" | "tags" | "address" | "formula" | "rollup";
  required?: boolean;
  helpText?: (string | null);
  options?: Array<{
    value: string;
    label: string;
  }>;
};
}

export type CreateCustomFieldResult = {
  fieldId: string;
};

/** Add a custom field */
export function createCustomField(args: CreateCustomFieldArgs, options?: RequestOptions): Promise<CreateCustomFieldResult> {
  return request<CreateCustomFieldResult>({
    operationId: "createCustomField",
    method: "POST",
    path: "/setup/objects/{objectKey}/fields",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateCustomObjectArgs {
  body: {
  objectKey: string;
  label: string;
  plural: string;
  icon: string;
  sharing: "private" | "team" | "everyone";
};
}

export type CreateCustomObjectResult = {
  objects: Array<{
    objectKey: string;
    label: string;
    plural: string;
    icon: string;
    records: number;
  }>;
};

/** Create an object */
export function createCustomObject(args: CreateCustomObjectArgs, options?: RequestOptions): Promise<CreateCustomObjectResult> {
  return request<CreateCustomObjectResult>({
    operationId: "createCustomObject",
    method: "POST",
    path: "/setup/objects/custom",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateDerivedFieldArgs {
  path: { objectKey: string };
  body: {
  fieldKey: string;
  label: string;
  type: "formula" | "rollup";
  formula?: string;
  rollup?: {
    objectKey: string;
    foreignKey: string;
    fn: "count" | "sum" | "min" | "max";
    field: (string | null);
  };
};
}

export type CreateDerivedFieldResult = {
  fields: Array<{
    fieldKey: string;
    label: string;
    type: "formula" | "rollup";
    formula: (string | null);
    rollup: ({
      objectKey: string;
      foreignKey: string;
      fn: "count" | "sum" | "min" | "max";
      field: (string | null);
    } | null);
  }>;
};

/** Add a formula or rollup field */
export function createDerivedField(args: CreateDerivedFieldArgs, options?: RequestOptions): Promise<CreateDerivedFieldResult> {
  return request<CreateDerivedFieldResult>({
    operationId: "createDerivedField",
    method: "POST",
    path: "/setup/objects/{objectKey}/derived",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateEmbedArgs {
  body: {
  name: string;
  widget: string;
  objectKey?: (string | null);
  origins: Array<string>;
  tokenSeconds?: number;
};
}

export type CreateEmbedResult = S.Embed;

/** Let one widget be rendered on named origins */
export function createEmbed(args: CreateEmbedArgs, options?: RequestOptions): Promise<CreateEmbedResult> {
  return request<CreateEmbedResult>({
    operationId: "createEmbed",
    method: "POST",
    path: "/setup/embeds",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateEventArgs {
  body: {
  title: string;
  startsAt: string;
  endsAt: string;
  location?: (string | null);
  note?: (string | null);
  relatedType?: (string | null);
  relatedId?: (string | null);
  userIds?: Array<string>;
  contactIds?: Array<string>;
};
}

export type CreateEventResult = S.CalendarEvent;

/** Arrange a meeting */
export function createEvent(args: CreateEventArgs, options?: RequestOptions): Promise<CreateEventResult> {
  return request<CreateEventResult>({
    operationId: "createEvent",
    method: "POST",
    path: "/events",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateEventSubscriberArgs {
  body: {
  name: string;
  events: Array<string>;
  fromStart: boolean;
};
}

export type CreateEventSubscriberResult = S.EventSubscriber;

/** A new subscriber, starting at the end of the stream or at its beginning */
export function createEventSubscriber(args: CreateEventSubscriberArgs, options?: RequestOptions): Promise<CreateEventSubscriberResult> {
  return request<CreateEventSubscriberResult>({
    operationId: "createEventSubscriber",
    method: "POST",
    path: "/developer/stream/subscribers",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateFlowArgs {
  body: {
  palette: "automation" | "journey" | "chatbot" | "approval";
  name: string;
};
}

export type CreateFlowResult = {
  flowId: string;
};

/** Start a new flow */
export function createFlow(args: CreateFlowArgs, options?: RequestOptions): Promise<CreateFlowResult> {
  return request<CreateFlowResult>({
    operationId: "createFlow",
    method: "POST",
    path: "/flows",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateInboundMailboxArgs {
  body: {
  label: string;
  leadSource?: (string | null);
  purpose?: "leads" | "cases";
};
}

export type CreateInboundMailboxResult = {
  mailbox: S.InboundMailbox;
  token: string;
  signingSecret: string;
};

/** Create a mailbox, returning its address token and signing secret once */
export function createInboundMailbox(args: CreateInboundMailboxArgs, options?: RequestOptions): Promise<CreateInboundMailboxResult> {
  return request<CreateInboundMailboxResult>({
    operationId: "createInboundMailbox",
    method: "POST",
    path: "/inbound-mailboxes",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateInboundSourceArgs {
  body: {
  name: string;
  flowId: string;
};
}

export type CreateInboundSourceResult = {
  source: S.InboundSource;
  token: string;
};

/** A new inbound source; its token is returned once and only a hash is kept */
export function createInboundSource(args: CreateInboundSourceArgs, options?: RequestOptions): Promise<CreateInboundSourceResult> {
  return request<CreateInboundSourceResult>({
    operationId: "createInboundSource",
    method: "POST",
    path: "/developer/inbound-sources",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateObjectRecordArgs {
  path: { objectKey: string };
  body: {
  values: {
    [key: string]: (string | number | boolean | null);
  };
};
}

export type CreateObjectRecordResult = {
  recordId: string;
  warnings: Array<S.ValidationFinding>;
};

/** Create a record of any object */
export function createObjectRecord(args: CreateObjectRecordArgs, options?: RequestOptions): Promise<CreateObjectRecordResult> {
  return request<CreateObjectRecordResult>({
    operationId: "createObjectRecord",
    method: "POST",
    path: "/records/{objectKey}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreatePermissionSetArgs {
  body: {
  name: string;
  description?: string;
  objects?: Array<{
    objectKey: string;
    actions: Array<"read" | "create" | "update" | "delete" | "export">;
  }>;
  capabilities?: Array<string>;
};
}

export type CreatePermissionSetResult = S.PermissionSetSummary;

/** Create a custom set */
export function createPermissionSet(args: CreatePermissionSetArgs, options?: RequestOptions): Promise<CreatePermissionSetResult> {
  return request<CreatePermissionSetResult>({
    operationId: "createPermissionSet",
    method: "POST",
    path: "/setup/permission-sets",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateProductArgs {
  body: {
  sku: string;
  name: string;
  description?: (string | null);
  family?: (string | null);
  listPrice: string;
  currency: string;
  unit: string;
  active?: boolean;
};
}

export type CreateProductResult = {
  product: S.Product;
};

/** Add a product to the catalogue */
export function createProduct(args: CreateProductArgs, options?: RequestOptions): Promise<CreateProductResult> {
  return request<CreateProductResult>({
    operationId: "createProduct",
    method: "POST",
    path: "/products",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateQuoteArgs {
  path: { dealId: string };
  body: {
  name?: (string | null);
  templateId?: (string | null);
};
}

export type CreateQuoteResult = {
  quoteId: string;
  number: string;
};

/** Start a quote on a deal, blank or from a template */
export function createQuote(args: CreateQuoteArgs, options?: RequestOptions): Promise<CreateQuoteResult> {
  return request<CreateQuoteResult>({
    operationId: "createQuote",
    method: "POST",
    path: "/deals/{dealId}/quotes",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateScimTokenArgs {
  body: {
  label?: string;
  expiresInDays?: (number | null);
};
}

export type CreateScimTokenResult = {
  tokenId: string;
  token: string;
  expiresAt: (string | null);
};

/** Issue a directory provisioning token */
export function createScimToken(args: CreateScimTokenArgs, options?: RequestOptions): Promise<CreateScimTokenResult> {
  return request<CreateScimTokenResult>({
    operationId: "createScimToken",
    method: "POST",
    path: "/settings/sso/scim-tokens",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateSegmentArgs {
  body: {
  name: string;
  objectKey: string;
  conditions: S.RuleDocument;
};
}

export type CreateSegmentResult = {
  segment: S.Segment;
};

/** Save an audience */
export function createSegment(args: CreateSegmentArgs, options?: RequestOptions): Promise<CreateSegmentResult> {
  return request<CreateSegmentResult>({
    operationId: "createSegment",
    method: "POST",
    path: "/segments",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateSubscriptionArgs {
  body: {
  accountId: string;
  dealId?: (string | null);
  startedOn: string;
  currency?: string;
  planKey: string;
  planLabel: string;
  quantity: string;
  unitPrice: string;
  mrr: string;
};
}

export type CreateSubscriptionResult = {
  subscriptionId: string;
  number: string;
};

/** Start a subscription */
export function createSubscription(args: CreateSubscriptionArgs, options?: RequestOptions): Promise<CreateSubscriptionResult> {
  return request<CreateSubscriptionResult>({
    operationId: "createSubscription",
    method: "POST",
    path: "/subscriptions",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateTagArgs {
  body: {
  label: string;
  colour?: ("bronze" | "sky" | "grass" | "amber" | "rose" | "plum" | "slate" | null);
};
}

export type CreateTagResult = S.Tag;

/** Add a tag to the vocabulary */
export function createTag(args: CreateTagArgs, options?: RequestOptions): Promise<CreateTagResult> {
  return request<CreateTagResult>({
    operationId: "createTag",
    method: "POST",
    path: "/setup/tags",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateTargetListArgs {
  body: {
  name: string;
};
}

export type CreateTargetListResult = S.TargetList;

/** Start a target account list */
export function createTargetList(args: CreateTargetListArgs, options?: RequestOptions): Promise<CreateTargetListResult> {
  return request<CreateTargetListResult>({
    operationId: "createTargetList",
    method: "POST",
    path: "/target-lists",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateUnitArgs {
  body: {
  label: string;
  ofUnitId: (string | null);
  count: (string | null);
};
}

export type CreateUnitResult = S.UnitOfMeasure;

/** Add a base unit, or a unit made of another */
export function createUnit(args: CreateUnitArgs, options?: RequestOptions): Promise<CreateUnitResult> {
  return request<CreateUnitResult>({
    operationId: "createUnit",
    method: "POST",
    path: "/units",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface CreateViewArgs {
  path: { objectKey: string };
  body: {
  name: string;
  definition: S.ViewDefinition;
  visibility: "private" | "team" | "everyone";
  teamId?: (string | null);
};
}

export type CreateViewResult = S.SavedView;

/** Save a way of looking at a list */
export function createView(args: CreateViewArgs, options?: RequestOptions): Promise<CreateViewResult> {
  return request<CreateViewResult>({
    operationId: "createView",
    method: "POST",
    path: "/views/{objectKey}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export type CustomerHealthResult = {
  customers: Array<S.AccountHealth>;
};

/** Every customer by health, least healthy first */
export function customerHealth(options?: RequestOptions): Promise<CustomerHealthResult> {
  return request<CustomerHealthResult>({
    operationId: "customerHealth",
    method: "GET",
    path: "/customer-health",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface CustomerHistoryArgs {
  path: { accountId: string };
  query?: { kinds?: string; limit?: number; cursor?: string };
}

export type CustomerHistoryResult = {
  history: S.CustomerHistory;
};

/** Deals, quotes, orders, invoices, payments, tickets, campaigns and subscription changes, newest first */
export function customerHistory(args: CustomerHistoryArgs, options?: RequestOptions): Promise<CustomerHistoryResult> {
  return request<CustomerHistoryResult>({
    operationId: "customerHistory",
    method: "GET",
    path: "/accounts/{accountId}/history",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export type DataQualityResult = {
  objects: Array<{
    object: "contact" | "account" | "lead" | "deal";
    total: number;
    completeness: (number | null);
    checks: Array<{
      key: string;
      count: number;
    }>;
    duplicateGroups: number;
    duplicateRecords: number;
    topDuplicates: Array<{
      value: string;
      count: number;
    }>;
    stale: number;
    staleDays: number;
  }>;
};

/** Completeness, duplicates and stale records for contacts, companies, leads and open deals */
export function dataQuality(options?: RequestOptions): Promise<DataQualityResult> {
  return request<DataQualityResult>({
    operationId: "dataQuality",
    method: "GET",
    path: "/analytics/data-quality",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type DataResidencyResult = S.Residency;

/** Where this workspace’s data is held */
export function dataResidency(options?: RequestOptions): Promise<DataResidencyResult> {
  return request<DataResidencyResult>({
    operationId: "dataResidency",
    method: "GET",
    path: "/setup/residency",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DealBreakdownArgs {
  query: { from: string; to: string; dimension: "owner" | "source" | "industry" | "segment" | "size" };
}

export type DealBreakdownResult = {
  currency: string;
  dimension: "owner" | "source" | "industry" | "segment" | "size";
  otherCurrency: number;
  rows: Array<{
    key: string;
    closed: number;
    won: number;
    lost: number;
    winRate: (number | null);
    wonValue: string;
    averageWonSize: (string | null);
    averageDaysToWin: (number | null);
    small: boolean;
  }>;
};

/** Closed deals by owner, source, industry, segment or size: win rate, value, size and days to win */
export function dealBreakdown(args: DealBreakdownArgs, options?: RequestOptions): Promise<DealBreakdownResult> {
  return request<DealBreakdownResult>({
    operationId: "dealBreakdown",
    method: "GET",
    path: "/analytics/deal-breakdown",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface DealCompetitorsArgs {
  path: { dealId: string };
}

export type DealCompetitorsResult = {
  competitors: Array<S.DealCompetitor>;
};

/** Who else is in this deal, and how it went against each */
export function dealCompetitors(args: DealCompetitorsArgs, options?: RequestOptions): Promise<DealCompetitorsResult> {
  return request<DealCompetitorsResult>({
    operationId: "dealCompetitors",
    method: "GET",
    path: "/deals/{dealId}/competitors",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DealDocumentsArgs {
  path: { dealId: string };
}

export type DealDocumentsResult = {
  documents: Array<S.DealDocument>;
};

/** The deal's documents, with its quotes', newest version of each */
export function dealDocuments(args: DealDocumentsArgs, options?: RequestOptions): Promise<DealDocumentsResult> {
  return request<DealDocumentsResult>({
    operationId: "dealDocuments",
    method: "GET",
    path: "/deals/{dealId}/documents",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DealHealthArgs {
  path: { dealId: string };
}

export type DealHealthResult = S.DealHealth;

/** How this deal is doing, and why */
export function dealHealth(args: DealHealthArgs, options?: RequestOptions): Promise<DealHealthResult> {
  return request<DealHealthResult>({
    operationId: "dealHealth",
    method: "GET",
    path: "/deals/{dealId}/health",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DealPeopleArgs {
  path: { dealId: string };
}

export type DealPeopleResult = {
  contacts: Array<S.DealContact>;
  team: Array<S.DealTeamMember>;
};

/** Who is on this deal, theirs and ours */
export function dealPeople(args: DealPeopleArgs, options?: RequestOptions): Promise<DealPeopleResult> {
  return request<DealPeopleResult>({
    operationId: "dealPeople",
    method: "GET",
    path: "/deals/{dealId}/people",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DealRegistrationArgs {
  path: { dealId: string };
}

export type DealRegistrationResult = {
  registration: ({
    partnerAccountId: string;
    partner: string;
    registeredBy: (string | null);
    registeredAt: string;
    protectedUntil: (string | null);
    status: "pending" | "accepted" | "rejected";
    note: (string | null);
  } | null);
};

/** Which partner registered a deal, if one did */
export function dealRegistration(args: DealRegistrationArgs, options?: RequestOptions): Promise<DealRegistrationResult> {
  return request<DealRegistrationResult>({
    operationId: "dealRegistration",
    method: "GET",
    path: "/deals/{dealId}/registration",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DealViewingsArgs {
  path: { dealId: string };
}

export type DealViewingsResult = {
  viewings: Array<S.Viewing>;
  rescheduled: number;
};

/** The viewings booked for this deal, and how often they moved */
export function dealViewings(args: DealViewingsArgs, options?: RequestOptions): Promise<DealViewingsResult> {
  return request<DealViewingsResult>({
    operationId: "dealViewings",
    method: "GET",
    path: "/deals/{dealId}/viewings",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DecideAbTestArgs {
  path: { campaignId: string };
  body: Record<string, never>;
}

export type DecideAbTestResult = {
  status: "decided" | "inconclusive";
  winnerVariantId: string;
  confidence: number;
  sentToRemainder: number;
  reason: string;
};

/** Decide the test now and send the remainder */
export function decideAbTest(args: DecideAbTestArgs, options?: RequestOptions): Promise<DecideAbTestResult> {
  return request<DecideAbTestResult>({
    operationId: "decideAbTest",
    method: "POST",
    path: "/campaigns/{campaignId}/ab-test/decide",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DecideAgentProposalArgs {
  path: { proposalId: string };
  body: {
  decision: "accept" | "edit" | "dismiss";
  editedArguments?: ({
    [key: string]: string;
  } | null);
  note?: (string | null);
};
}

export type DecideAgentProposalResult = {
  status: "pending" | "accepted" | "edited" | "dismissed" | "expired";
  perform: ({
    tool: string;
    arguments: {
      [key: string]: string;
    };
  } | null);
};

/** Accept, edit or dismiss a proposal */
export function decideAgentProposal(args: DecideAgentProposalArgs, options?: RequestOptions): Promise<DecideAgentProposalResult> {
  return request<DecideAgentProposalResult>({
    operationId: "decideAgentProposal",
    method: "POST",
    path: "/agents/proposals/{proposalId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DecideApprovalArgs {
  path: { requestId: string };
  body: {
  approve: boolean;
  comment?: (string | null);
};
}

export type DecideApprovalResult = S.ApprovalRequest;

/** Approve or reject, with a comment */
export function decideApproval(args: DecideApprovalArgs, options?: RequestOptions): Promise<DecideApprovalResult> {
  return request<DecideApprovalResult>({
    operationId: "decideApproval",
    method: "PUT",
    path: "/approvals/{requestId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DecideApprovalFromEmailArgs {
  body: {
  token: string;
  decision: "approved" | "rejected";
  comment: (string | null);
};
}

export type DecideApprovalFromEmailResult = {
  request: (S.ApprovalChain | null);
};

/** Approve or reject from an emailed link, as the person it was sent to */
export function decideApprovalFromEmail(args: DecideApprovalFromEmailArgs, options?: RequestOptions): Promise<DecideApprovalFromEmailResult> {
  return request<DecideApprovalFromEmailResult>({
    operationId: "decideApprovalFromEmail",
    method: "POST",
    path: "/public/approvals/decide",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DecideApprovalStepArgs {
  path: { requestId: string };
  body: {
  decision: "approved" | "rejected";
  comment: (string | null);
};
}

export type DecideApprovalStepResult = S.ApprovalChain;

/** Approve or reject the step waiting on me */
export function decideApprovalStep(args: DecideApprovalStepArgs, options?: RequestOptions): Promise<DecideApprovalStepResult> {
  return request<DecideApprovalStepResult>({
    operationId: "decideApprovalStep",
    method: "POST",
    path: "/approval-requests/{requestId}/decide",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DecideCustomerQuoteArgs {
  path: { quoteId: string };
  body: {
  token: string;
  decision: "accept" | "decline";
  optionalLineIds: Array<string>;
  reason?: (string | null);
};
}

export type DecideCustomerQuoteResult = {
  quote: (S.CustomerQuote | null);
  refusal: (S.PortalRefusal | null);
};

/** Accept a quote, with the optional lines chosen, or decline it */
export function decideCustomerQuote(args: DecideCustomerQuoteArgs, options?: RequestOptions): Promise<DecideCustomerQuoteResult> {
  return request<DecideCustomerQuoteResult>({
    operationId: "decideCustomerQuote",
    method: "POST",
    path: "/portal/quotes/{quoteId}/decision",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DecideMqlArgs {
  path: { leadId: string };
  body: {
  decision: "accept" | "reject";
  reasonId?: (string | null);
  note?: (string | null);
};
}

export type DecideMqlResult = {
  leadId: string;
  decision: "accepted" | "rejected";
};

/** Accept a marketing-qualified lead, or turn it back with a reason */
export function decideMql(args: DecideMqlArgs, options?: RequestOptions): Promise<DecideMqlResult> {
  return request<DecideMqlResult>({
    operationId: "decideMql",
    method: "POST",
    path: "/leads/{leadId}/mql-decision",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DecideRegistrationArgs {
  path: { dealId: string };
  body: {
  decision: "accept" | "reject";
  note?: (string | null);
};
}

export type DecideRegistrationResult = {
  dealId: string;
  status: "pending" | "accepted" | "rejected";
};

/** Accept or reject a partner's registration */
export function decideRegistration(args: DecideRegistrationArgs, options?: RequestOptions): Promise<DecideRegistrationResult> {
  return request<DecideRegistrationResult>({
    operationId: "decideRegistration",
    method: "POST",
    path: "/setup/partners/registrations/{dealId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DelegateApprovalStepArgs {
  path: { requestId: string };
  body: {
  toUserId: string;
  comment: (string | null);
};
}

export type DelegateApprovalStepResult = S.ApprovalChain;

/** Hand the step waiting on me to somebody else */
export function delegateApprovalStep(args: DelegateApprovalStepArgs, options?: RequestOptions): Promise<DelegateApprovalStepResult> {
  return request<DelegateApprovalStepResult>({
    operationId: "delegateApprovalStep",
    method: "POST",
    path: "/approval-requests/{requestId}/delegate",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DeleteAssetArgs {
  path: { assetId: string };
  body: Record<string, never>;
}

export type DeleteAssetResult = {
  deleted: boolean;
};

/** Remove an asset from the library */
export function deleteAsset(args: DeleteAssetArgs, options?: RequestOptions): Promise<DeleteAssetResult> {
  return request<DeleteAssetResult>({
    operationId: "deleteAsset",
    method: "POST",
    path: "/assets/{assetId}/delete",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DeleteAttachmentArgs {
  path: { attachmentId: string };
}

export type DeleteAttachmentResult = {
  removed: boolean;
};

/** Remove a file from a record */
export function deleteAttachment(args: DeleteAttachmentArgs, options?: RequestOptions): Promise<DeleteAttachmentResult> {
  return request<DeleteAttachmentResult>({
    operationId: "deleteAttachment",
    method: "POST",
    path: "/files/{attachmentId}/delete",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DeleteBiFeedArgs {
  path: { feedId: string };
  body: Record<string, never>;
}

export type DeleteBiFeedResult = {
  deleted: boolean;
};

/** Stop a BI feed; its address stops working at once */
export function deleteBiFeed(args: DeleteBiFeedArgs, options?: RequestOptions): Promise<DeleteBiFeedResult> {
  return request<DeleteBiFeedResult>({
    operationId: "deleteBiFeed",
    method: "POST",
    path: "/data-export/feeds/{feedId}/delete",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DeleteCustomFieldArgs {
  path: { fieldId: string };
}

export type DeleteCustomFieldResult = {
  removed: true;
};

/** Remove a custom field */
export function deleteCustomField(args: DeleteCustomFieldArgs, options?: RequestOptions): Promise<DeleteCustomFieldResult> {
  return request<DeleteCustomFieldResult>({
    operationId: "deleteCustomField",
    method: "DELETE",
    path: "/setup/fields/{fieldId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DeleteDocumentTemplateArgs {
  path: { templateId: string };
}

export type DeleteDocumentTemplateResult = {
  ok: true;
};

/** Remove a document template */
export function deleteDocumentTemplate(args: DeleteDocumentTemplateArgs, options?: RequestOptions): Promise<DeleteDocumentTemplateResult> {
  return request<DeleteDocumentTemplateResult>({
    operationId: "deleteDocumentTemplate",
    method: "DELETE",
    path: "/setup/document-templates/{templateId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DeleteEscalationRuleArgs {
  path: { ruleId: string };
}

export type DeleteEscalationRuleResult = {
  ok: boolean;
};

/** Remove an escalation rule */
export function deleteEscalationRule(args: DeleteEscalationRuleArgs, options?: RequestOptions): Promise<DeleteEscalationRuleResult> {
  return request<DeleteEscalationRuleResult>({
    operationId: "deleteEscalationRule",
    method: "DELETE",
    path: "/setup/escalations/{ruleId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DeleteEventSubscriberArgs {
  path: { subscriberId: string };
  body: Record<string, never>;
}

export type DeleteEventSubscriberResult = {
  deleted: boolean;
};

/** Remove a subscriber */
export function deleteEventSubscriber(args: DeleteEventSubscriberArgs, options?: RequestOptions): Promise<DeleteEventSubscriberResult> {
  return request<DeleteEventSubscriberResult>({
    operationId: "deleteEventSubscriber",
    method: "POST",
    path: "/developer/stream/subscribers/{subscriberId}/delete",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DeleteLeadScoreRuleArgs {
  path: { ruleId: string };
}

export type DeleteLeadScoreRuleResult = {
  ok: true;
};

/** Remove a scoring rule */
export function deleteLeadScoreRule(args: DeleteLeadScoreRuleArgs, options?: RequestOptions): Promise<DeleteLeadScoreRuleResult> {
  return request<DeleteLeadScoreRuleResult>({
    operationId: "deleteLeadScoreRule",
    method: "DELETE",
    path: "/leads/score-rules/{ruleId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DeleteModelProviderArgs {
  path: { providerId: string };
  body: Record<string, never>;
}

export type DeleteModelProviderResult = {
  deleted: boolean;
};

/** Remove a provider account, and every routing step that named it */
export function deleteModelProvider(args: DeleteModelProviderArgs, options?: RequestOptions): Promise<DeleteModelProviderResult> {
  return request<DeleteModelProviderResult>({
    operationId: "deleteModelProvider",
    method: "POST",
    path: "/setup/model-routing/providers/{providerId}/delete",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DeletePipelineArgs {
  path: { pipelineId: string };
}

export type DeletePipelineResult = {
  ok: boolean;
};

/** Remove a pipeline */
export function deletePipeline(args: DeletePipelineArgs, options?: RequestOptions): Promise<DeletePipelineResult> {
  return request<DeletePipelineResult>({
    operationId: "deletePipeline",
    method: "DELETE",
    path: "/setup/pipelines/{pipelineId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DeleteRecordArgs {
  path: { objectKey: string; recordId: string };
}

export type DeleteRecordResult = {
  deleted: true;
  restorable: boolean;
};

/** Move a record to the recycle bin */
export function deleteRecord(args: DeleteRecordArgs, options?: RequestOptions): Promise<DeleteRecordResult> {
  return request<DeleteRecordResult>({
    operationId: "deleteRecord",
    method: "DELETE",
    path: "/records/{objectKey}/{recordId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DeleteReportSubscriptionArgs {
  path: { reportId: string; subscriptionId: string };
  body: Record<string, never>;
}

export type DeleteReportSubscriptionResult = {
  deleted: boolean;
};

/** Stop sending it */
export function deleteReportSubscription(args: DeleteReportSubscriptionArgs, options?: RequestOptions): Promise<DeleteReportSubscriptionResult> {
  return request<DeleteReportSubscriptionResult>({
    operationId: "deleteReportSubscription",
    method: "POST",
    path: "/reports/{reportId}/subscriptions/{subscriptionId}/delete",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DeleteRoutingRuleArgs {
  path: { ruleId: string };
}

export type DeleteRoutingRuleResult = {
  ok: boolean;
};

/** Remove a routing rule */
export function deleteRoutingRule(args: DeleteRoutingRuleArgs, options?: RequestOptions): Promise<DeleteRoutingRuleResult> {
  return request<DeleteRoutingRuleResult>({
    operationId: "deleteRoutingRule",
    method: "DELETE",
    path: "/setup/routing/{ruleId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DeleteSegmentArgs {
  path: { segmentId: string };
}

export type DeleteSegmentResult = {
  removed: boolean;
};

/** Remove a saved audience */
export function deleteSegment(args: DeleteSegmentArgs, options?: RequestOptions): Promise<DeleteSegmentResult> {
  return request<DeleteSegmentResult>({
    operationId: "deleteSegment",
    method: "DELETE",
    path: "/segments/{segmentId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DeleteStageArgs {
  path: { stageId: string };
}

export type DeleteStageResult = {
  ok: boolean;
};

/** Remove a stage */
export function deleteStage(args: DeleteStageArgs, options?: RequestOptions): Promise<DeleteStageResult> {
  return request<DeleteStageResult>({
    operationId: "deleteStage",
    method: "DELETE",
    path: "/setup/pipelines/stages/{stageId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DeleteTargetArgs {
  query: { subjectKind: "user" | "team"; subjectId: string; periodStart: string; periodEnd: string };
}

export type DeleteTargetResult = {
  ok: boolean;
};

/** Remove a target */
export function deleteTarget(args: DeleteTargetArgs, options?: RequestOptions): Promise<DeleteTargetResult> {
  return request<DeleteTargetResult>({
    operationId: "deleteTarget",
    method: "DELETE",
    path: "/targets",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface DeleteValidationRuleArgs {
  path: { ruleId: string };
}

export type DeleteValidationRuleResult = {
  deleted: true;
};

/** Remove a validation rule */
export function deleteValidationRule(args: DeleteValidationRuleArgs, options?: RequestOptions): Promise<DeleteValidationRuleResult> {
  return request<DeleteValidationRuleResult>({
    operationId: "deleteValidationRule",
    method: "DELETE",
    path: "/setup/validation-rules/{ruleId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DeleteViewArgs {
  path: { viewId: string };
}

export type DeleteViewResult = {
  ok: true;
};

/** Delete a saved view */
export function deleteView(args: DeleteViewArgs, options?: RequestOptions): Promise<DeleteViewResult> {
  return request<DeleteViewResult>({
    operationId: "deleteView",
    method: "DELETE",
    path: "/views/detail/{viewId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DeleteWebhookArgs {
  path: { endpointId: string };
  body: Record<string, never>;
}

export type DeleteWebhookResult = {
  deleted: boolean;
};

/** Remove a webhook endpoint and its delivery log */
export function deleteWebhook(args: DeleteWebhookArgs, options?: RequestOptions): Promise<DeleteWebhookResult> {
  return request<DeleteWebhookResult>({
    operationId: "deleteWebhook",
    method: "POST",
    path: "/developer/webhooks/{endpointId}/delete",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export type DeliverabilityResult = {
  days: number;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  bounceRate: number;
  complaintRate: number;
  bounceWarning: boolean;
  complaintWarning: boolean;
  suppressed: number;
  heldForWarmup: number;
  warmup: {
    cap: (number | null);
    sentToday: number;
    day: (number | null);
  };
};

/** Bounce and complaint rates for the last 30 days against the thresholds mailbox providers use, and warm-up progress */
export function deliverability(options?: RequestOptions): Promise<DeliverabilityResult> {
  return request<DeliverabilityResult>({
    operationId: "deliverability",
    method: "GET",
    path: "/marketing/deliverability",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DeliverRemindersArgs {
  body: Record<string, never>;
}

export type DeliverRemindersResult = {
  sent: number;
  alreadyToday: number;
  priorities: Array<string>;
};

/** Tell people about work that is due */
export function deliverReminders(args: DeliverRemindersArgs, options?: RequestOptions): Promise<DeliverRemindersResult> {
  return request<DeliverRemindersResult>({
    operationId: "deliverReminders",
    method: "POST",
    path: "/work/reminders",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DiffConfigurationArgs {
  body: {
  bundle: S.ConfigBundle;
};
}

export type DiffConfigurationResult = S.ConfigDiff;

/** What applying a bundle would change, item by item */
export function diffConfiguration(args: DiffConfigurationArgs, options?: RequestOptions): Promise<DiffConfigurationResult> {
  return request<DiffConfigurationResult>({
    operationId: "diffConfiguration",
    method: "POST",
    path: "/setup/environments/diff",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DismissChecklistArgs {
  body: {
  dismissed: boolean;
};
}

export type DismissChecklistResult = S.SetupChecklist;

/** Put the setup checklist away, or bring it back */
export function dismissChecklist(args: DismissChecklistArgs, options?: RequestOptions): Promise<DismissChecklistResult> {
  return request<DismissChecklistResult>({
    operationId: "dismissChecklist",
    method: "POST",
    path: "/onboarding/checklist/dismissed",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DismissDuplicateArgs {
  path: { objectKey: string; recordId: string };
  body: {
  otherId: string;
};
}

export type DismissDuplicateResult = {
  ok: true;
};

/** Record that two records are not duplicates */
export function dismissDuplicate(args: DismissDuplicateArgs, options?: RequestOptions): Promise<DismissDuplicateResult> {
  return request<DismissDuplicateResult>({
    operationId: "dismissDuplicate",
    method: "POST",
    path: "/records/{objectKey}/{recordId}/duplicates/dismiss",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DispatchBoardArgs {
  query: { day: string };
}

export type DispatchBoardResult = {
  day: string;
  jobs: Array<S.FieldJob>;
  technicians: Array<{
    userId: string;
    name: string;
  }>;
};

/** A day's on-site jobs, and who can be sent */
export function dispatchBoard(args: DispatchBoardArgs, options?: RequestOptions): Promise<DispatchBoardResult> {
  return request<DispatchBoardResult>({
    operationId: "dispatchBoard",
    method: "GET",
    path: "/field-jobs/board",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface DocumentTemplatesArgs {
  query?: { objectKey?: "quote" | "deal" };
}

export type DocumentTemplatesResult = S.DocumentTemplates;

/** Document templates, and the fields each kind can use */
export function documentTemplates(args: DocumentTemplatesArgs, options?: RequestOptions): Promise<DocumentTemplatesResult> {
  return request<DocumentTemplatesResult>({
    operationId: "documentTemplates",
    method: "GET",
    path: "/setup/document-templates",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface DownloadExportFileArgs {
  path: { fileId: string };
}

export type DownloadExportFileResult = {
  objectKey: string;
  csv: string;
};

/** One exported file, as CSV text */
export function downloadExportFile(args: DownloadExportFileArgs, options?: RequestOptions): Promise<DownloadExportFileResult> {
  return request<DownloadExportFileResult>({
    operationId: "downloadExportFile",
    method: "GET",
    path: "/data-export/files/{fileId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type DropExpiredAddressesResult = S.AddressesDropped;

/** Drop addresses past the window now */
export function dropExpiredAddresses(options?: RequestOptions): Promise<DropExpiredAddressesResult> {
  return request<DropExpiredAddressesResult>({
    operationId: "dropExpiredAddresses",
    method: "POST",
    path: "/setup/tracking/drop-addresses",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface DryRunImportArgs {
  body: {
  objectKey: string;
  fileName: string;
  mapping: {
    [key: string]: string;
  };
  rows: Array<{
    [key: string]: string;
  }>;
};
}

export type DryRunImportResult = {
  runId: string;
  objectKey: string;
  fileName: string;
  totalRows: number;
  validRows: number;
  errors: Array<S.ImportError>;
  sample: Array<{
    [key: string]: string;
  }>;
  mapping: {
    [key: string]: string;
  };
};

/** Check a file without writing anything */
export function dryRunImport(args: DryRunImportArgs, options?: RequestOptions): Promise<DryRunImportResult> {
  return request<DryRunImportResult>({
    operationId: "dryRunImport",
    method: "POST",
    path: "/setup/imports/dry-run",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface DuplicateQueueArgs {
  query?: { objectKey?: "contact" | "account" | "lead"; limit?: number };
}

export type DuplicateQueueResult = {
  pairs: Array<S.DuplicatePair>;
};

/** Pairs that look like the same person or company */
export function duplicateQueue(args: DuplicateQueueArgs, options?: RequestOptions): Promise<DuplicateQueueResult> {
  return request<DuplicateQueueResult>({
    operationId: "duplicateQueue",
    method: "GET",
    path: "/duplicates",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface DuplicateViewArgs {
  path: { viewId: string };
  body: {
  name?: string;
};
}

export type DuplicateViewResult = S.SavedView;

/** Take a copy of a view */
export function duplicateView(args: DuplicateViewArgs, options?: RequestOptions): Promise<DuplicateViewResult> {
  return request<DuplicateViewResult>({
    operationId: "duplicateView",
    method: "POST",
    path: "/views/detail/{viewId}/duplicate",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface EditRecordCommentArgs {
  path: { objectKey: string; recordId: string; commentId: string };
  body: {
  body: string;
};
}

export type EditRecordCommentResult = {
  comment: S.RecordComment;
};

/** Change what you said, visibly */
export function editRecordComment(args: EditRecordCommentArgs, options?: RequestOptions): Promise<EditRecordCommentResult> {
  return request<EditRecordCommentResult>({
    operationId: "editRecordComment",
    method: "POST",
    path: "/records/{objectKey}/{recordId}/comments/{commentId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface EmbedDataArgs {
  query: { token: string; limit?: number };
}

export type EmbedDataResult = {
  widget: string;
  objectKey: (string | null);
  records: Array<{
    [key: string]: (string | number | boolean | null);
  }>;
  total: number;
};

/** What one embedded widget renders */
export function embedData(args: EmbedDataArgs, options?: RequestOptions): Promise<EmbedDataResult> {
  return request<EmbedDataResult>({
    operationId: "embedData",
    method: "GET",
    path: "/embed/data",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface EndConversationArgs {
  path: { conversationId: string };
}

export type EndConversationResult = {
  ended: boolean;
};

/** Close a conversation */
export function endConversation(args: EndConversationArgs, options?: RequestOptions): Promise<EndConversationResult> {
  return request<EndConversationResult>({
    operationId: "endConversation",
    method: "POST",
    path: "/chat/conversations/{conversationId}/end",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type EndMySupportVisitResult = S.VisitEnded;

/** End the support visit you are looking at */
export function endMySupportVisit(options?: RequestOptions): Promise<EndMySupportVisitResult> {
  return request<EndMySupportVisitResult>({
    operationId: "endMySupportVisit",
    method: "POST",
    path: "/me/visit/end",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface EnrichAccountArgs {
  path: { accountId: string };
}

export type EnrichAccountResult = S.AccountEnrichment;

/** Fill in what is known about this company */
export function enrichAccount(args: EnrichAccountArgs, options?: RequestOptions): Promise<EnrichAccountResult> {
  return request<EnrichAccountResult>({
    operationId: "enrichAccount",
    method: "POST",
    path: "/accounts/{accountId}/enrich",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type EnrichmentCreditsResult = S.EnrichmentCredits;

/** What is left, and where it went */
export function enrichmentCredits(options?: RequestOptions): Promise<EnrichmentCreditsResult> {
  return request<EnrichmentCreditsResult>({
    operationId: "enrichmentCredits",
    method: "GET",
    path: "/setup/enrichment/credits",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface EnrolInCadenceArgs {
  path: { contactId: string };
  body: {
  cadenceId: string;
};
}

export type EnrolInCadenceResult = {
  enrolled: number;
  already: number;
};

/** Put a contact into a cadence */
export function enrolInCadence(args: EnrolInCadenceArgs, options?: RequestOptions): Promise<EnrolInCadenceResult> {
  return request<EnrolInCadenceResult>({
    operationId: "enrolInCadence",
    method: "POST",
    path: "/contacts/{contactId}/cadence",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export type EscalateCaseBreachesResult = {
  escalated: number;
  told: Array<S.CaseEscalation>;
};

/** Tell somebody about every case that has breached */
export function escalateCaseBreaches(options?: RequestOptions): Promise<EscalateCaseBreachesResult> {
  return request<EscalateCaseBreachesResult>({
    operationId: "escalateCaseBreaches",
    method: "POST",
    path: "/cases/escalations",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type EscalateLeadsResult = {
  escalated: number;
  told: Array<{
    leadId: string;
    name: string;
    ownerId: (string | null);
    told: number;
  }>;
};

/** Tell somebody about every lead whose time to answer has gone */
export function escalateLeads(options?: RequestOptions): Promise<EscalateLeadsResult> {
  return request<EscalateLeadsResult>({
    operationId: "escalateLeads",
    method: "POST",
    path: "/leads/escalations",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface EscalateMqlBreachesArgs {
  body: Record<string, never>;
}

export type EscalateMqlBreachesResult = {
  escalated: number;
};

/** Tell somebody about each MQL whose answer is overdue, once */
export function escalateMqlBreaches(args: EscalateMqlBreachesArgs, options?: RequestOptions): Promise<EscalateMqlBreachesResult> {
  return request<EscalateMqlBreachesResult>({
    operationId: "escalateMqlBreaches",
    method: "POST",
    path: "/leads/mql/escalate",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ExecutivePackArgs {
  query: { period: "month" | "quarter" | "year"; endingOn: string };
}

export type ExecutivePackResult = {
  currency: string;
  from: string;
  to: string;
  previousFrom: string;
  previousTo: string;
  marginWithheld: boolean;
  lines: Array<{
    metric: "bookings" | "dealsWon" | "winRate" | "newPipeline" | "ordered" | "margin" | "invoiced" | "overdue" | "casesOpened" | "casesResolved";
    kind: "money" | "count" | "ratio";
    current: (number | null);
    previous: (number | null);
    change: (number | null);
    lastYear: (number | null);
    baseline: (number | null);
    againstBaseline: (number | null);
  }>;
};

/** The period summary for leadership: bookings, pipeline, orders, margin, billing and service, each beside the period before */
export function executivePack(args: ExecutivePackArgs, options?: RequestOptions): Promise<ExecutivePackResult> {
  return request<ExecutivePackResult>({
    operationId: "executivePack",
    method: "GET",
    path: "/analytics/executive-pack",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ExitCadenceEnrolmentArgs {
  path: { enrolmentId: string };
  body: Record<string, never>;
}

export type ExitCadenceEnrolmentResult = {
  exited: boolean;
};

/** Take somebody out of a cadence */
export function exitCadenceEnrolment(args: ExitCadenceEnrolmentArgs, options?: RequestOptions): Promise<ExitCadenceEnrolmentResult> {
  return request<ExitCadenceEnrolmentResult>({
    operationId: "exitCadenceEnrolment",
    method: "POST",
    path: "/cadence-enrolments/{enrolmentId}/exit",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ExpandKitOnQuoteArgs {
  path: { quoteId: string; lineId: string };
  body: Record<string, never>;
}

export type ExpandKitOnQuoteResult = {
  lineId: string;
  components: number;
};

/** Write a kit’s parts beneath its line, at zero */
export function expandKitOnQuote(args: ExpandKitOnQuoteArgs, options?: RequestOptions): Promise<ExpandKitOnQuoteResult> {
  return request<ExpandKitOnQuoteResult>({
    operationId: "expandKitOnQuote",
    method: "POST",
    path: "/quotes/{quoteId}/lines/{lineId}/expand",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ExportAuditLogArgs {
  body: {
  afterLogId?: (string | null);
  previousHash?: (string | null);
  limit?: number;
};
}

export type ExportAuditLogResult = {
  jsonl: string;
  count: number;
  firstLogId: (string | null);
  lastLogId: (string | null);
  lastHash: string;
  more: boolean;
};

/** Take the audit trail out for retention: JSON lines after a cursor, each hash-chained to the last */
export function exportAuditLog(args: ExportAuditLogArgs, options?: RequestOptions): Promise<ExportAuditLogResult> {
  return request<ExportAuditLogResult>({
    operationId: "exportAuditLog",
    method: "POST",
    path: "/settings/audit/export",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export type ExportConfigurationResult = S.ConfigBundle;

/** The whole workspace configuration, as a bundle */
export function exportConfiguration(options?: RequestOptions): Promise<ExportConfigurationResult> {
  return request<ExportConfigurationResult>({
    operationId: "exportConfiguration",
    method: "GET",
    path: "/setup/environments/export",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ExportSubjectDataArgs {
  query: { subjectEmail: string };
}

export type ExportSubjectDataResult = S.SubjectExport;

/** Everything held about a person, as a portable document */
export function exportSubjectData(args: ExportSubjectDataArgs, options?: RequestOptions): Promise<ExportSubjectDataResult> {
  return request<ExportSubjectDataResult>({
    operationId: "exportSubjectData",
    method: "GET",
    path: "/setup/privacy/export",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface FieldOrderCatalogArgs {
  query: { dealId: string; search?: string };
}

export type FieldOrderCatalogResult = {
  items: Array<{
    productId: string;
    sku: string;
    name: string;
    unit: string;
    unitPrice: string;
    currency: string;
    priceExplanation: string;
    available: (string | null);
    stockAgeHours: (number | null);
  }>;
  contacts: Array<{
    contactId: string;
    name: string;
  }>;
};

/** Products with this customer's price and what is available, for ordering on site */
export function fieldOrderCatalog(args: FieldOrderCatalogArgs, options?: RequestOptions): Promise<FieldOrderCatalogResult> {
  return request<FieldOrderCatalogResult>({
    operationId: "fieldOrderCatalog",
    method: "GET",
    path: "/field-orders/catalog",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface FinalComplaintResponseArgs {
  path: { caseId: string };
  body: {
  body: string;
};
}

export type FinalComplaintResponseResult = {
  done: true;
};

/** Send the final written response to a complaint */
export function finalComplaintResponse(args: FinalComplaintResponseArgs, options?: RequestOptions): Promise<FinalComplaintResponseResult> {
  return request<FinalComplaintResponseResult>({
    operationId: "finalComplaintResponse",
    method: "POST",
    path: "/cases/{caseId}/complaint/final-response",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface FindDuplicatesArgs {
  path: { objectKey: string };
  body: {
  email?: (string | null);
  phone?: (string | null);
  name?: (string | null);
  domain?: (string | null);
  excludeId?: (string | null);
};
}

export type FindDuplicatesResult = {
  matches: Array<S.DuplicateMatch>;
};

/** What this record looks like, before it is saved */
export function findDuplicates(args: FindDuplicatesArgs, options?: RequestOptions): Promise<FindDuplicatesResult> {
  return request<FindDuplicatesResult>({
    operationId: "findDuplicates",
    method: "POST",
    path: "/duplicates/{objectKey}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface FlowCostWarningsArgs {
  path: { flowId: string };
}

export type FlowCostWarningsResult = {
  warnings: Array<{
    kind: "self-trigger" | "long";
    nodeId: (string | null);
    message: string;
  }>;
};

/** What is likely to be expensive about this flow, before it is switched on */
export function flowCostWarnings(args: FlowCostWarningsArgs, options?: RequestOptions): Promise<FlowCostWarningsResult> {
  return request<FlowCostWarningsResult>({
    operationId: "flowCostWarnings",
    method: "GET",
    path: "/flows/{flowId}/cost-warnings",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface FlowNodeCountsArgs {
  path: { flowId: string };
}

export type FlowNodeCountsResult = {
  counts: Array<{
    nodeId: string;
    count: number;
  }>;
};

/** How many records are sitting at each step */
export function flowNodeCounts(args: FlowNodeCountsArgs, options?: RequestOptions): Promise<FlowNodeCountsResult> {
  return request<FlowNodeCountsResult>({
    operationId: "flowNodeCounts",
    method: "GET",
    path: "/flows/{flowId}/counts",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ForceSignOutArgs {
  body: {
  userId: string;
  reason: string;
};
}

export type ForceSignOutResult = {
  ended: number;
};

/** Sign a person out of every session, with the reason on the audit trail */
export function forceSignOut(args: ForceSignOutArgs, options?: RequestOptions): Promise<ForceSignOutResult> {
  return request<ForceSignOutResult>({
    operationId: "forceSignOut",
    method: "POST",
    path: "/setup/sessions/sign-out",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface FreezeSegmentArgs {
  path: { segmentId: string };
  body: {
  name?: (string | null);
};
}

export type FreezeSegmentResult = S.Segment;

/** Keep who matches now as a list that will not change */
export function freezeSegment(args: FreezeSegmentArgs, options?: RequestOptions): Promise<FreezeSegmentResult> {
  return request<FreezeSegmentResult>({
    operationId: "freezeSegment",
    method: "POST",
    path: "/segments/{segmentId}/freeze",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export type FrequencyCapResult = {
  active: boolean;
  maxMessages: (number | null);
  perDays: (number | null);
};

/** The limit on marketing messages per person per period */
export function frequencyCap(options?: RequestOptions): Promise<FrequencyCapResult> {
  return request<FrequencyCapResult>({
    operationId: "frequencyCap",
    method: "GET",
    path: "/marketing/frequency-cap",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GenerateInvoiceArgs {
  body: {
  subscriptionId: string;
  on?: (string | null);
  dueInDays?: number;
};
}

export type GenerateInvoiceResult = {
  outcome: S.InvoiceGeneration;
};

/** Bill one subscription for its next period */
export function generateInvoice(args: GenerateInvoiceArgs, options?: RequestOptions): Promise<GenerateInvoiceResult> {
  return request<GenerateInvoiceResult>({
    operationId: "generateInvoice",
    method: "POST",
    path: "/invoices/generate",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface GenerateQuoteDocumentArgs {
  path: { quoteId: string };
  body: {
  templateId: string;
};
}

export type GenerateQuoteDocumentResult = S.GeneratedDocument;

/** Generate a document from this quote and attach it */
export function generateQuoteDocument(args: GenerateQuoteDocumentArgs, options?: RequestOptions): Promise<GenerateQuoteDocumentResult> {
  return request<GenerateQuoteDocumentResult>({
    operationId: "generateQuoteDocument",
    method: "POST",
    path: "/quotes/{quoteId}/documents",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface GenerateVariantsArgs {
  path: { productId: string };
  body: {
  confirmed?: boolean;
};
}

export type GenerateVariantsResult = {
  created: number;
  skipped: number;
};

/** Create the variants the attributes describe */
export function generateVariants(args: GenerateVariantsArgs, options?: RequestOptions): Promise<GenerateVariantsResult> {
  return request<GenerateVariantsResult>({
    operationId: "generateVariants",
    method: "POST",
    path: "/products/{productId}/variants",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface GetAccountArgs {
  path: { accountId: string };
}

export type GetAccountResult = S.AccountDetail;

/** One company, with its subsidiaries and its rollups */
export function getAccount(args: GetAccountArgs, options?: RequestOptions): Promise<GetAccountResult> {
  return request<GetAccountResult>({
    operationId: "getAccount",
    method: "GET",
    path: "/accounts/{accountId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetAgentArgs {
  path: { agentId: string };
}

export type GetAgentResult = {
  agent: S.AgentDefinition;
};

/** One agent: topics, actions, sources, guardrails and tests */
export function getAgent(args: GetAgentArgs, options?: RequestOptions): Promise<GetAgentResult> {
  return request<GetAgentResult>({
    operationId: "getAgent",
    method: "GET",
    path: "/agents/{agentId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetAgentRunTraceArgs {
  path: { runId: string };
}

export type GetAgentRunTraceResult = {
  steps: Array<S.AgentTraceStep>;
};

/** The reasoning behind one run */
export function getAgentRunTrace(args: GetAgentRunTraceArgs, options?: RequestOptions): Promise<GetAgentRunTraceResult> {
  return request<GetAgentRunTraceResult>({
    operationId: "getAgentRunTrace",
    method: "GET",
    path: "/agents/runs/{runId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetAgentSpendArgs {
  query?: { from?: string; to?: string };
}

export type GetAgentSpendResult = {
  amount: string;
  currency: string;
  runs: number;
  inputTokens: string;
  outputTokens: string;
  byAgent: Array<{
    key: string;
    label: string;
    amount: string;
    runs: number;
    inputTokens: string;
    outputTokens: string;
  }>;
  byUser: Array<{
    key: string;
    label: string;
    amount: string;
    runs: number;
    inputTokens: string;
    outputTokens: string;
  }>;
  budgets: Array<S.AgentBudgetStatus>;
};

/** What agents have cost, over a period */
export function getAgentSpend(args: GetAgentSpendArgs, options?: RequestOptions): Promise<GetAgentSpendResult> {
  return request<GetAgentSpendResult>({
    operationId: "getAgentSpend",
    method: "GET",
    path: "/agents/spend",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface GetBulkJobArgs {
  path: { jobId: string };
}

export type GetBulkJobResult = S.BulkJob;

/** Where one job has got to */
export function getBulkJob(args: GetBulkJobArgs, options?: RequestOptions): Promise<GetBulkJobResult> {
  return request<GetBulkJobResult>({
    operationId: "getBulkJob",
    method: "GET",
    path: "/bulk/jobs/{jobId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetCampaignArgs {
  path: { campaignId: string };
}

export type GetCampaignResult = {
  campaign: S.Campaign;
  performance: S.CampaignPerformance;
};

/** One campaign and its results */
export function getCampaign(args: GetCampaignArgs, options?: RequestOptions): Promise<GetCampaignResult> {
  return request<GetCampaignResult>({
    operationId: "getCampaign",
    method: "GET",
    path: "/campaigns/{campaignId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetCaptureFormArgs {
  query: { token: string };
}

export type GetCaptureFormResult = S.CaptureForm;

/** The fields an embedded capture form renders */
export function getCaptureForm(args: GetCaptureFormArgs, options?: RequestOptions): Promise<GetCaptureFormResult> {
  return request<GetCaptureFormResult>({
    operationId: "getCaptureForm",
    method: "GET",
    path: "/embed/form",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface GetCaseArgs {
  path: { caseId: string };
}

export type GetCaseResult = {
  supportCase: S.SupportCase;
  messages: Array<S.CaseMessage>;
};

/** One case, its conversation and its SLA */
export function getCase(args: GetCaseArgs, options?: RequestOptions): Promise<GetCaseResult> {
  return request<GetCaseResult>({
    operationId: "getCase",
    method: "GET",
    path: "/cases/{caseId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type GetCaseQueueResult = {
  unassigned: Array<S.SupportCase>;
  mine: Array<S.SupportCase>;
  all: Array<S.SupportCase>;
};

/** The support queue: unassigned, mine, and all */
export function getCaseQueue(options?: RequestOptions): Promise<GetCaseQueueResult> {
  return request<GetCaseQueueResult>({
    operationId: "getCaseQueue",
    method: "GET",
    path: "/cases",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type GetChatQueueResult = {
  waiting: Array<S.ConversationSummary>;
  active: Array<S.ConversationSummary>;
  mine: Array<S.ConversationSummary>;
};

/** Conversations waiting, active, and mine */
export function getChatQueue(options?: RequestOptions): Promise<GetChatQueueResult> {
  return request<GetChatQueueResult>({
    operationId: "getChatQueue",
    method: "GET",
    path: "/chat/queue",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetContactArgs {
  path: { contactId: string };
}

export type GetContactResult = S.ContactDetail;

/** One contact, with its addresses, consent and tags */
export function getContact(args: GetContactArgs, options?: RequestOptions): Promise<GetContactResult> {
  return request<GetContactResult>({
    operationId: "getContact",
    method: "GET",
    path: "/contacts/{contactId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetContentArgs {
  path: { documentId: string };
}

export type GetContentResult = {
  document: S.ContentDocument;
};

/** One piece of content */
export function getContent(args: GetContentArgs, options?: RequestOptions): Promise<GetContentResult> {
  return request<GetContentResult>({
    operationId: "getContent",
    method: "GET",
    path: "/content/{documentId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetConversationArgs {
  path: { conversationId: string };
}

export type GetConversationResult = {
  conversation: S.ConversationSummary;
  messages: Array<S.ChatMessage>;
};

/** One conversation and everything said in it */
export function getConversation(args: GetConversationArgs, options?: RequestOptions): Promise<GetConversationResult> {
  return request<GetConversationResult>({
    operationId: "getConversation",
    method: "GET",
    path: "/chat/conversations/{conversationId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetCurrentPeriodArgs {
  query: { periodKind: "month" | "quarter" | "year" };
}

export type GetCurrentPeriodResult = {
  start: string;
  end: string;
};

/** The period today falls in, on this workspace's financial calendar */
export function getCurrentPeriod(args: GetCurrentPeriodArgs, options?: RequestOptions): Promise<GetCurrentPeriodResult> {
  return request<GetCurrentPeriodResult>({
    operationId: "getCurrentPeriod",
    method: "GET",
    path: "/forecast/current-period",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface GetDashboardArgs {
  path: { key: string };
}

export type GetDashboardResult = {
  dashboardId: string;
  key: string;
  name: string;
  description: (string | null);
  isDefault: boolean;
  widgets: Array<S.DashboardWidget>;
};

/** A dashboard, with every widget already resolved */
export function getDashboard(args: GetDashboardArgs, options?: RequestOptions): Promise<GetDashboardResult> {
  return request<GetDashboardResult>({
    operationId: "getDashboard",
    method: "GET",
    path: "/dashboards/{key}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetDealBoardArgs {
  query?: { pipelineId?: string; containingDeal?: string; cardsPerColumn?: number };
}

export type GetDealBoardResult = {
  pipelineId: string;
  pipelineLabel: string;
  columns: Array<S.StageColumn>;
  totalCount: number;
  totals: Array<S.CurrencyTotal>;
};

/** The pipeline as columns, with a count and a total on each */
export function getDealBoard(args: GetDealBoardArgs, options?: RequestOptions): Promise<GetDealBoardResult> {
  return request<GetDealBoardResult>({
    operationId: "getDealBoard",
    method: "GET",
    path: "/deals/board",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface GetDealPlaybookArgs {
  path: { dealId: string };
}

export type GetDealPlaybookResult = S.DealPlaybook;

/** The playbook for the stage this deal is in now */
export function getDealPlaybook(args: GetDealPlaybookArgs, options?: RequestOptions): Promise<GetDealPlaybookResult> {
  return request<GetDealPlaybookResult>({
    operationId: "getDealPlaybook",
    method: "GET",
    path: "/deals/{dealId}/playbook",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type GetDiscountCapResult = {
  capPercent: (string | null);
};

/** The discount above which somebody else has to agree */
export function getDiscountCap(options?: RequestOptions): Promise<GetDiscountCapResult> {
  return request<GetDiscountCapResult>({
    operationId: "getDiscountCap",
    method: "GET",
    path: "/setup/discount-cap",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetFlowArgs {
  path: { flowId: string };
}

export type GetFlowResult = {
  flowId: string;
  palette: "automation" | "journey" | "chatbot" | "approval";
  name: string;
  status: string;
  version: number;
  graph: S.FlowGraph;
  problems: Array<S.FlowProblem>;
  earlierVersions: Array<{
    version: number;
    inProgress: number;
  }>;
};

/** One flow, its graph, and everything wrong with it */
export function getFlow(args: GetFlowArgs, options?: RequestOptions): Promise<GetFlowResult> {
  return request<GetFlowResult>({
    operationId: "getFlow",
    method: "GET",
    path: "/flows/{flowId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetFlowRunArgs {
  path: { runId: string };
}

export type GetFlowRunResult = {
  runId: string;
  flowId: string;
  flowVersion: number;
  status: string;
  outcome: (string | null);
  startedAt: string;
  finishedAt: (string | null);
  relatedType: (string | null);
  relatedId: (string | null);
  replayOf: (string | null);
  steps: Array<S.FlowRunStep>;
};

/** One run, step by step, with what each node saw and decided */
export function getFlowRun(args: GetFlowRunArgs, options?: RequestOptions): Promise<GetFlowRunResult> {
  return request<GetFlowRunResult>({
    operationId: "getFlowRun",
    method: "GET",
    path: "/flow-runs/{runId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetForecastArgs {
  query: { fiscalYear: number; periodKind: "month" | "quarter" | "year"; subjectKind: "user" | "team" };
}

export type GetForecastResult = S.Forecast;

/** The forecast for a financial year */
export function getForecast(args: GetForecastArgs, options?: RequestOptions): Promise<GetForecastResult> {
  return request<GetForecastResult>({
    operationId: "getForecast",
    method: "GET",
    path: "/forecast",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface GetForecastAccuracyArgs {
  query: { periodStart: string; periodEnd: string };
}

export type GetForecastAccuracyResult = S.ForecastAccuracy;

/** What was forecast each week, against what actually closed */
export function getForecastAccuracy(args: GetForecastAccuracyArgs, options?: RequestOptions): Promise<GetForecastAccuracyResult> {
  return request<GetForecastAccuracyResult>({
    operationId: "getForecastAccuracy",
    method: "GET",
    path: "/forecast/accuracy",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface GetForecastRollUpArgs {
  query: { periodStart: string; periodEnd: string };
}

export type GetForecastRollUpResult = S.ForecastRollUp;

/** What a reporting line has submitted for a period */
export function getForecastRollUp(args: GetForecastRollUpArgs, options?: RequestOptions): Promise<GetForecastRollUpResult> {
  return request<GetForecastRollUpResult>({
    operationId: "getForecastRollUp",
    method: "GET",
    path: "/forecast/rollup",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type GetHomeResult = {
  kpi: {
    label: "openPipeline";
    amount: string;
    currency: string;
    dealCount: number;
    countedInCurrency: number;
    weighted: string;
  };
  myDay: S.HomeMyDay;
  pipeline: S.HomePipeline;
  recent: S.HomeRecent;
  checklist: S.HomeChecklist;
  lists: Array<{
    objectKey: string;
    label: string;
    count: number;
    href: string;
  }>;
};

/** What needs doing today */
export function getHome(options?: RequestOptions): Promise<GetHomeResult> {
  return request<GetHomeResult>({
    operationId: "getHome",
    method: "GET",
    path: "/home",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetInvoiceArgs {
  path: { invoiceId: string };
}

export type GetInvoiceResult = {
  invoice: S.Invoice;
};

/** One invoice and its lines */
export function getInvoice(args: GetInvoiceArgs, options?: RequestOptions): Promise<GetInvoiceResult> {
  return request<GetInvoiceResult>({
    operationId: "getInvoice",
    method: "GET",
    path: "/invoices/{invoiceId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetLandingPageArgs {
  path: { pageId: string };
}

export type GetLandingPageResult = S.LandingPage;

/** One landing page */
export function getLandingPage(args: GetLandingPageArgs, options?: RequestOptions): Promise<GetLandingPageResult> {
  return request<GetLandingPageResult>({
    operationId: "getLandingPage",
    method: "GET",
    path: "/landing-pages/{pageId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetLeadArgs {
  path: { leadId: string };
}

export type GetLeadResult = S.LeadDetail;

/** One lead, its score breakdown and where it converted to */
export function getLead(args: GetLeadArgs, options?: RequestOptions): Promise<GetLeadResult> {
  return request<GetLeadResult>({
    operationId: "getLead",
    method: "GET",
    path: "/leads/{leadId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetLoyaltyBalanceArgs {
  path: { accountId: string };
}

export type GetLoyaltyBalanceResult = {
  points: number;
  wallet: string;
};

/** Points and wallet, computed from the ledger */
export function getLoyaltyBalance(args: GetLoyaltyBalanceArgs, options?: RequestOptions): Promise<GetLoyaltyBalanceResult> {
  return request<GetLoyaltyBalanceResult>({
    operationId: "getLoyaltyBalance",
    method: "GET",
    path: "/accounts/{accountId}/loyalty",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetMarketingEventArgs {
  path: { eventId: string };
  body: Record<string, never>;
}

export type GetMarketingEventResult = {
  event: S.MarketingEvent;
  registrations: Array<S.EventRegistration>;
  reminders: {
    sent: number;
    skipped: number;
  };
};

/** One event, its registrations - and any reminders now due are sent */
export function getMarketingEvent(args: GetMarketingEventArgs, options?: RequestOptions): Promise<GetMarketingEventResult> {
  return request<GetMarketingEventResult>({
    operationId: "getMarketingEvent",
    method: "POST",
    path: "/marketing-events/{eventId}/open",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface GetMyWorkArgs {
  query?: { limit?: number };
}

export type GetMyWorkResult = {
  steps: Array<S.QueueRow>;
  types: Array<S.ActivityType>;
  unscheduled: {
    contact: number;
  };
};

/** Every step scheduled for me, across every object */
export function getMyWork(args: GetMyWorkArgs, options?: RequestOptions): Promise<GetMyWorkResult> {
  return request<GetMyWorkResult>({
    operationId: "getMyWork",
    method: "GET",
    path: "/work",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface GetObjectLayoutArgs {
  path: { objectKey: string };
}

export type GetObjectLayoutResult = {
  layout: S.LayoutSpec;
  fields: Array<{
    key: string;
    label: string;
    type: "text" | "textarea" | "email" | "phone" | "url" | "number" | "decimal" | "currency" | "percent" | "date" | "datetime" | "boolean" | "picklist" | "multipicklist" | "lookup" | "user" | "file" | "tags" | "address" | "formula" | "rollup";
    isCustom: boolean;
    required: boolean;
  }>;
  budget: number;
};

/** An object-s layout and the fields available to put on it */
export function getObjectLayout(args: GetObjectLayoutArgs, options?: RequestOptions): Promise<GetObjectLayoutResult> {
  return request<GetObjectLayoutResult>({
    operationId: "getObjectLayout",
    method: "GET",
    path: "/setup/objects/{objectKey}/layout",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetObjectRecordArgs {
  path: { objectKey: string; recordId: string };
}

export type GetObjectRecordResult = {
  [key: string]: (string | number | boolean | null);
};

/** One record of any object */
export function getObjectRecord(args: GetObjectRecordArgs, options?: RequestOptions): Promise<GetObjectRecordResult> {
  return request<GetObjectRecordResult>({
    operationId: "getObjectRecord",
    method: "GET",
    path: "/records/{objectKey}/{recordId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type GetOnboardingResult = {
  questions: Array<S.WizardQuestion>;
  answers: {
    [key: string]: Array<string>;
  };
  completed: boolean;
};

/** The five setup questions and any answers already given */
export function getOnboarding(options?: RequestOptions): Promise<GetOnboardingResult> {
  return request<GetOnboardingResult>({
    operationId: "getOnboarding",
    method: "GET",
    path: "/onboarding",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetPipelineFunnelArgs {
  query?: { pipelineId?: string; days?: number; from?: string; to?: string; ownerId?: string; segmentBy?: "owner" | "source" | "industry" | "segment" | "size"; segmentValue?: string };
}

export type GetPipelineFunnelResult = S.PipelineFunnel;

/** Stage-to-stage conversion and time in stage */
export function getPipelineFunnel(args: GetPipelineFunnelArgs, options?: RequestOptions): Promise<GetPipelineFunnelResult> {
  return request<GetPipelineFunnelResult>({
    operationId: "getPipelineFunnel",
    method: "GET",
    path: "/pipelines/funnel",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type GetPlaybookRampResult = S.PlaybookRamp;

/** How long somebody counts as still ramping */
export function getPlaybookRamp(options?: RequestOptions): Promise<GetPlaybookRampResult> {
  return request<GetPlaybookRampResult>({
    operationId: "getPlaybookRamp",
    method: "GET",
    path: "/setup/playbook-ramp",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetProductArgs {
  path: { productId: string };
}

export type GetProductResult = {
  product: S.Product;
};

/** One product */
export function getProduct(args: GetProductArgs, options?: RequestOptions): Promise<GetProductResult> {
  return request<GetProductResult>({
    operationId: "getProduct",
    method: "GET",
    path: "/products/{productId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetProductVariantsArgs {
  path: { productId: string };
}

export type GetProductVariantsResult = {
  productId: string;
  productName: string;
  sku: string;
  listPrice: string;
  currency: string;
  attributes: Array<S.ProductAttribute>;
  variants: Array<S.ProductVariant>;
  plan: {
    total: number;
    existing: number;
    warn: boolean;
    threshold: number;
  };
  grid: (S.VariantGrid | null);
};

/** A product-s attributes, the variants they made, and the grid to order across */
export function getProductVariants(args: GetProductVariantsArgs, options?: RequestOptions): Promise<GetProductVariantsResult> {
  return request<GetProductVariantsResult>({
    operationId: "getProductVariants",
    method: "GET",
    path: "/products/{productId}/variants",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetPromotionRedemptionsArgs {
  path: { promotionId: string };
}

export type GetPromotionRedemptionsResult = {
  redemptions: Array<S.PromotionRedemption>;
};

/** Every use of one promotion, most recent first */
export function getPromotionRedemptions(args: GetPromotionRedemptionsArgs, options?: RequestOptions): Promise<GetPromotionRedemptionsResult> {
  return request<GetPromotionRedemptionsResult>({
    operationId: "getPromotionRedemptions",
    method: "GET",
    path: "/promotions/{promotionId}/redemptions",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetQuoteArgs {
  path: { quoteId: string };
}

export type GetQuoteResult = S.Quote;

/** One quote, its lines and its totals */
export function getQuote(args: GetQuoteArgs, options?: RequestOptions): Promise<GetQuoteResult> {
  return request<GetQuoteResult>({
    operationId: "getQuote",
    method: "GET",
    path: "/quotes/{quoteId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetQuoteTemplateArgs {
  path: { templateId: string };
  query?: { version?: number };
}

export type GetQuoteTemplateResult = S.QuoteTemplate;

/** One quotation template, at its current version or an earlier one */
export function getQuoteTemplate(args: GetQuoteTemplateArgs, options?: RequestOptions): Promise<GetQuoteTemplateResult> {
  return request<GetQuoteTemplateResult>({
    operationId: "getQuoteTemplate",
    method: "GET",
    path: "/quote-templates/{templateId}",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export interface GetRecordApprovalsArgs {
  path: { objectKey: string; recordId: string };
}

export type GetRecordApprovalsResult = {
  pending: (S.ApprovalRequest | null);
  history: Array<S.ApprovalRequest>;
};

/** What is waiting on a record, and what was decided before */
export function getRecordApprovals(args: GetRecordApprovalsArgs, options?: RequestOptions): Promise<GetRecordApprovalsResult> {
  return request<GetRecordApprovalsResult>({
    operationId: "getRecordApprovals",
    method: "GET",
    path: "/approvals/{objectKey}/{recordId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetRecordFieldsArgs {
  path: { objectKey: string; recordId: string };
}

export type GetRecordFieldsResult = {
  record: {
    [key: string]: S.FieldValue;
  };
  fields: Array<S.FieldDefinition>;
  layout: S.RecordLayout;
};

/** A record-s values, the fields they belong to, and the layout */
export function getRecordFields(args: GetRecordFieldsArgs, options?: RequestOptions): Promise<GetRecordFieldsResult> {
  return request<GetRecordFieldsResult>({
    operationId: "getRecordFields",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/fields",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetRecordFollowStateArgs {
  path: { objectKey: string; recordId: string };
}

export type GetRecordFollowStateResult = {
  following: boolean;
  implicit: boolean;
  followers: number;
};

/** Whether I follow this record, and how many others do */
export function getRecordFollowState(args: GetRecordFollowStateArgs, options?: RequestOptions): Promise<GetRecordFollowStateResult> {
  return request<GetRecordFollowStateResult>({
    operationId: "getRecordFollowState",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/follow",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetRecordTimelineArgs {
  path: { objectKey: string; recordId: string };
  query?: { limit?: number; cursor?: (string | null); types?: string };
}

export type GetRecordTimelineResult = {
  entries: Array<S.TimelineEntry>;
  limit: number;
  hasMore: boolean;
  nextCursor: (string | null);
  total: number;
  types: Array<{
    type: string;
    count: number;
  }>;
};

/** What has happened on this record */
export function getRecordTimeline(args: GetRecordTimelineArgs, options?: RequestOptions): Promise<GetRecordTimelineResult> {
  return request<GetRecordTimelineResult>({
    operationId: "getRecordTimeline",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/timeline",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export interface GetRecordValidationArgs {
  path: { objectKey: string; recordId: string };
}

export type GetRecordValidationResult = {
  findings: Array<S.ValidationFinding>;
};

/** What a record breaks right now */
export function getRecordValidation(args: GetRecordValidationArgs, options?: RequestOptions): Promise<GetRecordValidationResult> {
  return request<GetRecordValidationResult>({
    operationId: "getRecordValidation",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/validation",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetReportArgs {
  path: { reportId: string };
}

export type GetReportResult = {
  report: S.SavedReport;
  result: S.ReportResult;
};

/** One saved report and its current figures */
export function getReport(args: GetReportArgs, options?: RequestOptions): Promise<GetReportResult> {
  return request<GetReportResult>({
    operationId: "getReport",
    method: "GET",
    path: "/reports/{reportId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetSalesOrderArgs {
  path: { orderId: string };
}

export type GetSalesOrderResult = {
  order: S.SalesOrder;
};

/** One order, with its lines and every change made to it */
export function getSalesOrder(args: GetSalesOrderArgs, options?: RequestOptions): Promise<GetSalesOrderResult> {
  return request<GetSalesOrderResult>({
    operationId: "getSalesOrder",
    method: "GET",
    path: "/sales-orders/{orderId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetSegmentArgs {
  path: { segmentId: string };
}

export type GetSegmentResult = {
  segment: S.Segment;
};

/** One saved audience and its conditions */
export function getSegment(args: GetSegmentArgs, options?: RequestOptions): Promise<GetSegmentResult> {
  return request<GetSegmentResult>({
    operationId: "getSegment",
    method: "GET",
    path: "/segments/{segmentId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type GetSegmentSchemaResult = {
  key: string;
  label: string;
  outcome: string;
  maxDepth: number;
  fields: Array<{
    key: string;
    label: string;
    type: string;
    help?: string;
  }>;
};

/** What a segment may ask about */
export function getSegmentSchema(options?: RequestOptions): Promise<GetSegmentSchemaResult> {
  return request<GetSegmentSchemaResult>({
    operationId: "getSegmentSchema",
    method: "GET",
    path: "/segments/schema",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type GetSessionResult = S.Session;

/** Who is signed in, and what this workspace has switched on */
export function getSession(options?: RequestOptions): Promise<GetSessionResult> {
  return request<GetSessionResult>({
    operationId: "getSession",
    method: "GET",
    path: "/me",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type GetSetupChecklistResult = S.SetupChecklist;

/** What is still to do before the workspace is working */
export function getSetupChecklist(options?: RequestOptions): Promise<GetSetupChecklistResult> {
  return request<GetSetupChecklistResult>({
    operationId: "getSetupChecklist",
    method: "GET",
    path: "/onboarding/checklist",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetStagePlaybookArgs {
  path: { stageId: string };
}

export type GetStagePlaybookResult = S.StagePlaybook;

/** What to ask at this stage, and what they will say back */
export function getStagePlaybook(args: GetStagePlaybookArgs, options?: RequestOptions): Promise<GetStagePlaybookResult> {
  return request<GetStagePlaybookResult>({
    operationId: "getStagePlaybook",
    method: "GET",
    path: "/pipelines/stages/{stageId}/playbook",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetSubscriptionArgs {
  path: { subscriptionId: string };
  query?: { asAt?: string };
}

export type GetSubscriptionResult = {
  subscription: S.SubscriptionState;
  events: Array<S.SubscriptionEvent>;
  arr: string;
};

/** One subscription, with its whole plan history */
export function getSubscription(args: GetSubscriptionArgs, options?: RequestOptions): Promise<GetSubscriptionResult> {
  return request<GetSubscriptionResult>({
    operationId: "getSubscription",
    method: "GET",
    path: "/subscriptions/{subscriptionId}",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export interface GetSubscriptionTermArgs {
  path: { subscriptionId: string };
}

export type GetSubscriptionTermResult = S.SubscriptionTerm;

/** When this agreement ends, and every term it has run */
export function getSubscriptionTerm(args: GetSubscriptionTermArgs, options?: RequestOptions): Promise<GetSubscriptionTermResult> {
  return request<GetSubscriptionTermResult>({
    operationId: "getSubscriptionTerm",
    method: "GET",
    path: "/subscriptions/{subscriptionId}/term",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type GetSupportAccessResult = S.SupportAccessState;

/** Whether support may come in, and who has been */
export function getSupportAccess(options?: RequestOptions): Promise<GetSupportAccessResult> {
  return request<GetSupportAccessResult>({
    operationId: "getSupportAccess",
    method: "GET",
    path: "/setup/support-access",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GetTargetGridArgs {
  query: { fiscalYear: number; periodKind: "month" | "quarter" | "year"; subjectKind: "user" | "team" };
}

export type GetTargetGridResult = S.TargetGrid;

/** Targets for a year, with what has been closed against them */
export function getTargetGrid(args: GetTargetGridArgs, options?: RequestOptions): Promise<GetTargetGridResult> {
  return request<GetTargetGridResult>({
    operationId: "getTargetGrid",
    method: "GET",
    path: "/targets",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface GetVisitorContextArgs {
  path: { visitorId: string };
}

export type GetVisitorContextResult = {
  visitorId: string;
  country: (string | null);
  city: (string | null);
  firstSeenAt: string;
  lastSeenAt: string;
  match: ({
    kind: "contact" | "lead";
    id: string;
    name: string;
  } | null);
  pages: Array<{
    url: string;
    title: (string | null);
    viewedAt: string;
  }>;
};

/** Who the visitor is, as far as anything knows */
export function getVisitorContext(args: GetVisitorContextArgs, options?: RequestOptions): Promise<GetVisitorContextResult> {
  return request<GetVisitorContextResult>({
    operationId: "getVisitorContext",
    method: "GET",
    path: "/chat/visitors/{visitorId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type GetWorkingWeekResult = {
  days: Array<number>;
  startMinute: number;
  endMinute: number;
  timeZone: string;
};

/** The days and hours this workspace works */
export function getWorkingWeek(options?: RequestOptions): Promise<GetWorkingWeekResult> {
  return request<GetWorkingWeekResult>({
    operationId: "getWorkingWeek",
    method: "GET",
    path: "/setup/working-week",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface GlobalSearchArgs {
  query: { q: string; limit?: number };
}

export type GlobalSearchResult = {
  groups: Array<S.SearchGroup>;
  searched: Array<string>;
};

/** Search every object this person may read */
export function globalSearch(args: GlobalSearchArgs, options?: RequestOptions): Promise<GlobalSearchResult> {
  return request<GlobalSearchResult>({
    operationId: "globalSearch",
    method: "GET",
    path: "/search",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface GrantEnrichmentCreditsArgs {
  body: {
  credits: number;
  reason: string;
};
}

export type GrantEnrichmentCreditsResult = S.CreditBalance;

/** Add credits, with the reason on the entry */
export function grantEnrichmentCredits(args: GrantEnrichmentCreditsArgs, options?: RequestOptions): Promise<GrantEnrichmentCreditsResult> {
  return request<GrantEnrichmentCreditsResult>({
    operationId: "grantEnrichmentCredits",
    method: "POST",
    path: "/setup/enrichment/credits",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface GraphqlQueryArgs {
  body: {
  query: string;
  variables?: {
    [key: string]: (string | number | boolean | null);
  };
  operationName?: string;
};
}

export type GraphqlQueryResult = {
  data: (string | null);
  errors: Array<S.GraphQLProblem>;
};

/** One query across related objects */
export function graphqlQuery(args: GraphqlQueryArgs, options?: RequestOptions): Promise<GraphqlQueryResult> {
  return request<GraphqlQueryResult>({
    operationId: "graphqlQuery",
    method: "POST",
    path: "/graphql",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ImportMinedArgs {
  body: {
  industry?: (string | null);
  geography?: (string | null);
  role?: (string | null);
  wanted?: number;
  domains: Array<string>;
};
}

export type ImportMinedResult = S.MiningImport;

/** Import the chosen companies as leads */
export function importMined(args: ImportMinedArgs, options?: RequestOptions): Promise<ImportMinedResult> {
  return request<ImportMinedResult>({
    operationId: "importMined",
    method: "POST",
    path: "/setup/mining/import",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface InstallPluginArgs {
  body: {
  manifest: {
    name: string;
    publisher?: string;
    version?: string;
    source: string;
    scopes?: Array<string>;
  };
  grant: Array<string>;
  timeoutMs?: number;
  memoryMb?: number;
};
}

export type InstallPluginResult = S.Plugin;

/** Install a plugin with exactly the permissions somebody ticked */
export function installPlugin(args: InstallPluginArgs, options?: RequestOptions): Promise<InstallPluginResult> {
  return request<InstallPluginResult>({
    operationId: "installPlugin",
    method: "POST",
    path: "/setup/plugins",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface InstallPrebuiltAgentArgs {
  path: { key: string };
  body: Record<string, never>;
}

export type InstallPrebuiltAgentResult = {
  agentId: string;
};

/** Add a ready-made agent to this workspace, switched off */
export function installPrebuiltAgent(args: InstallPrebuiltAgentArgs, options?: RequestOptions): Promise<InstallPrebuiltAgentResult> {
  return request<InstallPrebuiltAgentResult>({
    operationId: "installPrebuiltAgent",
    method: "POST",
    path: "/agents/prebuilt/{key}/install",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface InstallRecipeArgs {
  path: { key: string };
  body: Record<string, never>;
}

export type InstallRecipeResult = {
  flowId: string;
  palette: "automation" | "journey";
  href: string;
};

/** Copy a template into a new draft - never switched on - and say where to open it */
export function installRecipe(args: InstallRecipeArgs, options?: RequestOptions): Promise<InstallRecipeResult> {
  return request<InstallRecipeResult>({
    operationId: "installRecipe",
    method: "POST",
    path: "/automation-templates/{key}/install",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface InstallStandardFrameworksArgs {
  body: Record<string, never>;
}

export type InstallStandardFrameworksResult = {
  frameworks: Array<S.QualificationFramework>;
};

/** Add BANT and MEDDIC, ready to edit */
export function installStandardFrameworks(args: InstallStandardFrameworksArgs, options?: RequestOptions): Promise<InstallStandardFrameworksResult> {
  return request<InstallStandardFrameworksResult>({
    operationId: "installStandardFrameworks",
    method: "POST",
    path: "/qualification/frameworks/standard",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface InvoiceBillingStageArgs {
  path: { stageId: string };
}

export type InvoiceBillingStageResult = {
  invoiceId: string;
  gross: string;
};

/** Raise the stage's invoice as a draft */
export function invoiceBillingStage(args: InvoiceBillingStageArgs, options?: RequestOptions): Promise<InvoiceBillingStageResult> {
  return request<InvoiceBillingStageResult>({
    operationId: "invoiceBillingStage",
    method: "POST",
    path: "/billing-stages/{stageId}/invoice",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface InvoiceLadderArgs {
  path: { invoiceId: string };
}

export type InvoiceLadderResult = {
  ladder: S.DunningLadder;
};

/** Where an invoice has got to, and what was said to whom */
export function invoiceLadder(args: InvoiceLadderArgs, options?: RequestOptions): Promise<InvoiceLadderResult> {
  return request<InvoiceLadderResult>({
    operationId: "invoiceLadder",
    method: "GET",
    path: "/invoices/{invoiceId}/dunning",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface InvoiceOutstandingArgs {
  path: { invoiceId: string };
}

export type InvoiceOutstandingResult = {
  gross: string;
  received: string;
  outstanding: string;
  paid: boolean;
};

/** What is still owed on an invoice */
export function invoiceOutstanding(args: InvoiceOutstandingArgs, options?: RequestOptions): Promise<InvoiceOutstandingResult> {
  return request<InvoiceOutstandingResult>({
    operationId: "invoiceOutstanding",
    method: "GET",
    path: "/invoices/{invoiceId}/outstanding",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface InvoiceRevenueSchedulesArgs {
  path: { invoiceId: string };
}

export type InvoiceRevenueSchedulesResult = {
  schedules: Array<S.RevenueSchedule>;
};

/** The revenue schedules behind one invoice */
export function invoiceRevenueSchedules(args: InvoiceRevenueSchedulesArgs, options?: RequestOptions): Promise<InvoiceRevenueSchedulesResult> {
  return request<InvoiceRevenueSchedulesResult>({
    operationId: "invoiceRevenueSchedules",
    method: "GET",
    path: "/invoices/{invoiceId}/revenue",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface IssueInvoiceArgs {
  path: { invoiceId: string };
  body: {
  dueInDays?: number;
};
}

export type IssueInvoiceResult = {
  invoice: S.Invoice;
};

/** Issue a draft invoice */
export function issueInvoice(args: IssueInvoiceArgs, options?: RequestOptions): Promise<IssueInvoiceResult> {
  return request<IssueInvoiceResult>({
    operationId: "issueInvoice",
    method: "POST",
    path: "/invoices/{invoiceId}/issue",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface IssuePartnerLinkArgs {
  path: { contactId: string };
}

export type IssuePartnerLinkResult = {
  path: string;
  expiresAt: string;
};

/** Issue a partner portal link to somebody at a partner */
export function issuePartnerLink(args: IssuePartnerLinkArgs, options?: RequestOptions): Promise<IssuePartnerLinkResult> {
  return request<IssuePartnerLinkResult>({
    operationId: "issuePartnerLink",
    method: "POST",
    path: "/setup/partners/links/{contactId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface IssueReferralCodeArgs {
  path: { contactId: string };
}

export type IssueReferralCodeResult = {
  code: string;
};

/** The contact's referral code to hand out, made the first time */
export function issueReferralCode(args: IssueReferralCodeArgs, options?: RequestOptions): Promise<IssueReferralCodeResult> {
  return request<IssueReferralCodeResult>({
    operationId: "issueReferralCode",
    method: "POST",
    path: "/contacts/{contactId}/referral-code",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface JobRunsArgs {
  path: { scheduleId: string };
}

export type JobRunsResult = {
  runs: Array<S.JobRun>;
};

/** A job’s recent runs: what each did, or why it failed or was missed */
export function jobRuns(args: JobRunsArgs, options?: RequestOptions): Promise<JobRunsResult> {
  return request<JobRunsResult>({
    operationId: "jobRuns",
    method: "GET",
    path: "/setup/jobs/{scheduleId}/runs",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface LandingPreviewLinkArgs {
  path: { pageId: string };
  body: Record<string, never>;
}

export type LandingPreviewLinkResult = {
  path: string;
  version: number;
};

/** A private link to the current draft, valid for an hour */
export function landingPreviewLink(args: LandingPreviewLinkArgs, options?: RequestOptions): Promise<LandingPreviewLinkResult> {
  return request<LandingPreviewLinkResult>({
    operationId: "landingPreviewLink",
    method: "POST",
    path: "/landing-pages/{pageId}/preview-link",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface LeadPredictionArgs {
  path: { leadId: string };
}

export type LeadPredictionResult = {
  probability: (number | null);
  factors: Array<{
    label: string;
    weight: number;
  }>;
  status: {
    ready: boolean;
    closed: number;
    positives: number;
    negatives: number;
    needed: number;
    trainedAt: (string | null);
    auc: (number | null);
  };
};

/** How likely this lead is to convert, the factors behind it, and how well the model tests */
export function leadPrediction(args: LeadPredictionArgs, options?: RequestOptions): Promise<LeadPredictionResult> {
  return request<LeadPredictionResult>({
    operationId: "leadPrediction",
    method: "GET",
    path: "/leads/{leadId}/prediction",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface LeadViewingsArgs {
  path: { leadId: string };
}

export type LeadViewingsResult = {
  viewings: Array<S.Viewing>;
  rescheduled: number;
};

/** The viewings booked for this lead */
export function leadViewings(args: LeadViewingsArgs, options?: RequestOptions): Promise<LeadViewingsResult> {
  return request<LeadViewingsResult>({
    operationId: "leadViewings",
    method: "GET",
    path: "/leads/{leadId}/viewings",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface LetSupportInArgs {
  body: {
  hours: number;
  note?: (string | null);
};
}

export type LetSupportInResult = S.SupportAccess;

/** Let support into this workspace, until a time you choose */
export function letSupportIn(args: LetSupportInArgs, options?: RequestOptions): Promise<LetSupportInResult> {
  return request<LetSupportInResult>({
    operationId: "letSupportIn",
    method: "POST",
    path: "/setup/support-access",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface LifecycleOfArgs {
  path: { objectKey: "lead" | "contact"; recordId: string };
}

export type LifecycleOfResult = {
  stage: "subscriber" | "lead" | "mql" | "sql" | "customer" | "advocate";
  reached: {
    subscriber: (string | null);
    lead: (string | null);
    mql: (string | null);
    sql: (string | null);
    customer: (string | null);
    advocate: (string | null);
  };
};

/** Where this person is - subscriber to advocate - and when they reached each stage */
export function lifecycleOf(args: LifecycleOfArgs, options?: RequestOptions): Promise<LifecycleOfResult> {
  return request<LifecycleOfResult>({
    operationId: "lifecycleOf",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/lifecycle",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface LifecycleReportArgs {
  query: { periodStart: string; periodEnd: string };
}

export type LifecycleReportResult = {
  periodStart: string;
  periodEnd: string;
  byStage: {
    subscriber: number;
    lead: number;
    mql: number;
    sql: number;
    customer: number;
    advocate: number;
  };
  conversion: Array<{
    from: "subscriber" | "lead" | "mql" | "sql" | "customer" | "advocate";
    to: "subscriber" | "lead" | "mql" | "sql" | "customer" | "advocate";
    reached: number;
    went: number;
    medianDays: (number | null);
  }>;
};

/** Where the people who arrived in a period have got to, and how quickly they moved on */
export function lifecycleReport(args: LifecycleReportArgs, options?: RequestOptions): Promise<LifecycleReportResult> {
  return request<LifecycleReportResult>({
    operationId: "lifecycleReport",
    method: "GET",
    path: "/analytics/lifecycle",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface LiftSuppressionArgs {
  body: {
  channel: "email" | "sms" | "whatsapp" | "push";
  address: string;
};
}

export type LiftSuppressionResult = {
  lifted: boolean;
};

/** Start sending to an address again */
export function liftSuppression(args: LiftSuppressionArgs, options?: RequestOptions): Promise<LiftSuppressionResult> {
  return request<LiftSuppressionResult>({
    operationId: "liftSuppression",
    method: "POST",
    path: "/suppressions/lift",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ListAccountAddressesArgs {
  path: { accountId: string };
}

export type ListAccountAddressesResult = {
  addresses: Array<S.AccountAddress>;
};

/** A company’s billing, shipping, site and branch addresses */
export function listAccountAddresses(args: ListAccountAddressesArgs, options?: RequestOptions): Promise<ListAccountAddressesResult> {
  return request<ListAccountAddressesResult>({
    operationId: "listAccountAddresses",
    method: "GET",
    path: "/accounts/{accountId}/addresses",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListAccountsArgs {
  query?: { limit?: number; cursor?: (string | null); search?: string; ownerId?: string; parentId?: string };
}

export type ListAccountsResult = {
  rows: Array<S.AccountRow>;
  pageSize: number;
  hasMore: boolean;
  nextCursor: (string | null);
  total: number;
};

/** List companies */
export function listAccounts(args: ListAccountsArgs, options?: RequestOptions): Promise<ListAccountsResult> {
  return request<ListAccountsResult>({
    operationId: "listAccounts",
    method: "GET",
    path: "/accounts",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListAgentProposalsResult = {
  proposals: Array<S.AgentProposal>;
};

/** What agents are waiting to be told */
export function listAgentProposals(options?: RequestOptions): Promise<ListAgentProposalsResult> {
  return request<ListAgentProposalsResult>({
    operationId: "listAgentProposals",
    method: "GET",
    path: "/agents/proposals",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListAgentRunsArgs {
  query?: { agentId?: string };
}

export type ListAgentRunsResult = {
  runs: Array<{
    runId: string;
    agentId: string;
    agentName: string;
    invokedByName: (string | null);
    kind: string;
    status: string;
    outcome: (string | null);
    amount: string;
    currency: string;
    startedAt: string;
  }>;
  spend: {
    amount: string;
    currency: string;
    runs: number;
  };
};

/** What agents did, and what it cost */
export function listAgentRuns(args: ListAgentRunsArgs, options?: RequestOptions): Promise<ListAgentRunsResult> {
  return request<ListAgentRunsResult>({
    operationId: "listAgentRuns",
    method: "GET",
    path: "/agents/runs",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListAgentsResult = {
  agents: Array<S.AgentSummary>;
};

/** Agents in this workspace */
export function listAgents(options?: RequestOptions): Promise<ListAgentsResult> {
  return request<ListAgentsResult>({
    operationId: "listAgents",
    method: "GET",
    path: "/agents",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListAgentToolsResult = {
  tools: Array<{
    name: string;
    description: string;
    autonomy: "propose" | "read-only";
    entitlement: string;
    operationId: string;
    available: boolean;
  }>;
};

/** Every action an agent could be given, and whether it is available */
export function listAgentTools(options?: RequestOptions): Promise<ListAgentToolsResult> {
  return request<ListAgentToolsResult>({
    operationId: "listAgentTools",
    method: "GET",
    path: "/agents/tools",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListApiTokensResult = {
  tokens: Array<S.ApiToken>;
  scopes: Array<{
    key: string;
    label: string;
  }>;
};

/** The tokens programs use to call this workspace */
export function listApiTokens(options?: RequestOptions): Promise<ListApiTokensResult> {
  return request<ListApiTokensResult>({
    operationId: "listApiTokens",
    method: "GET",
    path: "/setup/api-tokens",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListApprovalProcessesResult = {
  processes: Array<S.ApprovalProcess>;
  people: Array<{
    userId: string;
    name: string;
  }>;
  roles: Array<{
    roleId: string;
    name: string;
  }>;
};

/** Approval processes, with the people and roles a step can name */
export function listApprovalProcesses(options?: RequestOptions): Promise<ListApprovalProcessesResult> {
  return request<ListApprovalProcessesResult>({
    operationId: "listApprovalProcesses",
    method: "GET",
    path: "/approval-processes",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListApprovalsWaitingResult = {
  requests: Array<S.ApprovalRequest>;
};

/** Everything waiting on a decision, oldest first */
export function listApprovalsWaiting(options?: RequestOptions): Promise<ListApprovalsWaitingResult> {
  return request<ListApprovalsWaitingResult>({
    operationId: "listApprovalsWaiting",
    method: "GET",
    path: "/approvals",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListAssetLibraryArgs {
  query?: { folderId?: string; kind?: "image" | "document" | "snippet" | "template"; search?: string };
}

export type ListAssetLibraryResult = {
  folders: Array<S.AssetFolder>;
  assets: Array<S.Asset>;
  roles: Array<{
    roleId: string;
    name: string;
  }>;
};

/** The library: folders, and the assets matching a folder, kind or search */
export function listAssetLibrary(args: ListAssetLibraryArgs, options?: RequestOptions): Promise<ListAssetLibraryResult> {
  return request<ListAssetLibraryResult>({
    operationId: "listAssetLibrary",
    method: "GET",
    path: "/assets",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ListAttachmentsArgs {
  path: { objectKey: string; recordId: string };
}

export type ListAttachmentsResult = {
  files: Array<S.Attachment>;
  maxBytes: number;
  maxLabel: string;
  scanning: boolean;
};

/** The files on one record */
export function listAttachments(args: ListAttachmentsArgs, options?: RequestOptions): Promise<ListAttachmentsResult> {
  return request<ListAttachmentsResult>({
    operationId: "listAttachments",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/files",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListBookingPagesResult = {
  workspace: string;
  pages: Array<S.BookingPage>;
};

/** The workspace's booking pages, and the address they are reached at */
export function listBookingPages(options?: RequestOptions): Promise<ListBookingPagesResult> {
  return request<ListBookingPagesResult>({
    operationId: "listBookingPages",
    method: "GET",
    path: "/booking-pages",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListBulkJobsResult = {
  jobs: Array<S.BulkJob>;
  batchSize: number;
  rowLimit: (number | null);
};

/** Bulk jobs, unfinished first */
export function listBulkJobs(options?: RequestOptions): Promise<ListBulkJobsResult> {
  return request<ListBulkJobsResult>({
    operationId: "listBulkJobs",
    method: "GET",
    path: "/bulk/jobs",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListCadencesResult = {
  cadences: Array<S.Cadence>;
};

/** Cadences, their steps, and where each enrolled person is */
export function listCadences(options?: RequestOptions): Promise<ListCadencesResult> {
  return request<ListCadencesResult>({
    operationId: "listCadences",
    method: "GET",
    path: "/cadences",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListCampaignRowsArgs {
  query?: { limit?: number; cursor?: (string | null); sort?: string; direction?: "asc" | "desc"; search?: string; status?: "draft" | "scheduled" | "sending" | "sent" | "paused" | "cancelled"; channel?: "email" | "sms" | "whatsapp"; viewId?: string; groupBy?: string; columns?: string };
}

export type ListCampaignRowsResult = {
  rows: Array<{
    campaignId: string;
    name: string;
    channel: "email" | "sms" | "whatsapp";
    status: "draft" | "scheduled" | "sending" | "sent" | "paused" | "cancelled";
    segmentName: (string | null);
    ownerName: (string | null);
    scheduledFor: (string | null);
    sentAt: (string | null);
  }>;
  total: number;
  limit: number;
  hasMore: boolean;
  nextCursor: (string | null);
  sort: {
    field: string;
    direction: "asc" | "desc";
  };
  columns: Array<string>;
  groupBy: (string | null);
  groupable: Array<string>;
};

/** Campaigns as a sortable, pageable list */
export function listCampaignRows(args: ListCampaignRowsArgs, options?: RequestOptions): Promise<ListCampaignRowsResult> {
  return request<ListCampaignRowsResult>({
    operationId: "listCampaignRows",
    method: "GET",
    path: "/campaigns/list",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListCampaignsResult = {
  campaigns: Array<S.CampaignWithPerformance>;
};

/** Campaigns and what each one did */
export function listCampaigns(options?: RequestOptions): Promise<ListCampaignsResult> {
  return request<ListCampaignsResult>({
    operationId: "listCampaigns",
    method: "GET",
    path: "/campaigns",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListCannedResponsesResult = {
  responses: Array<{
    responseId: string;
    shortcut: string;
    body: string;
  }>;
};

/** Replies an operator can insert rather than retype */
export function listCannedResponses(options?: RequestOptions): Promise<ListCannedResponsesResult> {
  return request<ListCannedResponsesResult>({
    operationId: "listCannedResponses",
    method: "GET",
    path: "/chat/canned-responses",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListCapabilitiesResult = {
  groups: Array<{
    key: string;
    label: string;
    capabilities: Array<{
      key: string;
      label: string;
    }>;
  }>;
};

/** The powers a permission set can hand out */
export function listCapabilities(options?: RequestOptions): Promise<ListCapabilitiesResult> {
  return request<ListCapabilitiesResult>({
    operationId: "listCapabilities",
    method: "GET",
    path: "/setup/capabilities",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListCasesArgs {
  query?: { limit?: number; cursor?: (string | null); sort?: string; direction?: "asc" | "desc"; search?: string; status?: "new" | "open" | "waiting-on-customer" | "resolved" | "closed"; priority?: "low" | "normal" | "high" | "urgent"; assignedTo?: string; viewId?: string; groupBy?: string; columns?: string };
}

export type ListCasesResult = {
  rows: Array<S.SupportCase>;
  total: number;
  limit: number;
  hasMore: boolean;
  nextCursor: (string | null);
  sort: {
    field: string;
    direction: "asc" | "desc";
  };
  columns: Array<string>;
  groupBy: (string | null);
  groupable: Array<string>;
};

/** Every case as a sortable, pageable list */
export function listCases(args: ListCasesArgs, options?: RequestOptions): Promise<ListCasesResult> {
  return request<ListCasesResult>({
    operationId: "listCases",
    method: "GET",
    path: "/cases/list",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ListCloseReasonsArgs {
  query: { outcome: "won" | "lost" };
}

export type ListCloseReasonsResult = {
  reasons: Array<S.CloseReason>;
};

/** The reasons a deal may be closed with */
export function listCloseReasons(args: ListCloseReasonsArgs, options?: RequestOptions): Promise<ListCloseReasonsResult> {
  return request<ListCloseReasonsResult>({
    operationId: "listCloseReasons",
    method: "GET",
    path: "/deals/close-reasons",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListCommissionPlansResult = {
  plans: Array<S.CommissionPlan>;
};

/** Commission plans and who is on each */
export function listCommissionPlans(options?: RequestOptions): Promise<ListCommissionPlansResult> {
  return request<ListCommissionPlansResult>({
    operationId: "listCommissionPlans",
    method: "GET",
    path: "/commission/plans",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListCompetitorsResult = {
  competitors: Array<S.Competitor>;
};

/** The competitors this workspace meets */
export function listCompetitors(options?: RequestOptions): Promise<ListCompetitorsResult> {
  return request<ListCompetitorsResult>({
    operationId: "listCompetitors",
    method: "GET",
    path: "/competitors",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListContactsArgs {
  query?: { cursor?: string; limit?: number; sort?: string; direction?: "asc" | "desc"; search?: string; ownerId?: string; accountId?: string; tag?: string; overdueOnly?: boolean; nothingScheduled?: boolean; viewId?: string; groupBy?: string; columns?: string };
}

export type ListContactsResult = {
  rows: Array<S.ContactRow>;
  nextCursor: (string | null);
  hasMore: boolean;
  pageSize: number;
  total: number;
  sort: S.ListSort;
  columns: Array<string>;
  groupBy: (string | null);
  groupable: Array<string>;
};

/** List contacts */
export function listContacts(args: ListContactsArgs, options?: RequestOptions): Promise<ListContactsResult> {
  return request<ListContactsResult>({
    operationId: "listContacts",
    method: "GET",
    path: "/contacts",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ListContentArgs {
  query?: { kind?: "email" | "landing-page" | "article" };
}

export type ListContentResult = {
  documents: Array<S.ContentDocument>;
};

/** Emails, landing pages and articles */
export function listContent(args: ListContentArgs, options?: RequestOptions): Promise<ListContentResult> {
  return request<ListContentResult>({
    operationId: "listContent",
    method: "GET",
    path: "/content",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListCurrenciesResult = {
  reporting: string;
  currencies: Array<string>;
  rates: Array<S.ExchangeRate>;
};

/** The currencies this workspace uses, and the rates between them */
export function listCurrencies(options?: RequestOptions): Promise<ListCurrenciesResult> {
  return request<ListCurrenciesResult>({
    operationId: "listCurrencies",
    method: "GET",
    path: "/setup/currencies",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListCustomObjectsResult = {
  objects: Array<S.CustomObject>;
};

/** Objects this workspace invented */
export function listCustomObjects(options?: RequestOptions): Promise<ListCustomObjectsResult> {
  return request<ListCustomObjectsResult>({
    operationId: "listCustomObjects",
    method: "GET",
    path: "/setup/objects/custom",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListDashboardsResult = {
  dashboards: Array<{
    dashboardId: string;
    key: string;
    name: string;
    description: (string | null);
    isDefault: boolean;
  }>;
};

/** Dashboards in this workspace */
export function listDashboards(options?: RequestOptions): Promise<ListDashboardsResult> {
  return request<ListDashboardsResult>({
    operationId: "listDashboards",
    method: "GET",
    path: "/dashboards",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListDataExportsArgs {
  body: Record<string, never>;
}

export type ListDataExportsResult = {
  exports: Array<S.DataExport>;
  feeds: Array<S.BiFeed>;
  exportable: Array<string>;
};

/** Exports with their files, BI feeds, and what can be exported */
export function listDataExports(args: ListDataExportsArgs, options?: RequestOptions): Promise<ListDataExportsResult> {
  return request<ListDataExportsResult>({
    operationId: "listDataExports",
    method: "POST",
    path: "/data-export/open",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export type ListDataRequestsResult = {
  requests: Array<S.DsrRequest>;
};

/** The data subject request queue */
export function listDataRequests(options?: RequestOptions): Promise<ListDataRequestsResult> {
  return request<ListDataRequestsResult>({
    operationId: "listDataRequests",
    method: "GET",
    path: "/setup/privacy/requests",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListDealQuotesArgs {
  path: { dealId: string };
}

export type ListDealQuotesResult = {
  quotes: Array<{
    quoteId: string;
    number: string;
    status: string;
    total: string;
    currency: string;
  }>;
};

/** The quotes on a deal */
export function listDealQuotes(args: ListDealQuotesArgs, options?: RequestOptions): Promise<ListDealQuotesResult> {
  return request<ListDealQuotesResult>({
    operationId: "listDealQuotes",
    method: "GET",
    path: "/deals/{dealId}/quotes",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListDealsArgs {
  query?: { limit?: number; cursor?: (string | null); pipelineId?: string; sort?: string; direction?: "asc" | "desc"; search?: string; stageId?: string; ownerId?: string; viewId?: string; groupBy?: string; columns?: string };
}

export type ListDealsResult = {
  deals: Array<S.DealCard>;
  limit: number;
  hasMore: boolean;
  nextCursor: (string | null);
  total: number;
  totalAmount: (string | null);
  sort: {
    field: string;
    direction: "asc" | "desc";
  };
  columns: Array<string>;
  groupBy: (string | null);
  groupable: Array<string>;
};

/** Deals as a list */
export function listDeals(args: ListDealsArgs, options?: RequestOptions): Promise<ListDealsResult> {
  return request<ListDealsResult>({
    operationId: "listDeals",
    method: "GET",
    path: "/deals",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListDealTemplatesResult = {
  templates: Array<S.DealTemplate>;
};

/** The shapes a new deal can start from */
export function listDealTemplates(options?: RequestOptions): Promise<ListDealTemplatesResult> {
  return request<ListDealTemplatesResult>({
    operationId: "listDealTemplates",
    method: "GET",
    path: "/deal-templates",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListDerivedFieldsArgs {
  path: { objectKey: string };
}

export type ListDerivedFieldsResult = {
  fields: Array<S.DerivedField>;
  functions: Array<{
    name: string;
    hint: string;
  }>;
};

/** Formula and rollup fields on an object, and the functions available */
export function listDerivedFields(args: ListDerivedFieldsArgs, options?: RequestOptions): Promise<ListDerivedFieldsResult> {
  return request<ListDerivedFieldsResult>({
    operationId: "listDerivedFields",
    method: "GET",
    path: "/setup/objects/{objectKey}/derived",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListDunningStepsResult = {
  steps: Array<S.DunningStep>;
};

/** The failed-payment ladder */
export function listDunningSteps(options?: RequestOptions): Promise<ListDunningStepsResult> {
  return request<ListDunningStepsResult>({
    operationId: "listDunningSteps",
    method: "GET",
    path: "/dunning/steps",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListEmbedsResult = {
  embeds: Array<S.Embed>;
  widgets: Array<{
    key: string;
    label: string;
  }>;
};

/** The widgets this workspace lets other sites render */
export function listEmbeds(options?: RequestOptions): Promise<ListEmbedsResult> {
  return request<ListEmbedsResult>({
    operationId: "listEmbeds",
    method: "GET",
    path: "/setup/embeds",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListEscalationRulesResult = {
  rules: Array<S.EscalationRule>;
};

/** The rules that hand work up when it goes undone */
export function listEscalationRules(options?: RequestOptions): Promise<ListEscalationRulesResult> {
  return request<ListEscalationRulesResult>({
    operationId: "listEscalationRules",
    method: "GET",
    path: "/setup/escalations",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListEventCapturesArgs {
  query: { eventId: string };
}

export type ListEventCapturesResult = {
  captures: Array<{
    captureId: string;
    name: string;
    company: (string | null);
    interest: (string | null);
    capturedAt: string;
  }>;
};

/** Who was captured at an event, newest first */
export function listEventCaptures(args: ListEventCapturesArgs, options?: RequestOptions): Promise<ListEventCapturesResult> {
  return request<ListEventCapturesResult>({
    operationId: "listEventCaptures",
    method: "GET",
    path: "/event-captures",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ListEventsArgs {
  query: { from: string; to: string };
}

export type ListEventsResult = {
  events: Array<S.CalendarEvent>;
};

/** Your meetings in a window */
export function listEvents(args: ListEventsArgs, options?: RequestOptions): Promise<ListEventsResult> {
  return request<ListEventsResult>({
    operationId: "listEvents",
    method: "GET",
    path: "/events",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListEventSubscribersResult = {
  subscribers: Array<S.EventSubscriber>;
};

/** Stream subscribers, each with its position and how far behind it is */
export function listEventSubscribers(options?: RequestOptions): Promise<ListEventSubscribersResult> {
  return request<ListEventSubscribersResult>({
    operationId: "listEventSubscribers",
    method: "GET",
    path: "/developer/stream/subscribers",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListFieldRulesArgs {
  path: { permissionSetId: string };
}

export type ListFieldRulesResult = {
  rules: Array<S.FieldRule>;
  peopleAffected: number;
};

/** The field rules a permission set carries */
export function listFieldRules(args: ListFieldRulesArgs, options?: RequestOptions): Promise<ListFieldRulesResult> {
  return request<ListFieldRulesResult>({
    operationId: "listFieldRules",
    method: "GET",
    path: "/setup/permission-sets/{permissionSetId}/fields",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListFlowRunsArgs {
  path: { flowId: string };
  query?: { limit?: number };
}

export type ListFlowRunsResult = {
  runs: Array<{
    runId: string;
    status: string;
    outcome: (string | null);
    startedAt: string;
    steps: number;
    resumeAt: (string | null);
    exitReason: (string | null);
  }>;
};

/** What this flow has done */
export function listFlowRuns(args: ListFlowRunsArgs, options?: RequestOptions): Promise<ListFlowRunsResult> {
  return request<ListFlowRunsResult>({
    operationId: "listFlowRuns",
    method: "GET",
    path: "/flows/{flowId}/runs",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ListFlowsArgs {
  query?: { palette?: "automation" | "journey" | "chatbot" | "approval" };
}

export type ListFlowsResult = {
  flows: Array<{
    flowId: string;
    palette: "automation" | "journey" | "chatbot" | "approval";
    name: string;
    status: string;
    version: number;
    updatedAt: string;
  }>;
};

/** Flows of one kind */
export function listFlows(args: ListFlowsArgs, options?: RequestOptions): Promise<ListFlowsResult> {
  return request<ListFlowsResult>({
    operationId: "listFlows",
    method: "GET",
    path: "/flows",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ListForecastDealsArgs {
  query: { subjectKind: "user" | "team"; subjectId: string; periodStart: string; periodEnd: string };
}

export type ListForecastDealsResult = {
  currency: string;
  deals: Array<S.ForecastDeal>;
  unconvertible: number;
};

/** The deals behind one cell of the forecast */
export function listForecastDeals(args: ListForecastDealsArgs, options?: RequestOptions): Promise<ListForecastDealsResult> {
  return request<ListForecastDealsResult>({
    operationId: "listForecastDeals",
    method: "GET",
    path: "/forecast/deals",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ListFormSubmissionsArgs {
  path: { embedId: string };
}

export type ListFormSubmissionsResult = {
  rows: Array<S.FormSubmission>;
  counts: {
    created: number;
    matched: number;
    spam: number;
    "no-consent": number;
    invalid: number;
  };
};

/** What a form received, rejections included */
export function listFormSubmissions(args: ListFormSubmissionsArgs, options?: RequestOptions): Promise<ListFormSubmissionsResult> {
  return request<ListFormSubmissionsResult>({
    operationId: "listFormSubmissions",
    method: "GET",
    path: "/embeds/{embedId}/submissions",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListImportsResult = {
  runs: Array<{
    runId: string;
    objectKey: string;
    fileName: string;
    status: string;
    totalRows: number;
    createdRows: number;
    failedRows: number;
    createdAt: string;
    reversibleUntil: (string | null);
  }>;
};

/** Recent imports and which can still be undone */
export function listImports(options?: RequestOptions): Promise<ListImportsResult> {
  return request<ListImportsResult>({
    operationId: "listImports",
    method: "GET",
    path: "/setup/imports",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListInboundMailboxesResult = {
  mailboxes: Array<S.InboundMailbox>;
};

/** Mailboxes that turn incoming mail into leads */
export function listInboundMailboxes(options?: RequestOptions): Promise<ListInboundMailboxesResult> {
  return request<ListInboundMailboxesResult>({
    operationId: "listInboundMailboxes",
    method: "GET",
    path: "/inbound-mailboxes",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListInvoicesArgs {
  query?: { accountId?: string; subscriptionId?: string; status?: "draft" | "issued" | "paid" | "void" };
}

export type ListInvoicesResult = {
  invoices: Array<S.InvoiceHead>;
};

/** Invoices, newest first */
export function listInvoices(args: ListInvoicesArgs, options?: RequestOptions): Promise<ListInvoicesResult> {
  return request<ListInvoicesResult>({
    operationId: "listInvoices",
    method: "GET",
    path: "/invoices",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListIpAllowlistResult = {
  entries: Array<S.IpAllowlistEntry>;
  yourIp: string;
};

/** The networks this workspace may be used from; empty means anywhere */
export function listIpAllowlist(options?: RequestOptions): Promise<ListIpAllowlistResult> {
  return request<ListIpAllowlistResult>({
    operationId: "listIpAllowlist",
    method: "GET",
    path: "/setup/ip-allowlist",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListJobSchedulesArgs {
  body: Record<string, never>;
}

export type ListJobSchedulesResult = {
  schedules: Array<S.JobSchedule>;
};

/** Scheduled jobs, with when each runs next and how the last run went */
export function listJobSchedules(args: ListJobSchedulesArgs, options?: RequestOptions): Promise<ListJobSchedulesResult> {
  return request<ListJobSchedulesResult>({
    operationId: "listJobSchedules",
    method: "POST",
    path: "/setup/jobs/open",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export type ListLandingPagesResult = {
  pages: Array<S.LandingPage>;
};

/** Landing pages, what each looks like now, and which version is live */
export function listLandingPages(options?: RequestOptions): Promise<ListLandingPagesResult> {
  return request<ListLandingPagesResult>({
    operationId: "listLandingPages",
    method: "GET",
    path: "/landing-pages",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListLeadsArgs {
  query?: { limit?: number; cursor?: (string | null); search?: string; status?: string; ownerId?: string; includeConverted?: boolean; sort?: string; direction?: "asc" | "desc"; viewId?: string; groupBy?: string; columns?: string };
}

export type ListLeadsResult = {
  rows: Array<S.LeadRow>;
  pageSize: number;
  hasMore: boolean;
  nextCursor: (string | null);
  total: number;
  statuses: Array<{
    key: string;
    label: string;
    count: number;
  }>;
  sort: {
    field: string;
    direction: "asc" | "desc";
  };
  columns: Array<string>;
  groupBy: (string | null);
  groupable: Array<string>;
};

/** The inbound queue, by status and score */
export function listLeads(args: ListLeadsArgs, options?: RequestOptions): Promise<ListLeadsResult> {
  return request<ListLeadsResult>({
    operationId: "listLeads",
    method: "GET",
    path: "/leads",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListLeadScoreRulesResult = {
  rules: Array<S.LeadScoreRule>;
  decayPerWeek: number;
};

/** The rules this workspace scores leads by */
export function listLeadScoreRules(options?: RequestOptions): Promise<ListLeadScoreRulesResult> {
  return request<ListLeadScoreRulesResult>({
    operationId: "listLeadScoreRules",
    method: "GET",
    path: "/leads/score-rules",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListMarketingEventsResult = {
  events: Array<S.MarketingEvent>;
  journeys: Array<{
    flowId: string;
    name: string;
  }>;
};

/** Webinars and events, each with its registration funnel */
export function listMarketingEvents(options?: RequestOptions): Promise<ListMarketingEventsResult> {
  return request<ListMarketingEventsResult>({
    operationId: "listMarketingEvents",
    method: "GET",
    path: "/marketing-events",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListModelRoutingResult = {
  providers: Array<S.ModelProvider>;
  routes: Array<S.ModelRouteStep>;
  tasks: Array<"agent" | "draft" | "summarise">;
};

/** Which model answers each kind of request, and whose account pays */
export function listModelRouting(options?: RequestOptions): Promise<ListModelRoutingResult> {
  return request<ListModelRoutingResult>({
    operationId: "listModelRouting",
    method: "GET",
    path: "/setup/model-routing",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListModuleCatalogueResult = {
  modules: Array<S.ModuleCard>;
};

/** Every module, its state and what it contains */
export function listModuleCatalogue(options?: RequestOptions): Promise<ListModuleCatalogueResult> {
  return request<ListModuleCatalogueResult>({
    operationId: "listModuleCatalogue",
    method: "GET",
    path: "/setup/modules",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListMyDevicesResult = {
  devices: Array<S.DeviceSession>;
};

/** Where this account is currently signed in */
export function listMyDevices(options?: RequestOptions): Promise<ListMyDevicesResult> {
  return request<ListMyDevicesResult>({
    operationId: "listMyDevices",
    method: "GET",
    path: "/me/devices",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListObjectRecordsArgs {
  path: { objectKey: string };
  query?: { limit?: number; cursor?: string; sort?: string; direction?: "asc" | "desc"; viewId?: string };
}

export type ListObjectRecordsResult = S.ApiRecordPage;

/** Records of any object, standard or custom */
export function listObjectRecords(args: ListObjectRecordsArgs, options?: RequestOptions): Promise<ListObjectRecordsResult> {
  return request<ListObjectRecordsResult>({
    operationId: "listObjectRecords",
    method: "GET",
    path: "/records/{objectKey}",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListObjectsResult = {
  objects: Array<S.ApiObject>;
};

/** Every object this API exposes, and what you may do with each */
export function listObjects(options?: RequestOptions): Promise<ListObjectsResult> {
  return request<ListObjectsResult>({
    operationId: "listObjects",
    method: "GET",
    path: "/objects",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListObjectSharingResult = {
  objects: Array<S.ObjectSharing>;
};

/** How this workspace shares each object */
export function listObjectSharing(options?: RequestOptions): Promise<ListObjectSharingResult> {
  return request<ListObjectSharingResult>({
    operationId: "listObjectSharing",
    method: "GET",
    path: "/setup/sharing",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListPalettesResult = {
  palettes: Array<{
    key: "automation" | "journey" | "chatbot" | "approval";
    label: string;
    entries: Array<{
      type: string;
      kind: "trigger" | "condition" | "action" | "delay" | "ask" | "handoff" | "stop";
      label: string;
      icon: string;
      summary: string;
      branches: Array<string>;
    }>;
  }>;
};

/** The four node palettes */
export function listPalettes(options?: RequestOptions): Promise<ListPalettesResult> {
  return request<ListPalettesResult>({
    operationId: "listPalettes",
    method: "GET",
    path: "/flows/palettes",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListPeopleResult = {
  people: Array<S.Person>;
  roles: Array<S.RoleNode>;
  teams: Array<S.TeamSummary>;
};

/** Who is here, the reporting line, and the teams */
export function listPeople(options?: RequestOptions): Promise<ListPeopleResult> {
  return request<ListPeopleResult>({
    operationId: "listPeople",
    method: "GET",
    path: "/setup/people",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListPermissionSetsResult = {
  sets: Array<S.PermissionSetSummary>;
  held: Array<{
    userId: string;
    permissionSetIds: Array<string>;
  }>;
};

/** Every permission set, what it grants, and who holds it */
export function listPermissionSets(options?: RequestOptions): Promise<ListPermissionSetsResult> {
  return request<ListPermissionSetsResult>({
    operationId: "listPermissionSets",
    method: "GET",
    path: "/setup/permission-sets",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListPipelinesResult = {
  pipelines: Array<{
    pipelineId: string;
    label: string;
    isDefault: boolean;
  }>;
};

/** The pipelines this workspace has */
export function listPipelines(options?: RequestOptions): Promise<ListPipelinesResult> {
  return request<ListPipelinesResult>({
    operationId: "listPipelines",
    method: "GET",
    path: "/pipelines",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListPipelineShapesResult = {
  pipelines: Array<S.PipelineShape>;
};

/** Every pipeline with its stages, and what sits in each */
export function listPipelineShapes(options?: RequestOptions): Promise<ListPipelineShapesResult> {
  return request<ListPipelineShapesResult>({
    operationId: "listPipelineShapes",
    method: "GET",
    path: "/setup/pipelines",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListPlansArgs {
  query?: { on?: string };
}

export type ListPlansResult = {
  plans: Array<S.Plan>;
};

/** The plan catalogue, priced as at a date */
export function listPlans(args: ListPlansArgs, options?: RequestOptions): Promise<ListPlansResult> {
  return request<ListPlansResult>({
    operationId: "listPlans",
    method: "GET",
    path: "/plans",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListPluginsResult = {
  plugins: Array<S.Plugin>;
  scopes: Array<{
    key: string;
    label: string;
    detail: string;
  }>;
};

/** Installed plugins, enabled first */
export function listPlugins(options?: RequestOptions): Promise<ListPluginsResult> {
  return request<ListPluginsResult>({
    operationId: "listPlugins",
    method: "GET",
    path: "/setup/plugins",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListPrebuiltAgentsResult = {
  prebuilt: Array<S.PrebuiltAgent>;
};

/** The agents that can be installed ready-made, and which are already here */
export function listPrebuiltAgents(options?: RequestOptions): Promise<ListPrebuiltAgentsResult> {
  return request<ListPrebuiltAgentsResult>({
    operationId: "listPrebuiltAgents",
    method: "GET",
    path: "/agents/prebuilt",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListPricelistsResult = {
  pricelists: Array<S.PricelistSummary>;
};

/** The price books, and whether each still decides anything */
export function listPricelists(options?: RequestOptions): Promise<ListPricelistsResult> {
  return request<ListPricelistsResult>({
    operationId: "listPricelists",
    method: "GET",
    path: "/pricelists",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListProductsArgs {
  query?: { search?: string };
}

export type ListProductsResult = {
  products: Array<{
    productId: string;
    sku: string;
    name: string;
    listPrice: string;
    unit: string;
    currency: string;
  }>;
};

/** The catalogue, for picking a line - the first 25 active products by name, or those matching a search */
export function listProducts(args: ListProductsArgs, options?: RequestOptions): Promise<ListProductsResult> {
  return request<ListProductsResult>({
    operationId: "listProducts",
    method: "GET",
    path: "/products",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListPromotionsResult = {
  promotions: Array<S.PromotionSummary>;
};

/** Coupons and automatic promotions, with whether each still does anything */
export function listPromotions(options?: RequestOptions): Promise<ListPromotionsResult> {
  return request<ListPromotionsResult>({
    operationId: "listPromotions",
    method: "GET",
    path: "/promotions",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListQualificationFrameworksArgs {
  query?: { objectKey?: "lead" | "deal" };
}

export type ListQualificationFrameworksResult = {
  frameworks: Array<S.QualificationFramework>;
};

/** The qualification frameworks this workspace uses */
export function listQualificationFrameworks(args: ListQualificationFrameworksArgs, options?: RequestOptions): Promise<ListQualificationFrameworksResult> {
  return request<ListQualificationFrameworksResult>({
    operationId: "listQualificationFrameworks",
    method: "GET",
    path: "/qualification/frameworks",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ListQuoteTemplatesArgs {
  query?: { currency?: string };
}

export type ListQuoteTemplatesResult = {
  templates: Array<{
    templateId: string;
    name: string;
    currency: string;
    version: number;
    lineCount: number;
    updatedAt: string;
  }>;
};

/** Quotation templates, optionally only those in one currency */
export function listQuoteTemplates(args: ListQuoteTemplatesArgs, options?: RequestOptions): Promise<ListQuoteTemplatesResult> {
  return request<ListQuoteTemplatesResult>({
    operationId: "listQuoteTemplates",
    method: "GET",
    path: "/quote-templates",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListRecipesResult = {
  recipes: Array<{
    key: string;
    category: "routing" | "hygiene" | "nurture" | "follow-up" | "alerting";
    name: string;
    description: string;
    palette: "automation" | "journey";
    object: string;
    steps: Array<string>;
  }>;
};

/** Ready-made automations and journeys, described in plain words */
export function listRecipes(options?: RequestOptions): Promise<ListRecipesResult> {
  return request<ListRecipesResult>({
    operationId: "listRecipes",
    method: "GET",
    path: "/automation-templates",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListRecycleBinArgs {
  query?: { objectKey?: string };
}

export type ListRecycleBinResult = {
  rows: Array<S.RecycleBinRow>;
  retentionDays: number;
};

/** What has been deleted and not yet lost */
export function listRecycleBin(args: ListRecycleBinArgs, options?: RequestOptions): Promise<ListRecycleBinResult> {
  return request<ListRecycleBinResult>({
    operationId: "listRecycleBin",
    method: "GET",
    path: "/setup/recycle-bin",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListReportsResult = {
  reports: Array<S.SavedReport>;
};

/** Saved reports */
export function listReports(options?: RequestOptions): Promise<ListReportsResult> {
  return request<ListReportsResult>({
    operationId: "listReports",
    method: "GET",
    path: "/reports",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListReportSourcesResult = {
  sources: Array<{
    objectKey: string;
    label: string;
    pluralLabel: string;
    recordRoute: (string | null);
    totalScope: "sharing" | "hierarchy";
    fields: Array<S.ReportField>;
    blank: S.ReportSpec;
  }>;
};

/** What a report can be built on, and which fields each offers */
export function listReportSources(options?: RequestOptions): Promise<ListReportSourcesResult> {
  return request<ListReportSourcesResult>({
    operationId: "listReportSources",
    method: "GET",
    path: "/reports/sources",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListReportSubscriptionsArgs {
  path: { reportId: string };
}

export type ListReportSubscriptionsResult = {
  subscriptions: Array<S.ReportSubscription>;
};

/** Who a report is sent to, and when */
export function listReportSubscriptions(args: ListReportSubscriptionsArgs, options?: RequestOptions): Promise<ListReportSubscriptionsResult> {
  return request<ListReportSubscriptionsResult>({
    operationId: "listReportSubscriptions",
    method: "GET",
    path: "/reports/{reportId}/subscriptions",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListRoutingRulesArgs {
  query?: { object?: "lead" | "support_case" };
}

export type ListRoutingRulesResult = {
  rules: Array<S.LeadRoutingRule>;
};

/** The rules that decide who a new lead belongs to */
export function listRoutingRules(args: ListRoutingRulesArgs, options?: RequestOptions): Promise<ListRoutingRulesResult> {
  return request<ListRoutingRulesResult>({
    operationId: "listRoutingRules",
    method: "GET",
    path: "/setup/routing",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListRuleSchemasResult = {
  schemas: Array<S.RuleSchema>;
};

/** What each kind of rule may ask about */
export function listRuleSchemas(options?: RequestOptions): Promise<ListRuleSchemasResult> {
  return request<ListRuleSchemasResult>({
    operationId: "listRuleSchemas",
    method: "GET",
    path: "/rule-schemas",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListSalesOrdersArgs {
  query?: { accountId?: string; dealId?: string };
}

export type ListSalesOrdersResult = {
  orders: Array<S.SalesOrderSummary>;
};

/** Confirmed orders, newest first */
export function listSalesOrders(args: ListSalesOrdersArgs, options?: RequestOptions): Promise<ListSalesOrdersResult> {
  return request<ListSalesOrdersResult>({
    operationId: "listSalesOrders",
    method: "GET",
    path: "/sales-orders",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListSegmentsResult = {
  segments: Array<S.Segment>;
};

/** The saved audiences in this workspace */
export function listSegments(options?: RequestOptions): Promise<ListSegmentsResult> {
  return request<ListSegmentsResult>({
    operationId: "listSegments",
    method: "GET",
    path: "/segments",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListSendingDomainsResult = {
  domains: Array<{
    domainId: string;
    domain: string;
    status: "pending" | "verified" | "failed";
    lastCheckedAt: (string | null);
    records: Array<{
      recordId: string;
      kind: "spf" | "dkim" | "dmarc" | "return-path";
      host: string;
      recordType: "TXT" | "CNAME" | "MX";
      expectedValue: string;
      observedValue: (string | null);
      verified: boolean;
      checkedAt: (string | null);
      explanation: string;
    }>;
  }>;
};

/** Sending domains with each DNS record to copy, and what DNS last returned for it */
export function listSendingDomains(options?: RequestOptions): Promise<ListSendingDomainsResult> {
  return request<ListSendingDomainsResult>({
    operationId: "listSendingDomains",
    method: "GET",
    path: "/marketing/sending-domains",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListSetupObjectsResult = {
  objects: Array<{
    key: string;
    label: string;
    pluralLabel: string;
    icon: string;
    customFields: number;
  }>;
};

/** The objects a workspace can configure */
export function listSetupObjects(options?: RequestOptions): Promise<ListSetupObjectsResult> {
  return request<ListSetupObjectsResult>({
    operationId: "listSetupObjects",
    method: "GET",
    path: "/setup/objects",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListSmsResult = {
  templates: Array<S.SmsTemplate>;
  senders: Array<{
    country: string;
    sender: string;
    dltEntityId: (string | null);
  }>;
};

/** SMS templates and the sender for each country */
export function listSms(options?: RequestOptions): Promise<ListSmsResult> {
  return request<ListSmsResult>({
    operationId: "listSms",
    method: "GET",
    path: "/marketing/sms",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListSubscriptionsArgs {
  query?: { asAt?: string };
}

export type ListSubscriptionsResult = {
  asAt: string;
  subscriptions: Array<S.SubscriptionState>;
  mrr: string;
  arr: string;
  active: number;
};

/** Subscriptions as they stood on a date */
export function listSubscriptions(args: ListSubscriptionsArgs, options?: RequestOptions): Promise<ListSubscriptionsResult> {
  return request<ListSubscriptionsResult>({
    operationId: "listSubscriptions",
    method: "GET",
    path: "/subscriptions",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ListSuppressionsArgs {
  query?: { channel?: "email" | "sms" | "whatsapp" | "push" };
}

export type ListSuppressionsResult = {
  suppressions: Array<{
    suppressionId: string;
    channel: string;
    address: string;
    reason: string;
    detail: (string | null);
    createdAt: string;
  }>;
};

/** Addresses this workspace will not send to */
export function listSuppressions(args: ListSuppressionsArgs, options?: RequestOptions): Promise<ListSuppressionsResult> {
  return request<ListSuppressionsResult>({
    operationId: "listSuppressions",
    method: "GET",
    path: "/suppressions",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListTagsResult = {
  tags: Array<S.Tag>;
  governed: boolean;
};

/** Every tag, most used first */
export function listTags(options?: RequestOptions): Promise<ListTagsResult> {
  return request<ListTagsResult>({
    operationId: "listTags",
    method: "GET",
    path: "/tags",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListTargetListsResult = {
  lists: Array<S.TargetList>;
};

/** Target account lists */
export function listTargetLists(options?: RequestOptions): Promise<ListTargetListsResult> {
  return request<ListTargetListsResult>({
    operationId: "listTargetLists",
    method: "GET",
    path: "/target-lists",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListTaxRatesArgs {
  query?: { on?: string };
}

export type ListTaxRatesResult = {
  rates: Array<S.TaxRate>;
};

/** Tax rates, and which are in force */
export function listTaxRates(args: ListTaxRatesArgs, options?: RequestOptions): Promise<ListTaxRatesResult> {
  return request<ListTaxRatesResult>({
    operationId: "listTaxRates",
    method: "GET",
    path: "/tax-rates",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type ListTerritoriesResult = {
  territories: Array<S.Territory>;
  schema: S.RuleSchema;
  named: Array<{
    territoryId: string;
    accountId: string;
    name: string;
  }>;
};

/** Territories, each with a live count of what it matches and where it overlaps */
export function listTerritories(options?: RequestOptions): Promise<ListTerritoriesResult> {
  return request<ListTerritoriesResult>({
    operationId: "listTerritories",
    method: "GET",
    path: "/setup/territories",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListUnitsResult = {
  units: Array<S.UnitOfMeasure>;
};

/** Every unit of measure, each with what it comes to in its base unit */
export function listUnits(options?: RequestOptions): Promise<ListUnitsResult> {
  return request<ListUnitsResult>({
    operationId: "listUnits",
    method: "GET",
    path: "/units",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListUsageMetersResult = {
  meters: Array<S.UsageMeter>;
};

/** The meters usage is recorded against */
export function listUsageMeters(options?: RequestOptions): Promise<ListUsageMetersResult> {
  return request<ListUsageMetersResult>({
    operationId: "listUsageMeters",
    method: "GET",
    path: "/usage/meters",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListValidationRulesArgs {
  path: { objectKey: string };
}

export type ListValidationRulesResult = {
  schema: S.RuleSchema;
  rules: Array<S.ValidationRule>;
};

/** The validation rules on one object, and what a rule may ask about */
export function listValidationRules(args: ListValidationRulesArgs, options?: RequestOptions): Promise<ListValidationRulesResult> {
  return request<ListValidationRulesResult>({
    operationId: "listValidationRules",
    method: "GET",
    path: "/setup/objects/{objectKey}/validation-rules",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ListViewsArgs {
  path: { objectKey: string };
}

export type ListViewsResult = {
  views: Array<S.SavedView>;
};

/** The saved views this person may use for an object */
export function listViews(args: ListViewsArgs, options?: RequestOptions): Promise<ListViewsResult> {
  return request<ListViewsResult>({
    operationId: "listViews",
    method: "GET",
    path: "/views/{objectKey}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListVisitFormsResult = {
  forms: Array<S.VisitForm>;
};

/** The checklists that can be filled in at a visit */
export function listVisitForms(options?: RequestOptions): Promise<ListVisitFormsResult> {
  return request<ListVisitFormsResult>({
    operationId: "listVisitForms",
    method: "GET",
    path: "/visit-forms",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type ListWebhooksResult = {
  endpoints: Array<S.WebhookEndpoint>;
  sources: Array<S.InboundSource>;
  events: Array<string>;
  automations: Array<{
    flowId: string;
    name: string;
  }>;
};

/** Webhook endpoints with their last week of deliveries, inbound sources, and the events on offer */
export function listWebhooks(options?: RequestOptions): Promise<ListWebhooksResult> {
  return request<ListWebhooksResult>({
    operationId: "listWebhooks",
    method: "GET",
    path: "/developer/webhooks",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface LogContactReplyArgs {
  path: { contactId: string };
  body: {
  note: (string | null);
};
}

export type LogContactReplyResult = {
  logged: boolean;
};

/** They replied: logged on the record, and every cadence they are in stops before its next step */
export function logContactReply(args: LogContactReplyArgs, options?: RequestOptions): Promise<LogContactReplyResult> {
  return request<LogContactReplyResult>({
    operationId: "logContactReply",
    method: "POST",
    path: "/contacts/{contactId}/replied",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface LogRecordActivityArgs {
  path: { objectKey: string; recordId: string };
  body: {
  type: "note" | "call" | "email" | "meeting" | "whatsapp" | "sms";
  subject: string;
  body?: (string | null);
  occurredAt?: (string | null);
  call?: ({
    direction: "outbound" | "inbound";
    outcome: "connected" | "no-answer" | "voicemail" | "wrong-number" | "busy" | "callback-requested";
    seconds: (number | null);
  } | null);
};
}

export type LogRecordActivityResult = {
  activityId: string;
  mentioned: Array<{
    handle: string;
    userId: string;
    name: string;
  }>;
};

/** Log a note, call or meeting on a record */
export function logRecordActivity(args: LogRecordActivityArgs, options?: RequestOptions): Promise<LogRecordActivityResult> {
  return request<LogRecordActivityResult>({
    operationId: "logRecordActivity",
    method: "POST",
    path: "/records/{objectKey}/{recordId}/timeline",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface MailResponseRateArgs {
  path: { campaignId: string };
}

export type MailResponseRateResult = {
  mailed: number;
  responded: number;
};

/** How many were mailed, and how many have replied */
export function mailResponseRate(args: MailResponseRateArgs, options?: RequestOptions): Promise<MailResponseRateResult> {
  return request<MailResponseRateResult>({
    operationId: "mailResponseRate",
    method: "GET",
    path: "/campaigns/{campaignId}/mail-responses",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface MarginReportArgs {
  query: { from: string; to: string; by: "product" | "owner" | "month" };
}

export type MarginReportResult = {
  currency: string;
  costBasis: "current-landed-cost";
  rows: Array<{
    key: string;
    revenue: string;
    costedRevenue: string;
    cost: string;
    margin: string;
    marginPercent: (number | null);
    uncostedRevenue: string;
  }>;
};

/** Ordered revenue against cost by product, owner or month; uncosted revenue shown apart, never costed at zero */
export function marginReport(args: MarginReportArgs, options?: RequestOptions): Promise<MarginReportResult> {
  return request<MarginReportResult>({
    operationId: "marginReport",
    method: "GET",
    path: "/analytics/margin",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface MarkComplaintArgs {
  path: { caseId: string };
  body: {
  category: string;
};
}

export type MarkComplaintResult = {
  done: true;
};

/** Record this case as a formal complaint, starting its deadlines */
export function markComplaint(args: MarkComplaintArgs, options?: RequestOptions): Promise<MarkComplaintResult> {
  return request<MarkComplaintResult>({
    operationId: "markComplaint",
    method: "POST",
    path: "/cases/{caseId}/complaint",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface MarkNotificationsReadArgs {
  body: {
  notificationId?: (string | null);
};
}

export type MarkNotificationsReadResult = {
  marked: number;
  unread: number;
};

/** Mark one notification read, or all of them */
export function markNotificationsRead(args: MarkNotificationsReadArgs, options?: RequestOptions): Promise<MarkNotificationsReadResult> {
  return request<MarkNotificationsReadResult>({
    operationId: "markNotificationsRead",
    method: "POST",
    path: "/me/notifications/read",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface MarkReferralRewardPaidArgs {
  body: {
  referralId: string;
};
}

export type MarkReferralRewardPaidResult = {
  paid: true;
};

/** Record that a referral reward has been paid */
export function markReferralRewardPaid(args: MarkReferralRewardPaidArgs, options?: RequestOptions): Promise<MarkReferralRewardPaidResult> {
  return request<MarkReferralRewardPaidResult>({
    operationId: "markReferralRewardPaid",
    method: "POST",
    path: "/referral-rewards/paid",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface McpCallArgs {
  body: {
  id?: (string | number);
  method: string;
  params?: {
    uri?: string;
    limit?: number;
  };
};
}

export type McpCallResult = {
  jsonrpc: "2.0";
  id: ((string | number) | null);
  result: (string | null);
  error: ({
    code: number;
    message: string;
  } | null);
};

/** The Model Context Protocol endpoint */
export function mcpCall(args: McpCallArgs, options?: RequestOptions): Promise<McpCallResult> {
  return request<McpCallResult>({
    operationId: "mcpCall",
    method: "POST",
    path: "/mcp",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface McpSurfaceArgs {
  path: { tokenId: string };
}

export type McpSurfaceResult = S.McpSurface;

/** Exactly what an external agent sees with this token */
export function mcpSurface(args: McpSurfaceArgs, options?: RequestOptions): Promise<McpSurfaceResult> {
  return request<McpSurfaceResult>({
    operationId: "mcpSurface",
    method: "GET",
    path: "/setup/api-tokens/{tokenId}/mcp",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface MergeRecordsArgs {
  path: { objectKey: string; recordId: string };
  body: {
  loserId: string;
  choices?: Array<{
    field: string;
    from: "winner" | "loser";
  }>;
};
}

export type MergeRecordsResult = {
  winnerId: string;
  fieldsTaken: number;
  rowsMoved: number;
  rowsDropped: number;
};

/** Merge two records into one */
export function mergeRecords(args: MergeRecordsArgs, options?: RequestOptions): Promise<MergeRecordsResult> {
  return request<MergeRecordsResult>({
    operationId: "mergeRecords",
    method: "POST",
    path: "/records/{objectKey}/{recordId}/merge",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface MergeTagsArgs {
  body: {
  keepId: string;
  mergeId: string;
};
}

export type MergeTagsResult = {
  kept: S.Tag;
  moved: number;
};

/** Fold one tag into another */
export function mergeTags(args: MergeTagsArgs, options?: RequestOptions): Promise<MergeTagsResult> {
  return request<MergeTagsResult>({
    operationId: "mergeTags",
    method: "POST",
    path: "/setup/tags/merge",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface MeteredTotalsArgs {
  query: { subscriptionId: string; periodStart: string; periodEnd: string; currency: string };
}

export type MeteredTotalsResult = {
  totals: Array<S.MeteredTotal>;
};

/** What a subscription has used, per meter, for a period */
export function meteredTotals(args: MeteredTotalsArgs, options?: RequestOptions): Promise<MeteredTotalsResult> {
  return request<MeteredTotalsResult>({
    operationId: "meteredTotals",
    method: "GET",
    path: "/usage/totals",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface MintEmbedTokenArgs {
  path: { embedId: string };
  body: Record<string, never>;
}

export type MintEmbedTokenResult = {
  token: string;
  expiresIn: number;
  widget: string;
  objectKey: (string | null);
};

/** A short-lived token for one page load of one widget */
export function mintEmbedToken(args: MintEmbedTokenArgs, options?: RequestOptions): Promise<MintEmbedTokenResult> {
  return request<MintEmbedTokenResult>({
    operationId: "mintEmbedToken",
    method: "POST",
    path: "/embed/{embedId}/token",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface MoveDealArgs {
  path: { dealId: string };
  body: {
  stageId: string;
  closeReason?: (string | null);
  closeReasonId?: (string | null);
};
}

export type MoveDealResult = {
  dealId: string;
  stageId: string;
  stageLabel: string;
  outcome: string;
};

/** Move a deal to another stage */
export function moveDeal(args: MoveDealArgs, options?: RequestOptions): Promise<MoveDealResult> {
  return request<MoveDealResult>({
    operationId: "moveDeal",
    method: "POST",
    path: "/deals/{dealId}/stage",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface MqlQueueArgs {
  query?: { mine?: "true" | "false" };
}

export type MqlQueueResult = {
  threshold: (number | null);
  leads: Array<{
    leadId: string;
    name: string;
    company: (string | null);
    ownerName: (string | null);
    scoreAtHandoff: number;
    score: number;
    mqlAt: string;
    due: (string | null);
    overdue: boolean;
  }>;
};

/** Marketing-qualified leads waiting for sales to accept or turn back, the most overdue first */
export function mqlQueue(args: MqlQueueArgs, options?: RequestOptions): Promise<MqlQueueResult> {
  return request<MqlQueueResult>({
    operationId: "mqlQueue",
    method: "GET",
    path: "/leads/mql",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface MqlReportArgs {
  query?: { days?: number };
}

export type MqlReportResult = {
  threshold: (number | null);
  decided: number;
  accepted: number;
  rejected: number;
  byReason: Array<{
    reasonId: string;
    label: string;
    count: number;
  }>;
  byScore: Array<{
    from: number;
    to: number;
    accepted: number;
    rejected: number;
  }>;
};

/** What sales said about the leads marketing sent: by reason, and by score at handoff */
export function mqlReport(args: MqlReportArgs, options?: RequestOptions): Promise<MqlReportResult> {
  return request<MqlReportResult>({
    operationId: "mqlReport",
    method: "GET",
    path: "/marketing/mql-report",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type MqlSettingsResult = S.MqlSettings;

/** The MQL threshold, the time sales has to answer, and the reasons they can give */
export function mqlSettings(options?: RequestOptions): Promise<MqlSettingsResult> {
  return request<MqlSettingsResult>({
    operationId: "mqlSettings",
    method: "GET",
    path: "/setup/mql",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface MuteNotificationsArgs {
  body: {
  objectKey: string;
  recordId: string;
  muted: boolean;
};
}

export type MuteNotificationsResult = {
  ok: true;
};

/** Stop hearing about one record */
export function muteNotifications(args: MuteNotificationsArgs, options?: RequestOptions): Promise<MuteNotificationsResult> {
  return request<MuteNotificationsResult>({
    operationId: "muteNotifications",
    method: "POST",
    path: "/me/notifications/mute",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export type MyAvailabilityResult = {
  hours: Array<S.WeeklyHours>;
};

/** Your working hours, which your booking pages offer times from */
export function myAvailability(options?: RequestOptions): Promise<MyAvailabilityResult> {
  return request<MyAvailabilityResult>({
    operationId: "myAvailability",
    method: "GET",
    path: "/availability",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type MyCallListsResult = {
  lists: Array<S.CallListSummary>;
};

/** The call lists I am working through */
export function myCallLists(options?: RequestOptions): Promise<MyCallListsResult> {
  return request<MyCallListsResult>({
    operationId: "myCallLists",
    method: "GET",
    path: "/call-lists",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface NoteRecordVisitArgs {
  path: { objectKey: string; recordId: string };
}

export type NoteRecordVisitResult = {
  ok: true;
};

/** Note that this person opened a record */
export function noteRecordVisit(args: NoteRecordVisitArgs, options?: RequestOptions): Promise<NoteRecordVisitResult> {
  return request<NoteRecordVisitResult>({
    operationId: "noteRecordVisit",
    method: "POST",
    path: "/records/{objectKey}/{recordId}/visit",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type NotificationPreferencesResult = {
  preferences: Array<S.NotificationPreference>;
};

/** Which events this person hears about */
export function notificationPreferences(options?: RequestOptions): Promise<NotificationPreferencesResult> {
  return request<NotificationPreferencesResult>({
    operationId: "notificationPreferences",
    method: "GET",
    path: "/me/notifications/preferences",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface NotificationsArgs {
  query?: { limit?: number; unreadOnly?: boolean };
}

export type NotificationsResult = {
  items: Array<S.InboxItem>;
  unread: number;
};

/** This person’s inbox, and the number on the bell */
export function notifications(args: NotificationsArgs, options?: RequestOptions): Promise<NotificationsResult> {
  return request<NotificationsResult>({
    operationId: "notifications",
    method: "GET",
    path: "/me/notifications",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type NurtureResult = {
  settings: S.NurtureSettings;
  enrolments: Array<{
    leadId: string;
    leadName: string;
    wayIn: "rejected" | "not-ready";
    enrolledAt: string;
    exitedAt: (string | null);
    exitReason: (string | null);
  }>;
};

/** Nurture settings, and who is in nurture or left it and why */
export function nurture(options?: RequestOptions): Promise<NurtureResult> {
  return request<NurtureResult>({
    operationId: "nurture",
    method: "GET",
    path: "/nurture",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type OnboardingTemplateResult = {
  steps: Array<S.OnboardingTemplateStep>;
};

/** The steps every new customer is onboarded through */
export function onboardingTemplate(options?: RequestOptions): Promise<OnboardingTemplateResult> {
  return request<OnboardingTemplateResult>({
    operationId: "onboardingTemplate",
    method: "GET",
    path: "/onboarding/template",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface OpenAbTestArgs {
  path: { campaignId: string };
  body: Record<string, never>;
}

export type OpenAbTestResult = {
  test: (S.AbTest | null);
};

/** A campaign's A/B test and its results - decided now if its window has passed */
export function openAbTest(args: OpenAbTestArgs, options?: RequestOptions): Promise<OpenAbTestResult> {
  return request<OpenAbTestResult>({
    operationId: "openAbTest",
    method: "POST",
    path: "/campaigns/{campaignId}/ab-test/open",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface OpenApprovalFromEmailArgs {
  body: {
  token: string;
};
}

export type OpenApprovalFromEmailResult = {
  request: (S.ApprovalChain | null);
};

/** The request an approver’s emailed link names */
export function openApprovalFromEmail(args: OpenApprovalFromEmailArgs, options?: RequestOptions): Promise<OpenApprovalFromEmailResult> {
  return request<OpenApprovalFromEmailResult>({
    operationId: "openApprovalFromEmail",
    method: "POST",
    path: "/public/approvals/open",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface OpenApprovalInboxArgs {
  body: Record<string, never>;
}

export type OpenApprovalInboxResult = {
  requests: Array<S.ApprovalChain>;
  delegation: ({
    delegateId: string;
    startsOn: string;
    endsOn: string;
  } | null);
};

/** What is waiting on me - overdue steps are escalated first */
export function openApprovalInbox(args: OpenApprovalInboxArgs, options?: RequestOptions): Promise<OpenApprovalInboxResult> {
  return request<OpenApprovalInboxResult>({
    operationId: "openApprovalInbox",
    method: "POST",
    path: "/approvals/inbox",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface OpenBookingPageArgs {
  path: { workspace: string; slug: string };
}

export type OpenBookingPageResult = {
  page: ({
    title: string;
    description: (string | null);
    ownerName: string;
    slots: Array<{
      startsAt: string;
      endsAt: string;
    }>;
  } | null);
};

/** A booking page as a visitor sees it: whose it is and the free times */
export function openBookingPage(args: OpenBookingPageArgs, options?: RequestOptions): Promise<OpenBookingPageResult> {
  return request<OpenBookingPageResult>({
    operationId: "openBookingPage",
    method: "GET",
    path: "/public/booking-pages/{workspace}/{slug}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface OpenCallListArgs {
  path: { listId: string };
}

export type OpenCallListResult = {
  list: S.CallList;
  next: (S.CallListEntry | null);
};

/** A call list and where it has got to */
export function openCallList(args: OpenCallListArgs, options?: RequestOptions): Promise<OpenCallListResult> {
  return request<OpenCallListResult>({
    operationId: "openCallList",
    method: "GET",
    path: "/call-lists/{listId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface OpenCustomerQuoteArgs {
  path: { quoteId: string };
  query: { token: string };
}

export type OpenCustomerQuoteResult = {
  quote: (S.CustomerQuote | null);
  refusal: (S.PortalRefusal | null);
};

/** One sent quote, as the customer reads it */
export function openCustomerQuote(args: OpenCustomerQuoteArgs, options?: RequestOptions): Promise<OpenCustomerQuoteResult> {
  return request<OpenCustomerQuoteResult>({
    operationId: "openCustomerQuote",
    method: "GET",
    path: "/portal/quotes/{quoteId}",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export interface OpenDataRequestArgs {
  body: {
  kind: "access" | "erasure";
  subjectEmail: string;
};
}

export type OpenDataRequestResult = S.DsrRequest;

/** Record a request from a data subject */
export function openDataRequest(args: OpenDataRequestArgs, options?: RequestOptions): Promise<OpenDataRequestResult> {
  return request<OpenDataRequestResult>({
    operationId: "openDataRequest",
    method: "POST",
    path: "/setup/privacy/requests",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface OpenPagePreviewArgs {
  body: {
  token: string;
};
}

export type OpenPagePreviewResult = {
  page: (S.PublicLandingPage | null);
};

/** The draft of a landing page, through a private preview link */
export function openPagePreview(args: OpenPagePreviewArgs, options?: RequestOptions): Promise<OpenPagePreviewResult> {
  return request<OpenPagePreviewResult>({
    operationId: "openPagePreview",
    method: "POST",
    path: "/public/pages/preview",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface OpenPartnerPortalArgs {
  query: { token: string };
}

export type OpenPartnerPortalResult = {
  workspaceName: (string | null);
  contact: Array<{
    key: string;
    value: (string | null);
  }>;
  deals: Array<S.PartnerDeal>;
  collateral: Array<S.PartnerCollateral>;
  refusal: ({
    kind: "invalid" | "expired" | "revoked" | "wrong-surface";
    canRequestNewLink: boolean;
  } | null);
};

/** A partner's own registrations, and the links shared with partners */
export function openPartnerPortal(args: OpenPartnerPortalArgs, options?: RequestOptions): Promise<OpenPartnerPortalResult> {
  return request<OpenPartnerPortalResult>({
    operationId: "openPartnerPortal",
    method: "GET",
    path: "/portal/partner/home",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface OpenPortalArgs {
  path: { surface: "booking" | "preferences" | "portal" };
  query: { token: string };
}

export type OpenPortalResult = {
  branding: (S.PortalBranding | null);
  contact: Array<S.PortalField>;
  quotes: Array<{
    quoteId: string;
    fields: Array<S.PortalField>;
  }>;
  preferences: Array<S.PortalPreference>;
  slots: Array<S.BookingSlot>;
  refusal: (S.PortalRefusal | null);
};

/** Open a public surface with a link */
export function openPortal(args: OpenPortalArgs, options?: RequestOptions): Promise<OpenPortalResult> {
  return request<OpenPortalResult>({
    operationId: "openPortal",
    method: "GET",
    path: "/portal/{surface}",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export interface OpenPublicEventArgs {
  path: { workspace: string; slug: string };
}

export type OpenPublicEventResult = {
  event: ({
    name: string;
    kind: "webinar" | "in-person";
    description: (string | null);
    startsAt: string;
    endsAt: string;
    location: (string | null);
    full: boolean;
    open: boolean;
    form: ({
      fields: Array<{
        field: string;
        label: string;
        required: boolean;
      }>;
      consentRequired: boolean;
      consentText: (string | null);
    } | null);
  } | null);
};

/** An event’s registration page, as a visitor sees it */
export function openPublicEvent(args: OpenPublicEventArgs, options?: RequestOptions): Promise<OpenPublicEventResult> {
  return request<OpenPublicEventResult>({
    operationId: "openPublicEvent",
    method: "GET",
    path: "/public/events/{workspace}/{slug}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface OpenPublicPageArgs {
  path: { workspace: string; slug: string };
  query?: { visitor?: string };
}

export type OpenPublicPageResult = {
  page: (S.PublicLandingPage | null);
};

/** A published landing page, as a visitor sees it */
export function openPublicPage(args: OpenPublicPageArgs, options?: RequestOptions): Promise<OpenPublicPageResult> {
  return request<OpenPublicPageResult>({
    operationId: "openPublicPage",
    method: "GET",
    path: "/public/pages/{workspace}/{slug}",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export interface OpenRenewalsArgs {
  body: {
  on?: (string | null);
};
}

export type OpenRenewalsResult = {
  opened: Array<{
    dealId: string;
    subscriptionId: string;
    subscriptionNumber: string;
    termEndsOn: string;
    amount: string;
    currency: string;
    autoRenews: boolean;
  }>;
};

/** Open a renewal deal for every term coming up */
export function openRenewals(args: OpenRenewalsArgs, options?: RequestOptions): Promise<OpenRenewalsResult> {
  return request<OpenRenewalsResult>({
    operationId: "openRenewals",
    method: "POST",
    path: "/revenue/renewals/open",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface OpenSurveyArgs {
  body: {
  token: string;
};
}

export type OpenSurveyResult = {
  survey: ({
    kind: "csat" | "nps" | "ces";
    caseNumber: string;
    subject: string;
    answered: boolean;
  } | null);
};

/** The one question a customer is asked about a resolved case */
export function openSurvey(args: OpenSurveyArgs, options?: RequestOptions): Promise<OpenSurveyResult> {
  return request<OpenSurveyResult>({
    operationId: "openSurvey",
    method: "POST",
    path: "/public/survey/open",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export type PartnerOverviewResult = {
  partners: Array<{
    accountId: string;
    name: string;
    protectionDays: number;
    contacts: Array<{
      contactId: string;
      name: string;
      email: (string | null);
    }>;
  }>;
  registrations: Array<S.PartnerRegistration>;
  collateral: Array<S.PartnerCollateral>;
};

/** Partners, their people, what they registered and the links shared with them */
export function partnerOverview(options?: RequestOptions): Promise<PartnerOverviewResult> {
  return request<PartnerOverviewResult>({
    operationId: "partnerOverview",
    method: "GET",
    path: "/setup/partners",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface PersonReachArgs {
  path: { userId: string };
}

export type PersonReachResult = S.PersonReach;

/** What the reporting line actually gives somebody */
export function personReach(args: PersonReachArgs, options?: RequestOptions): Promise<PersonReachResult> {
  return request<PersonReachResult>({
    operationId: "personReach",
    method: "GET",
    path: "/setup/people/{userId}/reach",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface PinViewArgs {
  path: { viewId: string };
  body: {
  pinned: boolean;
};
}

export type PinViewResult = S.SavedView;

/** Pin or unpin a view for yourself */
export function pinView(args: PinViewArgs, options?: RequestOptions): Promise<PinViewResult> {
  return request<PinViewResult>({
    operationId: "pinView",
    method: "POST",
    path: "/views/detail/{viewId}/pin",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PipelineOnArgs {
  path: { takenOn: string };
  query?: { pipelineId?: string };
}

export type PipelineOnResult = S.PipelineOnDate;

/** What the pipeline looked like on a past date */
export function pipelineOn(args: PipelineOnArgs, options?: RequestOptions): Promise<PipelineOnResult> {
  return request<PipelineOnResult>({
    operationId: "pipelineOn",
    method: "GET",
    path: "/pipelines/snapshots/{takenOn}",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export interface PlanDayArgs {
  query: { day: string; latitude?: number; longitude?: number };
}

export type PlanDayResult = {
  visits: Array<S.PlannedVisit>;
};

/** A day of visits, nearest first from where you are */
export function planDay(args: PlanDayArgs, options?: RequestOptions): Promise<PlanDayResult> {
  return request<PlanDayResult>({
    operationId: "planDay",
    method: "GET",
    path: "/visits/day",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface PlanErasureArgs {
  query: { subjectEmail: string };
}

export type PlanErasureResult = S.ErasurePlan;

/** Every store an erasure would touch, and every store it would not */
export function planErasure(args: PlanErasureArgs, options?: RequestOptions): Promise<PlanErasureResult> {
  return request<PlanErasureResult>({
    operationId: "planErasure",
    method: "GET",
    path: "/setup/privacy/erasure-plan",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type PlanOptionsResult = {
  options: Array<{
    key: string;
    label: string;
    rank: number;
    kind: "base" | "addon";
    current: boolean;
  }>;
};

/** The plans this product offers, and the one this workspace is on */
export function planOptions(options?: RequestOptions): Promise<PlanOptionsResult> {
  return request<PlanOptionsResult>({
    operationId: "planOptions",
    method: "GET",
    path: "/setup/plan/options",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface PlanVisitArgs {
  body: {
  accountId: string;
  contactId: (string | null);
  day: string;
  purpose: (string | null);
};
}

export type PlanVisitResult = {
  visitId: string;
};

/** Put a visit on a day */
export function planVisit(args: PlanVisitArgs, options?: RequestOptions): Promise<PlanVisitResult> {
  return request<PlanVisitResult>({
    operationId: "planVisit",
    method: "POST",
    path: "/visits",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PluginRunsArgs {
  path: { pluginId: string };
}

export type PluginRunsResult = {
  runs: Array<{
    runId: string;
    trigger: string;
    outcome: "ok" | "failed" | "timed-out" | "refused";
    durationMs: number;
    output: (string | null);
    error: (string | null);
    ranAt: string;
  }>;
};

/** What this plugin has been doing */
export function pluginRuns(args: PluginRunsArgs, options?: RequestOptions): Promise<PluginRunsResult> {
  return request<PluginRunsResult>({
    operationId: "pluginRuns",
    method: "GET",
    path: "/setup/plugins/{pluginId}/runs",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface PollEventsArgs {
  path: { subscriberId: string };
  body: {
  limit?: number;
};
}

export type PollEventsResult = {
  events: Array<S.StreamEvent>;
};

/** The next events after this subscriber’s cursor, oldest first */
export function pollEvents(args: PollEventsArgs, options?: RequestOptions): Promise<PollEventsResult> {
  return request<PollEventsResult>({
    operationId: "pollEvents",
    method: "POST",
    path: "/developer/stream/subscribers/{subscriberId}/poll",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PostRecordCommentArgs {
  path: { objectKey: string; recordId: string };
  body: {
  parentId: (string | null);
  body: string;
  recordLabel: string;
};
}

export type PostRecordCommentResult = {
  comment: S.RecordComment;
  mentioned: Array<{
    handle: string;
    userId: string;
    name: string;
  }>;
};

/** Say something about a record, and pull a colleague in with @ */
export function postRecordComment(args: PostRecordCommentArgs, options?: RequestOptions): Promise<PostRecordCommentResult> {
  return request<PostRecordCommentResult>({
    operationId: "postRecordComment",
    method: "POST",
    path: "/records/{objectKey}/{recordId}/comments",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export type PredictionStatusResult = {
  ready: boolean;
  closed: number;
  positives: number;
  negatives: number;
  needed: number;
  trainedAt: (string | null);
  auc: (number | null);
};

/** Whether the prediction is ready, what it learned from, and how well it tests */
export function predictionStatus(options?: RequestOptions): Promise<PredictionStatusResult> {
  return request<PredictionStatusResult>({
    operationId: "predictionStatus",
    method: "GET",
    path: "/leads/prediction",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface PrepareMailDropArgs {
  path: { campaignId: string };
}

export type PrepareMailDropResult = {
  campaignId: string;
  csv: string;
  included: number;
  excluded: Array<{
    reason: string;
    count: number;
  }>;
};

/** Prepare a postal campaign's mailing file, one response code per person */
export function prepareMailDrop(args: PrepareMailDropArgs, options?: RequestOptions): Promise<PrepareMailDropResult> {
  return request<PrepareMailDropResult>({
    operationId: "prepareMailDrop",
    method: "POST",
    path: "/campaigns/{campaignId}/mail-drop",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface PreviewCampaignAudienceArgs {
  path: { campaignId: string };
}

export type PreviewCampaignAudienceResult = {
  total: number;
  reachable: number;
  refusals: Array<S.SendRefusal>;
};

/** How many this campaign would reach, and who it would not */
export function previewCampaignAudience(args: PreviewCampaignAudienceArgs, options?: RequestOptions): Promise<PreviewCampaignAudienceResult> {
  return request<PreviewCampaignAudienceResult>({
    operationId: "previewCampaignAudience",
    method: "GET",
    path: "/campaigns/{campaignId}/audience",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface PreviewEmbedArgs {
  path: { embedId: string };
  body: Record<string, never>;
}

export type PreviewEmbedResult = {
  token: string;
  expiresIn: number;
  widget: string;
  objectKey: (string | null);
};

/** A token for the configuration screen’s own preview */
export function previewEmbed(args: PreviewEmbedArgs, options?: RequestOptions): Promise<PreviewEmbedResult> {
  return request<PreviewEmbedResult>({
    operationId: "previewEmbed",
    method: "POST",
    path: "/setup/embeds/{embedId}/preview",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PreviewFormulaArgs {
  path: { objectKey: string };
  body: {
  formula: string;
  recordId: (string | null);
};
}

export type PreviewFormulaResult = {
  value: (string | number | boolean | null);
  error: ({
    message: string;
    position: number;
  } | null);
  sampleId: (string | null);
};

/** Run a formula against one real record */
export function previewFormula(args: PreviewFormulaArgs, options?: RequestOptions): Promise<PreviewFormulaResult> {
  return request<PreviewFormulaResult>({
    operationId: "previewFormula",
    method: "POST",
    path: "/setup/objects/{objectKey}/derived/preview",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PreviewGroundingArgs {
  path: { agentId: string };
  body: {
  question: string;
};
}

export type PreviewGroundingResult = {
  citations: Array<S.GroundingCitation>;
  sources: Array<S.AgentSource>;
};

/** What this agent would read for a question, before anybody asks it */
export function previewGrounding(args: PreviewGroundingArgs, options?: RequestOptions): Promise<PreviewGroundingResult> {
  return request<PreviewGroundingResult>({
    operationId: "previewGrounding",
    method: "POST",
    path: "/agents/{agentId}/grounding/preview",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PreviewLeadConversionArgs {
  path: { leadId: string };
  body: {
  accountId?: (string | null);
  createDeal?: boolean;
  dealName?: (string | null);
  dealAmount?: (string | null);
  pipelineId?: (string | null);
};
}

export type PreviewLeadConversionResult = S.ConversionPlan;

/** What converting this lead would create */
export function previewLeadConversion(args: PreviewLeadConversionArgs, options?: RequestOptions): Promise<PreviewLeadConversionResult> {
  return request<PreviewLeadConversionResult>({
    operationId: "previewLeadConversion",
    method: "POST",
    path: "/leads/{leadId}/conversion-preview",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PreviewMergeArgs {
  path: { objectKey: string; recordId: string };
  body: {
  loserId: string;
  choices?: Array<{
    field: string;
    from: "winner" | "loser";
  }>;
};
}

export type PreviewMergeResult = S.MergePreview;

/** The record the merge would produce, field by field */
export function previewMerge(args: PreviewMergeArgs, options?: RequestOptions): Promise<PreviewMergeResult> {
  return request<PreviewMergeResult>({
    operationId: "previewMerge",
    method: "POST",
    path: "/records/{objectKey}/{recordId}/merge/preview",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PreviewMiningArgs {
  body: {
  industry?: (string | null);
  geography?: (string | null);
  role?: (string | null);
  wanted?: number;
};
}

export type PreviewMiningResult = S.MiningPreview;

/** What this search would find, and what importing it would cost */
export function previewMining(args: PreviewMiningArgs, options?: RequestOptions): Promise<PreviewMiningResult> {
  return request<PreviewMiningResult>({
    operationId: "previewMining",
    method: "POST",
    path: "/setup/mining/preview",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PreviewModuleImpactArgs {
  path: { moduleKey: string };
  body: {
  enabling: boolean;
};
}

export type PreviewModuleImpactResult = S.ModuleImpact;

/** What switching this module would do */
export function previewModuleImpact(args: PreviewModuleImpactArgs, options?: RequestOptions): Promise<PreviewModuleImpactResult> {
  return request<PreviewModuleImpactResult>({
    operationId: "previewModuleImpact",
    method: "POST",
    path: "/setup/modules/{moduleKey}/impact",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PreviewPermissionSetAssignmentArgs {
  path: { userId: string };
  body: {
  permissionSetIds: Array<string>;
};
}

export type PreviewPermissionSetAssignmentResult = S.PermissionDiff;

/** What would change if this person held these sets */
export function previewPermissionSetAssignment(args: PreviewPermissionSetAssignmentArgs, options?: RequestOptions): Promise<PreviewPermissionSetAssignmentResult> {
  return request<PreviewPermissionSetAssignmentResult>({
    operationId: "previewPermissionSetAssignment",
    method: "POST",
    path: "/setup/people/{userId}/permission-sets/preview",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PreviewPlanChangeArgs {
  body: {
  plan: string;
};
}

export type PreviewPlanChangeResult = S.PlanChangePreview;

/** What changing plan would do, before anything changes */
export function previewPlanChange(args: PreviewPlanChangeArgs, options?: RequestOptions): Promise<PreviewPlanChangeResult> {
  return request<PreviewPlanChangeResult>({
    operationId: "previewPlanChange",
    method: "POST",
    path: "/setup/plan/preview",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PreviewPriceArgs {
  body: {
  productId: string;
  quantity: string;
  accountId?: (string | null);
  on?: (string | null);
};
}

export type PreviewPriceResult = S.PriceAnswer;

/** What a sample customer would pay, and why */
export function previewPrice(args: PreviewPriceArgs, options?: RequestOptions): Promise<PreviewPriceResult> {
  return request<PreviewPriceResult>({
    operationId: "previewPrice",
    method: "POST",
    path: "/pricing/preview",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PreviewPromotionsArgs {
  body: {
  subtotal: string;
  lineCount: number;
  accountId?: (string | null);
  code?: (string | null);
};
}

export type PreviewPromotionsResult = {
  promotions: Array<{
    promotionId: string;
    name: string;
    discount: string;
    explanation: string;
  }>;
};

/** Which promotions apply to an order, and what they take off */
export function previewPromotions(args: PreviewPromotionsArgs, options?: RequestOptions): Promise<PreviewPromotionsResult> {
  return request<PreviewPromotionsResult>({
    operationId: "previewPromotions",
    method: "POST",
    path: "/promotions/preview",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PreviewQuoteDocumentArgs {
  path: { quoteId: string };
  query: { templateId: string };
}

export type PreviewQuoteDocumentResult = S.DocumentPreview;

/** Whether this quote can be printed with a template, and what would stop it */
export function previewQuoteDocument(args: PreviewQuoteDocumentArgs, options?: RequestOptions): Promise<PreviewQuoteDocumentResult> {
  return request<PreviewQuoteDocumentResult>({
    operationId: "previewQuoteDocument",
    method: "GET",
    path: "/quotes/{quoteId}/documents/preview",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export type PreviewRebalanceResult = {
  moves: Array<{
    accountId: string;
    company: string;
    fromName: (string | null);
    toName: (string | null);
  }>;
  total: number;
  shown: number;
};

/** Which companies a rebalance would move, before it moves them */
export function previewRebalance(options?: RequestOptions): Promise<PreviewRebalanceResult> {
  return request<PreviewRebalanceResult>({
    operationId: "previewRebalance",
    method: "GET",
    path: "/setup/territories/rebalance",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface PreviewReportArgs {
  body: {
  spec: S.ReportSpec;
};
}

export type PreviewReportResult = {
  result: (S.ReportResult | null);
  problems: Array<string>;
};

/** What a report would say, without saving it */
export function previewReport(args: PreviewReportArgs, options?: RequestOptions): Promise<PreviewReportResult> {
  return request<PreviewReportResult>({
    operationId: "previewReport",
    method: "POST",
    path: "/reports/preview",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PreviewRestoreArgs {
  path: { backupId: string };
  body: {
  scope: "record" | "object" | "workspace";
  objectKey?: string;
  recordId?: string;
};
}

export type PreviewRestoreResult = S.RestorePreview;

/** What restoring from this backup would put back */
export function previewRestore(args: PreviewRestoreArgs, options?: RequestOptions): Promise<PreviewRestoreResult> {
  return request<PreviewRestoreResult>({
    operationId: "previewRestore",
    method: "POST",
    path: "/setup/backups/{backupId}/restore",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PreviewSegmentArgs {
  body: {
  objectKey: string;
  conditions: S.RuleDocument;
};
}

export type PreviewSegmentResult = {
  count: number;
  sample: Array<{
    id: string;
    label: string;
  }>;
  problems: Array<{
    path: string;
    message: string;
  }>;
};

/** How many match, and a few of them */
export function previewSegment(args: PreviewSegmentArgs, options?: RequestOptions): Promise<PreviewSegmentResult> {
  return request<PreviewSegmentResult>({
    operationId: "previewSegment",
    method: "POST",
    path: "/segments/preview",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PreviewVariantsArgs {
  path: { productId: string };
  body: Record<string, never>;
}

export type PreviewVariantsResult = {
  productId: string;
  total: number;
  warn: boolean;
  threshold: number;
  existing: number;
  combinations: Array<{
    [key: string]: string;
  }>;
};

/** How many variants the attributes would generate */
export function previewVariants(args: PreviewVariantsArgs, options?: RequestOptions): Promise<PreviewVariantsResult> {
  return request<PreviewVariantsResult>({
    operationId: "previewVariants",
    method: "POST",
    path: "/products/{productId}/variant-preview",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ProductBuildableArgs {
  path: { productId: string };
}

export type ProductBuildableResult = {
  productId: string;
  known: boolean;
  buildable: (number | null);
  limitedBy: ({
    productId: string;
    name: string;
    available: string;
    needed: string;
  } | null);
  oldestAgeHours: (number | null);
};

/** How many of a kit the parts allow, and which part runs out first */
export function productBuildable(args: ProductBuildableArgs, options?: RequestOptions): Promise<ProductBuildableResult> {
  return request<ProductBuildableResult>({
    operationId: "productBuildable",
    method: "GET",
    path: "/products/{productId}/buildable",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ProductCompositionArgs {
  path: { productId: string };
}

export type ProductCompositionResult = {
  composition: S.ProductComposition;
};

/** What a kit or bundle is made of */
export function productComposition(args: ProductCompositionArgs, options?: RequestOptions): Promise<ProductCompositionResult> {
  return request<ProductCompositionResult>({
    operationId: "productComposition",
    method: "GET",
    path: "/products/{productId}/composition",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ProductStockArgs {
  path: { productId: string };
}

export type ProductStockResult = {
  stock: S.ProductStock;
};

/** What a warehouse system last said about this product */
export function productStock(args: ProductStockArgs, options?: RequestOptions): Promise<ProductStockResult> {
  return request<ProductStockResult>({
    operationId: "productStock",
    method: "GET",
    path: "/products/{productId}/stock",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ProrateChangeArgs {
  body: {
  fromPlanId: (string | null);
  toPlanId: string;
  currency: string;
  periodStart: string;
  periodEnd: string;
  changeOn: string;
  quantity?: string;
};
}

export type ProrateChangeResult = {
  proration: S.Proration;
};

/** What a mid-period change costs */
export function prorateChange(args: ProrateChangeArgs, options?: RequestOptions): Promise<ProrateChangeResult> {
  return request<ProrateChangeResult>({
    operationId: "prorateChange",
    method: "POST",
    path: "/plans/prorate",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export type PruneBackupsResult = S.BackupPrune;

/** Remove backups past their retention */
export function pruneBackups(options?: RequestOptions): Promise<PruneBackupsResult> {
  return request<PruneBackupsResult>({
    operationId: "pruneBackups",
    method: "POST",
    path: "/setup/backups/prune",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface PublishAgentArgs {
  path: { agentId: string };
  body: {
  status: "active" | "paused" | "draft";
};
}

export type PublishAgentResult = {
  status: string;
  blockedBy: Array<S.AgentTestOutcome>;
};

/** Switch an agent on, or say what is stopping it */
export function publishAgent(args: PublishAgentArgs, options?: RequestOptions): Promise<PublishAgentResult> {
  return request<PublishAgentResult>({
    operationId: "publishAgent",
    method: "POST",
    path: "/agents/{agentId}/status",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PublishCustomEventArgs {
  body: {
  name: string;
  payload: {
    [key: string]: (string | number | boolean | null);
  };
};
}

export type PublishCustomEventResult = {
  seq: number;
};

/** Publish a custom event onto the stream (custom.<name>) */
export function publishCustomEvent(args: PublishCustomEventArgs, options?: RequestOptions): Promise<PublishCustomEventResult> {
  return request<PublishCustomEventResult>({
    operationId: "publishCustomEvent",
    method: "POST",
    path: "/developer/stream/events",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface PublishLandingPageArgs {
  path: { pageId: string };
  body: Record<string, never>;
}

export type PublishLandingPageResult = S.LandingPage;

/** Make the previewed version live */
export function publishLandingPage(args: PublishLandingPageArgs, options?: RequestOptions): Promise<PublishLandingPageResult> {
  return request<PublishLandingPageResult>({
    operationId: "publishLandingPage",
    method: "POST",
    path: "/landing-pages/{pageId}/publish",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export type PurgeExpiredResult = {
  purged: number;
  byObject: Array<{
    objectKey: string;
    purged: number;
  }>;
};

/** Remove everything whose retention window has run out */
export function purgeExpired(options?: RequestOptions): Promise<PurgeExpiredResult> {
  return request<PurgeExpiredResult>({
    operationId: "purgeExpired",
    method: "POST",
    path: "/setup/recycle-bin/purge-expired",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface PurgeRecordArgs {
  path: { objectKey: string; recordId: string };
}

export type PurgeRecordResult = {
  purged: boolean;
};

/** Remove one record for good */
export function purgeRecord(args: PurgeRecordArgs, options?: RequestOptions): Promise<PurgeRecordResult> {
  return request<PurgeRecordResult>({
    operationId: "purgeRecord",
    method: "POST",
    path: "/setup/recycle-bin/{objectKey}/{recordId}/purge",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type PushSettingsResult = {
  publicKey: string;
  devices: number;
};

/** The key a device subscribes with, and how many of this person's devices get alerts */
export function pushSettings(options?: RequestOptions): Promise<PushSettingsResult> {
  return request<PushSettingsResult>({
    operationId: "pushSettings",
    method: "GET",
    path: "/me/push",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface QueryObjectRecordsArgs {
  path: { objectKey: string };
  body: {
  filter?: S.RuleDocument;
  limit?: number;
  cursor?: (string | null);
  sort?: ({
    field: string;
    direction: "asc" | "desc";
  } | null);
  /** A saved view to read through. Its filter, sort, grouping and columns all apply. */
  viewId?: string;
};
}

export type QueryObjectRecordsResult = S.ApiRecordPage;

/** Records of any object, filtered */
export function queryObjectRecords(args: QueryObjectRecordsArgs, options?: RequestOptions): Promise<QueryObjectRecordsResult> {
  return request<QueryObjectRecordsResult>({
    operationId: "queryObjectRecords",
    method: "POST",
    path: "/records/{objectKey}/query",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface QuoteDocumentsArgs {
  path: { quoteId: string };
}

export type QuoteDocumentsResult = S.QuoteDocuments;

/** Documents generated from this quote */
export function quoteDocuments(args: QuoteDocumentsArgs, options?: RequestOptions): Promise<QuoteDocumentsResult> {
  return request<QuoteDocumentsResult>({
    operationId: "quoteDocuments",
    method: "GET",
    path: "/quotes/{quoteId}/documents",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RaiseHealthAlertsArgs {
  body: Record<string, never>;
}

export type RaiseHealthAlertsResult = {
  announced: Array<{
    signal: string;
    told: number;
  }>;
};

/** Tell the people who can act about anything that has just gone down */
export function raiseHealthAlerts(args: RaiseHealthAlertsArgs, options?: RequestOptions): Promise<RaiseHealthAlertsResult> {
  return request<RaiseHealthAlertsResult>({
    operationId: "raiseHealthAlerts",
    method: "POST",
    path: "/setup/health/alerts",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RaisePortalCaseArgs {
  body: {
  token: string;
  subject: string;
  body: string;
};
}

export type RaisePortalCaseResult = {
  number: (string | null);
  refusal: (S.PortalRefusal | null);
};

/** A customer asks for help from their portal */
export function raisePortalCase(args: RaisePortalCaseArgs, options?: RequestOptions): Promise<RaisePortalCaseResult> {
  return request<RaisePortalCaseResult>({
    operationId: "raisePortalCase",
    method: "POST",
    path: "/portal/cases",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RaiseProformaArgs {
  path: { stageId: string };
}

export type RaiseProformaResult = {
  proforma: S.Proforma;
};

/** Request a stage's payment with a pro-forma - not a tax invoice, and not counted as owed */
export function raiseProforma(args: RaiseProformaArgs, options?: RequestOptions): Promise<RaiseProformaResult> {
  return request<RaiseProformaResult>({
    operationId: "raiseProforma",
    method: "POST",
    path: "/billing-stages/{stageId}/proforma",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ReadAssetFileArgs {
  path: { assetId: string };
}

export type ReadAssetFileResult = {
  asset: S.Asset;
  contentBase64: string;
};

/** The file behind an image or document asset */
export function readAssetFile(args: ReadAssetFileArgs, options?: RequestOptions): Promise<ReadAssetFileResult> {
  return request<ReadAssetFileResult>({
    operationId: "readAssetFile",
    method: "GET",
    path: "/assets/{assetId}/file",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ReadAttachmentArgs {
  path: { attachmentId: string };
}

export type ReadAttachmentResult = {
  file: S.Attachment;
  contentBase64: string;
};

/** The file itself */
export function readAttachment(args: ReadAttachmentArgs, options?: RequestOptions): Promise<ReadAttachmentResult> {
  return request<ReadAttachmentResult>({
    operationId: "readAttachment",
    method: "GET",
    path: "/files/{attachmentId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ReassignNextStepArgs {
  path: { stepId: string };
  body: {
  ownerId: string;
};
}

export type ReassignNextStepResult = {
  reassigned: true;
};

/** Pass a step to somebody else */
export function reassignNextStep(args: ReassignNextStepArgs, options?: RequestOptions): Promise<ReassignNextStepResult> {
  return request<ReassignNextStepResult>({
    operationId: "reassignNextStep",
    method: "PATCH",
    path: "/work/{stepId}/owner",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RecallApprovalArgs {
  path: { requestId: string };
  body: {
  comment: (string | null);
};
}

export type RecallApprovalResult = S.ApprovalChain;

/** Take back a request I made */
export function recallApproval(args: RecallApprovalArgs, options?: RequestOptions): Promise<RecallApprovalResult> {
  return request<RecallApprovalResult>({
    operationId: "recallApproval",
    method: "POST",
    path: "/approval-requests/{requestId}/recall",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ReceiveInboundEventArgs {
  path: { token: string };
  body: {
  [key: string]: (string | number | boolean | null);
};
}

export type ReceiveInboundEventResult = {
  accepted: boolean;
  outcome: string;
  runId: (string | null);
};

/** Another system sends an event; it starts the source’s automation with the payload */
export function receiveInboundEvent(args: ReceiveInboundEventArgs, options?: RequestOptions): Promise<ReceiveInboundEventResult> {
  return request<ReceiveInboundEventResult>({
    operationId: "receiveInboundEvent",
    method: "POST",
    path: "/public/hooks/{token}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export type RecentBehaviourResult = {
  events: Array<{
    eventId: number;
    kind: "page-viewed" | "form-submitted" | "email-clicked" | "custom-event" | "field-changed";
    detail: string;
    person: (string | null);
    outcome: (string | null);
    occurredAt: string;
  }>;
};

/** The latest behaviour events, and which journeys each started - or why none */
export function recentBehaviour(options?: RequestOptions): Promise<RecentBehaviourResult> {
  return request<RecentBehaviourResult>({
    operationId: "recentBehaviour",
    method: "GET",
    path: "/journeys/recent-events",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RecentInboundEmailArgs {
  path: { mailboxId: string };
}

export type RecentInboundEmailResult = {
  messages: Array<{
    inboundId: string;
    from: string;
    subject: (string | null);
    receivedAt: string;
    outcome: "lead-created" | "lead-matched" | "contact-matched" | "threaded" | "ignored" | "case-created" | "case-threaded";
    reason: (string | null);
    leadId: (string | null);
    contactId: (string | null);
  }>;
};

/** What recent mail became, and why */
export function recentInboundEmail(args: RecentInboundEmailArgs, options?: RequestOptions): Promise<RecentInboundEmailResult> {
  return request<RecentInboundEmailResult>({
    operationId: "recentInboundEmail",
    method: "GET",
    path: "/inbound-mailboxes/{mailboxId}/recent",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RecentlyViewedArgs {
  query?: { limit?: number };
}

export type RecentlyViewedResult = {
  recents: Array<S.RecentlyViewed>;
};

/** Records this person opened lately */
export function recentlyViewed(args: RecentlyViewedArgs, options?: RequestOptions): Promise<RecentlyViewedResult> {
  return request<RecentlyViewedResult>({
    operationId: "recentlyViewed",
    method: "GET",
    path: "/recents",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface RecognitionByMonthArgs {
  query: { from: string; to: string };
}

export type RecognitionByMonthResult = {
  months: Array<S.RecognitionMonth>;
};

/** What was earned each month, and what is still owed as service */
export function recognitionByMonth(args: RecognitionByMonthArgs, options?: RequestOptions): Promise<RecognitionByMonthResult> {
  return request<RecognitionByMonthResult>({
    operationId: "recognitionByMonth",
    method: "GET",
    path: "/revenue/recognition",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface RecordApprovalsArgs {
  path: { objectKey: string; recordId: string };
}

export type RecordApprovalsResult = {
  requests: Array<S.ApprovalChain>;
  processes: Array<{
    processId: string;
    name: string;
  }>;
};

/** A record's approval requests, each with its whole chain, and the processes it can be submitted to */
export function recordApprovals(args: RecordApprovalsArgs, options?: RequestOptions): Promise<RecordApprovalsResult> {
  return request<RecordApprovalsResult>({
    operationId: "recordApprovals",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/approvals",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RecordBusinessReviewArgs {
  path: { accountId: string };
  body: {
  heldOn: string;
  summary: string;
  agreed?: (string | null);
};
}

export type RecordBusinessReviewResult = S.SuccessPlan;

/** Record a business review that has happened */
export function recordBusinessReview(args: RecordBusinessReviewArgs, options?: RequestOptions): Promise<RecordBusinessReviewResult> {
  return request<RecordBusinessReviewResult>({
    operationId: "recordBusinessReview",
    method: "POST",
    path: "/accounts/{accountId}/success-plan/reviews",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RecordCampaignSpendArgs {
  path: { campaignId: string };
  body: {
  spentOn: string;
  amount: string;
  currency: string;
  description: string;
  supplier?: (string | null);
  reference?: (string | null);
};
}

export type RecordCampaignSpendResult = S.CampaignBudget;

/** Record a line of spend - an invoice, a fee - in the campaign currency */
export function recordCampaignSpend(args: RecordCampaignSpendArgs, options?: RequestOptions): Promise<RecordCampaignSpendResult> {
  return request<RecordCampaignSpendResult>({
    operationId: "recordCampaignSpend",
    method: "POST",
    path: "/campaigns/{campaignId}/spend",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RecordCommentsArgs {
  path: { objectKey: string; recordId: string };
}

export type RecordCommentsResult = {
  comments: Array<S.RecordComment>;
};

/** The internal discussion on a record, open questions first */
export function recordComments(args: RecordCommentsArgs, options?: RequestOptions): Promise<RecordCommentsResult> {
  return request<RecordCommentsResult>({
    operationId: "recordComments",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/comments",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RecordConsentArgs {
  body: {
  subjectType: "contact" | "lead";
  subjectId: string;
  channel: "email" | "sms" | "whatsapp" | "call" | "post";
  status: "opted-in" | "opted-out" | "unknown";
  source: string;
  evidenceUrl?: (string | null);
  evidenceNote?: (string | null);
};
}

export type RecordConsentResult = {
  consentId: string;
};

/** Record what somebody agreed to */
export function recordConsent(args: RecordConsentArgs, options?: RequestOptions): Promise<RecordConsentResult> {
  return request<RecordConsentResult>({
    operationId: "recordConsent",
    method: "POST",
    path: "/consent",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RecordEventAttendanceArgs {
  path: { eventId: string };
  body: {
  entries: Array<{
    email: string;
    minutes: number;
  }>;
};
}

export type RecordEventAttendanceResult = {
  matched: number;
  unmatched: Array<string>;
};

/** Attendance as the video provider reports it: email and minutes per person */
export function recordEventAttendance(args: RecordEventAttendanceArgs, options?: RequestOptions): Promise<RecordEventAttendanceResult> {
  return request<RecordEventAttendanceResult>({
    operationId: "recordEventAttendance",
    method: "POST",
    path: "/marketing-events/{eventId}/attendance",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RecordEventsArgs {
  path: { objectKey: string; recordId: string };
}

export type RecordEventsResult = {
  events: Array<S.CalendarEvent>;
};

/** Meetings about one record */
export function recordEvents(args: RecordEventsArgs, options?: RequestOptions): Promise<RecordEventsResult> {
  return request<RecordEventsResult>({
    operationId: "recordEvents",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/events",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RecordHistoryArgs {
  path: { objectKey: string; recordId: string };
  query?: { limit?: number; before?: string };
}

export type RecordHistoryResult = {
  entries: Array<S.AuditRow>;
};

/** Who changed this record, and what they changed */
export function recordHistory(args: RecordHistoryArgs, options?: RequestOptions): Promise<RecordHistoryResult> {
  return request<RecordHistoryResult>({
    operationId: "recordHistory",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/history",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export interface RecordLoyaltyMovementArgs {
  path: { accountId: string };
  body: {
  points?: number;
  wallet?: string;
  reason: string;
};
}

export type RecordLoyaltyMovementResult = {
  points: number;
  wallet: string;
};

/** Move points or wallet, with a reason */
export function recordLoyaltyMovement(args: RecordLoyaltyMovementArgs, options?: RequestOptions): Promise<RecordLoyaltyMovementResult> {
  return request<RecordLoyaltyMovementResult>({
    operationId: "recordLoyaltyMovement",
    method: "POST",
    path: "/accounts/{accountId}/loyalty",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RecordMailResponseArgs {
  body: {
  code: string;
  note?: (string | null);
};
}

export type RecordMailResponseResult = {
  campaignId: string;
  campaignName: string;
  contactId: string;
  contactName: string;
  firstResponse: boolean;
};

/** Record a reply by the code on the card */
export function recordMailResponse(args: RecordMailResponseArgs, options?: RequestOptions): Promise<RecordMailResponseResult> {
  return request<RecordMailResponseResult>({
    operationId: "recordMailResponse",
    method: "POST",
    path: "/mail-responses",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RecordPaymentArgs {
  path: { invoiceId: string };
  body: {
  amount: string;
  currency: string;
  outcome: "succeeded" | "failed" | "pending";
  failureCode?: (string | null);
  failureMessage?: (string | null);
  attemptedAt?: (string | null);
  reference: string;
};
}

export type RecordPaymentResult = {
  attemptId: string;
  outstanding: string;
  paid: boolean;
};

/** Record what happened when payment was attempted */
export function recordPayment(args: RecordPaymentArgs, options?: RequestOptions): Promise<RecordPaymentResult> {
  return request<RecordPaymentResult>({
    operationId: "recordPayment",
    method: "POST",
    path: "/invoices/{invoiceId}/payments",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RecordQualificationArgs {
  path: { objectKey: string; recordId: string };
  query: { frameworkId: string };
}

export type RecordQualificationResult = {
  qualification: S.Qualification;
};

/** How far a record has been qualified, and what is still unexamined */
export function recordQualification(args: RecordQualificationArgs, options?: RequestOptions): Promise<RecordQualificationResult> {
  return request<RecordQualificationResult>({
    operationId: "recordQualification",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/qualification",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export interface RecordReferralArgs {
  path: { leadId: string };
  body: {
  code?: (string | null);
  referrerId?: (string | null);
};
}

export type RecordReferralResult = {
  referralId: string;
  referrerId: string;
  referrerName: string;
};

/** Say who referred this lead, by their code or by the contact */
export function recordReferral(args: RecordReferralArgs, options?: RequestOptions): Promise<RecordReferralResult> {
  return request<RecordReferralResult>({
    operationId: "recordReferral",
    method: "POST",
    path: "/leads/{leadId}/referral",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RecordSourceArgs {
  path: { objectKey: string; recordId: string };
}

export type RecordSourceResult = {
  objectKey: "lead" | "contact" | "deal";
  first: (S.MarketingTouch | null);
  last: (S.MarketingTouch | null);
  touchCount: (number | null);
  carry: ({
    carried: boolean;
    contactId: (string | null);
    contactName: (string | null);
    contactVisible: boolean;
    carriedAt: (string | null);
  } | null);
};

/** First and last touch of a lead or contact, or the source a deal was created with */
export function recordSource(args: RecordSourceArgs, options?: RequestOptions): Promise<RecordSourceResult> {
  return request<RecordSourceResult>({
    operationId: "recordSource",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/source",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RecordStockPositionsArgs {
  body: {
  positions: Array<{
    productId: string;
    location?: string;
    onHand: string;
    allocated?: string;
    asOf: string;
    source?: string;
  }>;
};
}

export type RecordStockPositionsResult = {
  recorded: number;
};

/** Record what a warehouse system reported */
export function recordStockPositions(args: RecordStockPositionsArgs, options?: RequestOptions): Promise<RecordStockPositionsResult> {
  return request<RecordStockPositionsResult>({
    operationId: "recordStockPositions",
    method: "POST",
    path: "/stock",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RecordSubscriptionEventArgs {
  path: { subscriptionId: string };
  body: {
  kind: "started" | "upgraded" | "downgraded" | "quantity-changed" | "price-changed" | "paused" | "resumed" | "cancelled" | "reactivated" | "corrected";
  effectiveOn: string;
  planKey?: (string | null);
  planLabel?: (string | null);
  quantity?: (string | null);
  unitPrice?: (string | null);
  mrr: string;
  reason?: (string | null);
  corrects?: (number | null);
};
}

export type RecordSubscriptionEventResult = {
  eventId: number;
};

/** Record a plan change */
export function recordSubscriptionEvent(args: RecordSubscriptionEventArgs, options?: RequestOptions): Promise<RecordSubscriptionEventResult> {
  return request<RecordSubscriptionEventResult>({
    operationId: "recordSubscriptionEvent",
    method: "POST",
    path: "/subscriptions/{subscriptionId}/events",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RecordUsageArgs {
  body: {
  subscriptionId: string;
  meterId: string;
  quantity: string;
  occurredAt: string;
  reference: string;
};
}

export type RecordUsageResult = {
  usageId: string;
  replaced: boolean;
};

/** Record a meter reading */
export function recordUsage(args: RecordUsageArgs, options?: RequestOptions): Promise<RecordUsageResult> {
  return request<RecordUsageResult>({
    operationId: "recordUsage",
    method: "POST",
    path: "/usage/records",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RecordViewingOutcomeArgs {
  path: { visitId: string };
  body: {
  outcome: "held" | "no-show" | "cancelled";
  note?: (string | null);
  interest?: ("hot" | "warm" | "cold" | "not-interested" | null);
};
}

export type RecordViewingOutcomeResult = {
  viewing: S.Viewing;
};

/** Say what happened at a viewing */
export function recordViewingOutcome(args: RecordViewingOutcomeArgs, options?: RequestOptions): Promise<RecordViewingOutcomeResult> {
  return request<RecordViewingOutcomeResult>({
    operationId: "recordViewingOutcome",
    method: "POST",
    path: "/visits/{visitId}/outcome",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RedeemPromotionArgs {
  path: { promotionId: string };
  body: {
  relatedType: string;
  relatedId: string;
  accountId?: (string | null);
  discount: string;
  currency?: string;
};
}

export type RedeemPromotionResult = {
  redemptionId: string;
  promotionId: string;
  name: string;
  discount: string;
  currency: string;
  relatedType: string;
  relatedId: string;
  redeemedAt: string;
  redeemedBy: (string | null);
};

/** Use a promotion once, if there is a use left */
export function redeemPromotion(args: RedeemPromotionArgs, options?: RequestOptions): Promise<RedeemPromotionResult> {
  return request<RedeemPromotionResult>({
    operationId: "redeemPromotion",
    method: "POST",
    path: "/promotions/{promotionId}/redemptions",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RedeemSignInLinkArgs {
  body: {
  token: string;
};
}

export type RedeemSignInLinkResult = S.SignInOutcome;

/** Exchange a sign-in link for a session */
export function redeemSignInLink(args: RedeemSignInLinkArgs, options?: RequestOptions): Promise<RedeemSignInLinkResult> {
  return request<RedeemSignInLinkResult>({
    operationId: "redeemSignInLink",
    method: "POST",
    path: "/auth/sign-in-link/redeem",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export type ReferralProgrammeResult = S.ReferralProgramme;

/** The referral programme and its reward */
export function referralProgramme(options?: RequestOptions): Promise<ReferralProgrammeResult> {
  return request<ReferralProgrammeResult>({
    operationId: "referralProgramme",
    method: "GET",
    path: "/referral-programme",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RefuseDataRequestArgs {
  path: { requestId: string };
  body: {
  reason: string;
};
}

export type RefuseDataRequestResult = S.DsrRequest;

/** Record that a request will not be answered, and why */
export function refuseDataRequest(args: RefuseDataRequestArgs, options?: RequestOptions): Promise<RefuseDataRequestResult> {
  return request<RefuseDataRequestResult>({
    operationId: "refuseDataRequest",
    method: "POST",
    path: "/setup/privacy/requests/{requestId}/refuse",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RegisterAssetArgs {
  path: { accountId: string };
  body: {
  productId?: (string | null);
  name?: (string | null);
  serial?: (string | null);
  location?: (string | null);
  installedOn: string;
};
}

export type RegisterAssetResult = S.CustomerAsset;

/** Register an installed asset */
export function registerAsset(args: RegisterAssetArgs, options?: RequestOptions): Promise<RegisterAssetResult> {
  return request<RegisterAssetResult>({
    operationId: "registerAsset",
    method: "POST",
    path: "/accounts/{accountId}/assets",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RegisterForEventArgs {
  path: { workspace: string; slug: string };
  body: {
  values: {
    [key: string]: string;
  };
  consent?: boolean;
  utm?: {
    [key: string]: string;
  };
  referrer?: (string | null);
  filledInSeconds?: number;
};
}

export type RegisterForEventResult = {
  accepted: boolean;
  message: string;
  messageKey: (string | null);
  field: (string | null);
  registration: ("registered" | "waitlisted" | "already-registered" | null);
  found: boolean;
};

/** A visitor registers for an event */
export function registerForEvent(args: RegisterForEventArgs, options?: RequestOptions): Promise<RegisterForEventResult> {
  return request<RegisterForEventResult>({
    operationId: "registerForEvent",
    method: "POST",
    path: "/public/events/{workspace}/{slug}/register",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RegisterPartnerDealArgs {
  body: {
  token: string;
  companyName: string;
  domain?: (string | null);
  dealName: string;
  amount: string;
  currency: string;
  closeDate: string;
};
}

export type RegisterPartnerDealResult = {
  dealId: (string | null);
  status: ("under-review" | "accepted" | "not-accepted" | null);
  protectedUntil: (string | null);
  refusal: ({
    kind: "invalid" | "expired" | "revoked" | "wrong-surface";
    canRequestNewLink: boolean;
  } | null);
};

/** Register a deal */
export function registerPartnerDeal(args: RegisterPartnerDealArgs, options?: RequestOptions): Promise<RegisterPartnerDealResult> {
  return request<RegisterPartnerDealResult>({
    operationId: "registerPartnerDeal",
    method: "POST",
    path: "/portal/partner/register",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ReindexGroundingArgs {
  body: Record<string, never>;
}

export type ReindexGroundingResult = {
  indexed: number;
  skipped: number;
  mode: "live" | "sandbox" | "not-configured";
};

/** Index what the workspace’s agents are grounded on */
export function reindexGrounding(args: ReindexGroundingArgs, options?: RequestOptions): Promise<ReindexGroundingResult> {
  return request<ReindexGroundingResult>({
    operationId: "reindexGrounding",
    method: "POST",
    path: "/agents/grounding/reindex",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RelationshipMapArgs {
  path: { accountId: string };
  query?: { dealId?: string };
}

export type RelationshipMapResult = {
  map: S.RelationshipMap;
};

/** Who reports to whom and who influences whom, optionally over a deal */
export function relationshipMap(args: RelationshipMapArgs, options?: RequestOptions): Promise<RelationshipMapResult> {
  return request<RelationshipMapResult>({
    operationId: "relationshipMap",
    method: "GET",
    path: "/accounts/{accountId}/relationships",
    pathParams: args.path,
    query: args.query,
    body: undefined,
  }, options);
}

export interface RemoveAbTestArgs {
  path: { campaignId: string };
  body: Record<string, never>;
}

export type RemoveAbTestResult = {
  removed: boolean;
};

/** Take the A/B test off a campaign that has not been sent */
export function removeAbTest(args: RemoveAbTestArgs, options?: RequestOptions): Promise<RemoveAbTestResult> {
  return request<RemoveAbTestResult>({
    operationId: "removeAbTest",
    method: "POST",
    path: "/campaigns/{campaignId}/ab-test/remove",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RemoveAccountAddressArgs {
  path: { accountId: string; addressId: string };
}

export type RemoveAccountAddressResult = {
  removed: string;
  noDefault: ("billing" | "shipping" | "site" | "branch" | null);
};

/** Remove an address */
export function removeAccountAddress(args: RemoveAccountAddressArgs, options?: RequestOptions): Promise<RemoveAccountAddressResult> {
  return request<RemoveAccountAddressResult>({
    operationId: "removeAccountAddress",
    method: "DELETE",
    path: "/accounts/{accountId}/addresses/{addressId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RemoveBillingStageArgs {
  path: { stageId: string };
}

export type RemoveBillingStageResult = {
  removed: true;
};

/** Take a stage nothing has been billed against out of the plan */
export function removeBillingStage(args: RemoveBillingStageArgs, options?: RequestOptions): Promise<RemoveBillingStageResult> {
  return request<RemoveBillingStageResult>({
    operationId: "removeBillingStage",
    method: "POST",
    path: "/billing-stages/{stageId}/delete",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RemoveCampaignSpendArgs {
  body: {
  spendId: string;
};
}

export type RemoveCampaignSpendResult = S.CampaignBudget;

/** Remove a spend line recorded in error */
export function removeCampaignSpend(args: RemoveCampaignSpendArgs, options?: RequestOptions): Promise<RemoveCampaignSpendResult> {
  return request<RemoveCampaignSpendResult>({
    operationId: "removeCampaignSpend",
    method: "POST",
    path: "/campaign-spend/delete",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RemoveCurrencyArgs {
  path: { code: string };
}

export type RemoveCurrencyResult = {
  currencies: Array<string>;
};

/** Stop offering a currency */
export function removeCurrency(args: RemoveCurrencyArgs, options?: RequestOptions): Promise<RemoveCurrencyResult> {
  return request<RemoveCurrencyResult>({
    operationId: "removeCurrency",
    method: "DELETE",
    path: "/setup/currencies/{code}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RemoveDealCompetitorArgs {
  path: { dealId: string; competitorId: string };
}

export type RemoveDealCompetitorResult = {
  ok: true;
};

/** Take a competitor off a deal */
export function removeDealCompetitor(args: RemoveDealCompetitorArgs, options?: RequestOptions): Promise<RemoveDealCompetitorResult> {
  return request<RemoveDealCompetitorResult>({
    operationId: "removeDealCompetitor",
    method: "DELETE",
    path: "/deals/{dealId}/competitors/{competitorId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RemoveDealContactArgs {
  path: { dealId: string; contactId: string };
}

export type RemoveDealContactResult = {
  ok: true;
};

/** Take a contact off a deal */
export function removeDealContact(args: RemoveDealContactArgs, options?: RequestOptions): Promise<RemoveDealContactResult> {
  return request<RemoveDealContactResult>({
    operationId: "removeDealContact",
    method: "DELETE",
    path: "/deals/{dealId}/contacts/{contactId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RemoveIpAllowlistEntryArgs {
  path: { entryId: string };
}

export type RemoveIpAllowlistEntryResult = {
  ok: boolean;
};

/** Stop allowing a network; refused if it would shut you out */
export function removeIpAllowlistEntry(args: RemoveIpAllowlistEntryArgs, options?: RequestOptions): Promise<RemoveIpAllowlistEntryResult> {
  return request<RemoveIpAllowlistEntryResult>({
    operationId: "removeIpAllowlistEntry",
    method: "DELETE",
    path: "/setup/ip-allowlist/{entryId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RemovePartnerCollateralArgs {
  path: { collateralId: string };
}

export type RemovePartnerCollateralResult = {
  removed: boolean;
};

/** Stop sharing a link with partners */
export function removePartnerCollateral(args: RemovePartnerCollateralArgs, options?: RequestOptions): Promise<RemovePartnerCollateralResult> {
  return request<RemovePartnerCollateralResult>({
    operationId: "removePartnerCollateral",
    method: "POST",
    path: "/setup/partners/collateral/{collateralId}/delete",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RemoveRelationshipArgs {
  path: { accountId: string; relationshipId: string };
}

export type RemoveRelationshipResult = {
  removed: string;
};

/** Remove a line */
export function removeRelationship(args: RemoveRelationshipArgs, options?: RequestOptions): Promise<RemoveRelationshipResult> {
  return request<RemoveRelationshipResult>({
    operationId: "removeRelationship",
    method: "DELETE",
    path: "/accounts/{accountId}/relationships/{relationshipId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RemoveTagArgs {
  path: { objectKey: string; recordId: string };
  body: {
  tagId: string;
};
}

export type RemoveTagResult = {
  tags: Array<S.Tag>;
};

/** Take a tag off a record */
export function removeTag(args: RemoveTagArgs, options?: RequestOptions): Promise<RemoveTagResult> {
  return request<RemoveTagResult>({
    operationId: "removeTag",
    method: "POST",
    path: "/records/{objectKey}/{recordId}/tags/remove",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RenameCustomObjectArgs {
  path: { objectKey: string };
  body: {
  label: string;
  plural: string;
  icon: string;
};
}

export type RenameCustomObjectResult = {
  objects: Array<{
    objectKey: string;
    label: string;
    plural: string;
    icon: string;
    records: number;
  }>;
};

/** Change what an object is called */
export function renameCustomObject(args: RenameCustomObjectArgs, options?: RequestOptions): Promise<RenameCustomObjectResult> {
  return request<RenameCustomObjectResult>({
    operationId: "renameCustomObject",
    method: "PUT",
    path: "/setup/objects/custom/{objectKey}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RenameTagArgs {
  path: { tagId: string };
  body: {
  label: string;
};
}

export type RenameTagResult = S.Tag;

/** Rename a tag everywhere it is used */
export function renameTag(args: RenameTagArgs, options?: RequestOptions): Promise<RenameTagResult> {
  return request<RenameTagResult>({
    operationId: "renameTag",
    method: "POST",
    path: "/setup/tags/{tagId}/rename",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RenewalSummaryArgs {
  query?: { withinDays?: number };
}

export type RenewalSummaryResult = {
  summary: S.RenewalSummary;
};

/** Renewals being worked, and the ones that will lapse unless somebody wins them */
export function renewalSummary(args: RenewalSummaryArgs, options?: RequestOptions): Promise<RenewalSummaryResult> {
  return request<RenewalSummaryResult>({
    operationId: "renewalSummary",
    method: "GET",
    path: "/revenue/renewals",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ReorderRoutingRulesArgs {
  body: {
  ruleIds: Array<string>;
  object?: "lead" | "support_case";
};
}

export type ReorderRoutingRulesResult = {
  rules: Array<S.LeadRoutingRule>;
};

/** Put the rules in an order */
export function reorderRoutingRules(args: ReorderRoutingRulesArgs, options?: RequestOptions): Promise<ReorderRoutingRulesResult> {
  return request<ReorderRoutingRulesResult>({
    operationId: "reorderRoutingRules",
    method: "PUT",
    path: "/setup/routing/order",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ReorderStagesArgs {
  path: { pipelineId: string };
  body: {
  stageIds: Array<string>;
};
}

export type ReorderStagesResult = S.PipelineShape;

/** Put the stages in an order */
export function reorderStages(args: ReorderStagesArgs, options?: RequestOptions): Promise<ReorderStagesResult> {
  return request<ReorderStagesResult>({
    operationId: "reorderStages",
    method: "PUT",
    path: "/setup/pipelines/{pipelineId}/order",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ReplayEventsArgs {
  path: { subscriberId: string };
  body: {
  fromSeq: number;
};
}

export type ReplayEventsResult = S.EventSubscriber;

/** Move the cursor back; everything after it is delivered again */
export function replayEvents(args: ReplayEventsArgs, options?: RequestOptions): Promise<ReplayEventsResult> {
  return request<ReplayEventsResult>({
    operationId: "replayEvents",
    method: "POST",
    path: "/developer/stream/subscribers/{subscriberId}/replay",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ReplayFlowRunArgs {
  path: { runId: string };
  body: {
  record: {
    [key: string]: S.ConfigValue;
  };
};
}

export type ReplayFlowRunResult = {
  runId: string;
  status: string;
  outcome: string;
  steps: Array<S.FlowRunStep>;
};

/** Run it again, keeping the original */
export function replayFlowRun(args: ReplayFlowRunArgs, options?: RequestOptions): Promise<ReplayFlowRunResult> {
  return request<ReplayFlowRunResult>({
    operationId: "replayFlowRun",
    method: "POST",
    path: "/flow-runs/{runId}/replay",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ReplayWebhookDeliveryArgs {
  path: { deliveryId: string };
  body: Record<string, never>;
}

export type ReplayWebhookDeliveryResult = {
  status: "pending" | "succeeded" | "failed" | "dead";
};

/** Send a delivery again, as a new delivery linked to the original */
export function replayWebhookDelivery(args: ReplayWebhookDeliveryArgs, options?: RequestOptions): Promise<ReplayWebhookDeliveryResult> {
  return request<ReplayWebhookDeliveryResult>({
    operationId: "replayWebhookDelivery",
    method: "POST",
    path: "/developer/webhook-deliveries/{deliveryId}/replay",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ReplyToCaseArgs {
  path: { caseId: string };
  body: {
  visibility: "public" | "internal";
  body: string;
};
}

export type ReplyToCaseResult = {
  messageId: string;
};

/** Reply to the customer, or add an internal note */
export function replyToCase(args: ReplyToCaseArgs, options?: RequestOptions): Promise<ReplyToCaseResult> {
  return request<ReplyToCaseResult>({
    operationId: "replyToCase",
    method: "POST",
    path: "/cases/{caseId}/messages",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RequestSignInLinkArgs {
  body: {
  email: string;
};
}

export type RequestSignInLinkResult = {
  status: "sent-if-it-exists";
  expiresInMinutes: number;
};

/** Email a single-use sign-in link */
export function requestSignInLink(args: RequestSignInLinkArgs, options?: RequestOptions): Promise<RequestSignInLinkResult> {
  return request<RequestSignInLinkResult>({
    operationId: "requestSignInLink",
    method: "POST",
    path: "/auth/sign-in-link",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RescheduleViewingArgs {
  path: { visitId: string };
  body: {
  startsAt: string;
  endsAt: string;
  note?: (string | null);
};
}

export type RescheduleViewingResult = {
  viewing: S.Viewing;
};

/** Move a viewing to another time */
export function rescheduleViewing(args: RescheduleViewingArgs, options?: RequestOptions): Promise<RescheduleViewingResult> {
  return request<RescheduleViewingResult>({
    operationId: "rescheduleViewing",
    method: "POST",
    path: "/visits/{visitId}/reschedule",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export type RescoreLeadsResult = {
  rescored: number;
};

/** Apply the current rules to every lead */
export function rescoreLeads(options?: RequestOptions): Promise<RescoreLeadsResult> {
  return request<RescoreLeadsResult>({
    operationId: "rescoreLeads",
    method: "POST",
    path: "/leads/rescore",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ResidencyOptionsArgs {
  query?: { plan?: string };
}

export type ResidencyOptionsResult = {
  regions: Array<{
    key: string;
    label: string;
    jurisdiction: string;
  }>;
  choosable: boolean;
};

/** The regions a plan may choose from at signup */
export function residencyOptions(args: ResidencyOptionsArgs, options?: RequestOptions): Promise<ResidencyOptionsResult> {
  return request<ResidencyOptionsResult>({
    operationId: "residencyOptions",
    method: "GET",
    path: "/residency/options",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ResolveRecordCommentArgs {
  path: { objectKey: string; recordId: string; commentId: string };
  body: {
  resolved: boolean;
};
}

export type ResolveRecordCommentResult = {
  comment: S.RecordComment;
};

/** Mark a question answered, or reopen it */
export function resolveRecordComment(args: ResolveRecordCommentArgs, options?: RequestOptions): Promise<ResolveRecordCommentResult> {
  return request<ResolveRecordCommentResult>({
    operationId: "resolveRecordComment",
    method: "POST",
    path: "/records/{objectKey}/{recordId}/comments/{commentId}/resolve",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RespondToEventArgs {
  path: { eventId: string };
  body: {
  response: "accepted" | "declined" | "tentative";
};
}

export type RespondToEventResult = S.CalendarEvent;

/** Answer an invitation */
export function respondToEvent(args: RespondToEventArgs, options?: RequestOptions): Promise<RespondToEventResult> {
  return request<RespondToEventResult>({
    operationId: "respondToEvent",
    method: "POST",
    path: "/events/{eventId}/respond",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RestoreRecordArgs {
  path: { objectKey: string; recordId: string };
}

export type RestoreRecordResult = {
  restored: true;
};

/** Bring a record back out of the recycle bin */
export function restoreRecord(args: RestoreRecordArgs, options?: RequestOptions): Promise<RestoreRecordResult> {
  return request<RestoreRecordResult>({
    operationId: "restoreRecord",
    method: "POST",
    path: "/records/{objectKey}/{recordId}/restore",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ResumeBulkJobArgs {
  path: { jobId: string };
}

export type ResumeBulkJobResult = S.BulkJob;

/** Pick a stalled job up where it left off */
export function resumeBulkJob(args: ResumeBulkJobArgs, options?: RequestOptions): Promise<ResumeBulkJobResult> {
  return request<ResumeBulkJobResult>({
    operationId: "resumeBulkJob",
    method: "POST",
    path: "/bulk/jobs/{jobId}/resume",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RetentionCurveArgs {
  query?: { months?: number; since?: string };
}

export type RetentionCurveResult = {
  currency: string;
  months: number;
  cohorts: Array<S.RetentionCohort>;
  incomplete: Array<string>;
};

/** Retention by joining month */
export function retentionCurve(args: RetentionCurveArgs, options?: RequestOptions): Promise<RetentionCurveResult> {
  return request<RetentionCurveResult>({
    operationId: "retentionCurve",
    method: "GET",
    path: "/revenue/retention",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface RevenueMovementArgs {
  query: { from: string; to: string };
}

export type RevenueMovementResult = {
  from: string;
  to: string;
  currency: string;
  openingMrr: string;
  closingMrr: string;
  totals: S.RevenueMovementTotals;
  movements: Array<S.RevenueMovement>;
  reconciliation: {
    balances: boolean;
    difference: string;
  };
};

/** MRR movement over a period */
export function revenueMovement(args: RevenueMovementArgs, options?: RequestOptions): Promise<RevenueMovementResult> {
  return request<RevenueMovementResult>({
    operationId: "revenueMovement",
    method: "GET",
    path: "/revenue/movement",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface RevenueSnapshotArgs {
  query?: { asAt?: string };
}

export type RevenueSnapshotResult = {
  asAt: string;
  mrr: string;
  arr: string;
  active: number;
};

/** MRR and ARR on a date */
export function revenueSnapshot(args: RevenueSnapshotArgs, options?: RequestOptions): Promise<RevenueSnapshotResult> {
  return request<RevenueSnapshotResult>({
    operationId: "revenueSnapshot",
    method: "GET",
    path: "/revenue/snapshot",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ReverseImportArgs {
  path: { runId: string };
  body: Record<string, never>;
}

export type ReverseImportResult = {
  removed: number;
  keptBecauseEdited: number;
};

/** Undo an import */
export function reverseImport(args: ReverseImportArgs, options?: RequestOptions): Promise<ReverseImportResult> {
  return request<ReverseImportResult>({
    operationId: "reverseImport",
    method: "POST",
    path: "/setup/imports/{runId}/reversal",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ReverseLeadConversionArgs {
  path: { leadId: string };
  body: Record<string, never>;
}

export type ReverseLeadConversionResult = {
  removed: Array<string>;
};

/** Undo a conversion */
export function reverseLeadConversion(args: ReverseLeadConversionArgs, options?: RequestOptions): Promise<ReverseLeadConversionResult> {
  return request<ReverseLeadConversionResult>({
    operationId: "reverseLeadConversion",
    method: "POST",
    path: "/leads/{leadId}/conversion-reversal",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ReviewPluginArgs {
  body: {
  manifest: {
    name: string;
    publisher?: string;
    version?: string;
    source: string;
    scopes?: Array<string>;
  };
};
}

export type ReviewPluginResult = {
  name: string;
  publisher: string;
  version: string;
  asks: Array<{
    scope: string;
    label: string;
    detail: string;
  }>;
  unrecognisedScopes: Array<string>;
};

/** What a manifest is asking for, before anything is installed */
export function reviewPlugin(args: ReviewPluginArgs, options?: RequestOptions): Promise<ReviewPluginResult> {
  return request<ReviewPluginResult>({
    operationId: "reviewPlugin",
    method: "POST",
    path: "/setup/plugins/review",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RevokeApiTokenArgs {
  path: { tokenId: string };
}

export type RevokeApiTokenResult = S.ApiToken;

/** Stop a token, now */
export function revokeApiToken(args: RevokeApiTokenArgs, options?: RequestOptions): Promise<RevokeApiTokenResult> {
  return request<RevokeApiTokenResult>({
    operationId: "revokeApiToken",
    method: "POST",
    path: "/setup/api-tokens/{tokenId}/revoke",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RevokeEmbedArgs {
  path: { embedId: string };
}

export type RevokeEmbedResult = S.Embed;

/** Stop a widget being rendered anywhere */
export function revokeEmbed(args: RevokeEmbedArgs, options?: RequestOptions): Promise<RevokeEmbedResult> {
  return request<RevokeEmbedResult>({
    operationId: "revokeEmbed",
    method: "POST",
    path: "/setup/embeds/{embedId}/revoke",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RevokeMyDeviceArgs {
  path: { sessionId: string };
}

export type RevokeMyDeviceResult = {
  revoked: true;
};

/** Sign out of another device */
export function revokeMyDevice(args: RevokeMyDeviceArgs, options?: RequestOptions): Promise<RevokeMyDeviceResult> {
  return request<RevokeMyDeviceResult>({
    operationId: "revokeMyDevice",
    method: "DELETE",
    path: "/me/devices/{sessionId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RevokeScimTokenArgs {
  path: { tokenId: string };
}

export type RevokeScimTokenResult = {
  ok: true;
};

/** Revoke a directory provisioning token */
export function revokeScimToken(args: RevokeScimTokenArgs, options?: RequestOptions): Promise<RevokeScimTokenResult> {
  return request<RevokeScimTokenResult>({
    operationId: "revokeScimToken",
    method: "DELETE",
    path: "/settings/sso/scim-tokens/{tokenId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RunAgentTestsArgs {
  path: { agentId: string };
}

export type RunAgentTestsResult = {
  outcomes: Array<S.AgentTestOutcome>;
};

/** Run the eval suite */
export function runAgentTests(args: RunAgentTestsArgs, options?: RequestOptions): Promise<RunAgentTestsResult> {
  return request<RunAgentTestsResult>({
    operationId: "runAgentTests",
    method: "POST",
    path: "/agents/{agentId}/tests/run",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export type RunBackupResult = S.Backup;

/** Take a backup now */
export function runBackup(options?: RequestOptions): Promise<RunBackupResult> {
  return request<RunBackupResult>({
    operationId: "runBackup",
    method: "POST",
    path: "/setup/backups",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface RunDataExportArgs {
  path: { exportId: string };
  body: Record<string, never>;
}

export type RunDataExportResult = {
  detail: string;
};

/** Write the export now */
export function runDataExport(args: RunDataExportArgs, options?: RequestOptions): Promise<RunDataExportResult> {
  return request<RunDataExportResult>({
    operationId: "runDataExport",
    method: "POST",
    path: "/data-export/{exportId}/run",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RunDunningArgs {
  body: {
  on?: string;
};
}

export type RunDunningResult = {
  sent: number;
  notified: number;
  escalated: number;
  skipped: number;
  outcomes: Array<S.DunningOutcome>;
};

/** Chase every overdue invoice by one rung */
export function runDunning(args: RunDunningArgs, options?: RequestOptions): Promise<RunDunningResult> {
  return request<RunDunningResult>({
    operationId: "runDunning",
    method: "POST",
    path: "/dunning/run",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RunInvoicingArgs {
  body: {
  on?: string;
};
}

export type RunInvoicingResult = {
  issued: number;
  drafted: number;
  skipped: number;
  outcomes: Array<S.InvoiceGeneration>;
};

/** Bill every subscription whose date has arrived */
export function runInvoicing(args: RunInvoicingArgs, options?: RequestOptions): Promise<RunInvoicingResult> {
  return request<RunInvoicingResult>({
    operationId: "runInvoicing",
    method: "POST",
    path: "/invoices/run",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface RunJobNowArgs {
  path: { scheduleId: string };
  body: Record<string, never>;
}

export type RunJobNowResult = S.JobRun;

/** Run a job now, and see what it did */
export function runJobNow(args: RunJobNowArgs, options?: RequestOptions): Promise<RunJobNowResult> {
  return request<RunJobNowResult>({
    operationId: "runJobNow",
    method: "POST",
    path: "/setup/jobs/{scheduleId}/run",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SamlCallbackArgs {
  body: {
  SAMLResponse: string;
  RelayState?: string;
};
}

export type SamlCallbackResult = {
  status: "signed-in";
  returnTo: (string | null);
  provisioned: boolean;
  expiresAt: string;
};

/** Complete a SAML sign-in and open a session */
export function samlCallback(args: SamlCallbackArgs, options?: RequestOptions): Promise<SamlCallbackResult> {
  return request<SamlCallbackResult>({
    operationId: "samlCallback",
    method: "POST",
    path: "/auth/saml/callback",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export type SamlMetadataResult = {
  entityId: string;
  callbackUrl: string;
  metadata: string;
};

/** The metadata document a provider is configured from */
export function samlMetadata(options?: RequestOptions): Promise<SamlMetadataResult> {
  return request<SamlMetadataResult>({
    operationId: "samlMetadata",
    method: "GET",
    path: "/auth/saml/metadata",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface SaveAbTestArgs {
  path: { campaignId: string };
  body: {
  kind: "subject" | "content";
  metric: "opened" | "clicked";
  testPercent: number;
  minimumSample: number;
  windowHours: number;
  variants: Array<{
    label: string;
    subject: (string | null);
    documentId: (string | null);
  }>;
};
}

export type SaveAbTestResult = S.AbTest;

/** Set up or change the A/B test of a campaign that has not been sent */
export function saveAbTest(args: SaveAbTestArgs, options?: RequestOptions): Promise<SaveAbTestResult> {
  return request<SaveAbTestResult>({
    operationId: "saveAbTest",
    method: "POST",
    path: "/campaigns/{campaignId}/ab-test",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveAccountPreferencesArgs {
  path: { accountId: string };
  body: {
  paymentTermsDays: (number | null);
  preferredLanguage: (string | null);
  preferredChannel: ("email" | "phone" | "sms" | "whatsapp" | "post" | "in-person" | null);
  documentDelivery: ("email" | "portal" | "post" | null);
  deliveryInstructions: (string | null);
};
}

export type SaveAccountPreferencesResult = {
  preferences: S.AccountPreferences;
  effective: S.EffectivePreferences;
};

/** Set a company’s language, channel, delivery and payment terms */
export function saveAccountPreferences(args: SaveAccountPreferencesArgs, options?: RequestOptions): Promise<SaveAccountPreferencesResult> {
  return request<SaveAccountPreferencesResult>({
    operationId: "saveAccountPreferences",
    method: "PUT",
    path: "/accounts/{accountId}/preferences",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveAgentArgs {
  body: {
  agentId?: (string | null);
  key: string;
  name: string;
  purpose: string;
  instructions: string;
  model?: (string | null);
};
}

export type SaveAgentResult = {
  agentId: string;
};

/** Create or change an agent */
export function saveAgent(args: SaveAgentArgs, options?: RequestOptions): Promise<SaveAgentResult> {
  return request<SaveAgentResult>({
    operationId: "saveAgent",
    method: "POST",
    path: "/agents",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveApprovalProcessArgs {
  body: {
  processId: (string | null);
  name: string;
  objectKey: "deal" | "quote" | "account" | "contact" | "lead";
  entryConditions: S.RuleDocument;
  active: boolean;
  steps: Array<{
    name: string;
    approverKind: "users" | "role" | "owner-manager";
    approverUserIds: Array<string>;
    approverRoleId: (string | null);
    mode: "any" | "all";
    timeoutHours: (number | null);
    escalateTo: (string | null);
  }>;
};
}

export type SaveApprovalProcessResult = S.ApprovalProcess;

/** Create or change an approval process */
export function saveApprovalProcess(args: SaveApprovalProcessArgs, options?: RequestOptions): Promise<SaveApprovalProcessResult> {
  return request<SaveApprovalProcessResult>({
    operationId: "saveApprovalProcess",
    method: "POST",
    path: "/approval-processes",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveBookingPageArgs {
  body: {
  pageId: (string | null);
  slug: string;
  title: string;
  description?: (string | null);
  userId: string;
  active: boolean;
};
}

export type SaveBookingPageResult = {
  pages: Array<S.BookingPage>;
};

/** Create or change a booking page */
export function saveBookingPage(args: SaveBookingPageArgs, options?: RequestOptions): Promise<SaveBookingPageResult> {
  return request<SaveBookingPageResult>({
    operationId: "saveBookingPage",
    method: "POST",
    path: "/booking-pages",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveBrandingSettingsArgs {
  body: {
  enabled?: boolean;
  accentColour?: (string | null);
  logoUrl?: (string | null);
  faviconUrl?: (string | null);
  loginBackgroundUrl?: (string | null);
  sendingDomain?: (string | null);
};
}

export type SaveBrandingSettingsResult = S.BrandingSettings;

/** Set the workspace’s colour and assets */
export function saveBrandingSettings(args: SaveBrandingSettingsArgs, options?: RequestOptions): Promise<SaveBrandingSettingsResult> {
  return request<SaveBrandingSettingsResult>({
    operationId: "saveBrandingSettings",
    method: "PUT",
    path: "/settings/branding",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveCadenceArgs {
  body: {
  cadenceId: (string | null);
  name: string;
  active: boolean;
  exitOnReply: boolean;
  enrolRule: (S.RuleDocument | null);
  steps: Array<{
    channel: "email" | "call" | "linkedin" | "task";
    delayDays: number;
    subject: (string | null);
    body: string;
  }>;
};
}

export type SaveCadenceResult = S.Cadence;

/** Create or change a cadence: steps, delays, exit on reply, and who enrols by rule */
export function saveCadence(args: SaveCadenceArgs, options?: RequestOptions): Promise<SaveCadenceResult> {
  return request<SaveCadenceResult>({
    operationId: "saveCadence",
    method: "POST",
    path: "/cadences",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveCampaignArgs {
  body: {
  campaignId?: (string | null);
  name: string;
  channel: "email" | "sms" | "whatsapp" | "event" | "ads" | "other" | "post";
  parentId?: (string | null);
  segmentId?: (string | null);
  documentId?: (string | null);
  cost?: (string | null);
  scheduledFor?: (string | null);
};
}

export type SaveCampaignResult = {
  campaignId: string;
};

/** Create or change a campaign */
export function saveCampaign(args: SaveCampaignArgs, options?: RequestOptions): Promise<SaveCampaignResult> {
  return request<SaveCampaignResult>({
    operationId: "saveCampaign",
    method: "POST",
    path: "/campaigns",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveCaptureFormArgs {
  path: { embedId: string };
  body: {
  fields: Array<S.FormField>;
  consentRequired: boolean;
  consentText?: (string | null);
  doubleOptIn?: boolean;
  successMessage?: (string | null);
  honeypotField?: (string | null);
  minFillSeconds?: number;
};
}

export type SaveCaptureFormResult = S.CaptureForm;

/** What a capture form collects */
export function saveCaptureForm(args: SaveCaptureFormArgs, options?: RequestOptions): Promise<SaveCaptureFormResult> {
  return request<SaveCaptureFormResult>({
    operationId: "saveCaptureForm",
    method: "PUT",
    path: "/embeds/{embedId}/form",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveCloseReasonArgs {
  body: {
  reasonId?: string;
  label?: string;
  outcome?: "won" | "lost";
  sortOrder?: number;
  active?: boolean;
};
}

export type SaveCloseReasonResult = S.CloseReason;

/** Add a close reason, rename one, or retire it */
export function saveCloseReason(args: SaveCloseReasonArgs, options?: RequestOptions): Promise<SaveCloseReasonResult> {
  return request<SaveCloseReasonResult>({
    operationId: "saveCloseReason",
    method: "POST",
    path: "/deals/close-reasons",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveCommissionPlanArgs {
  body: {
  planId: (string | null);
  name: string;
  currency: string;
  basePercent: number;
  tiers: Array<S.CommissionTier>;
};
}

export type SaveCommissionPlanResult = {
  plans: Array<S.CommissionPlan>;
};

/** Create or change a commission plan */
export function saveCommissionPlan(args: SaveCommissionPlanArgs, options?: RequestOptions): Promise<SaveCommissionPlanResult> {
  return request<SaveCommissionPlanResult>({
    operationId: "saveCommissionPlan",
    method: "POST",
    path: "/commission/plans",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveCompetitorArgs {
  body: {
  competitorId?: string;
  name?: string;
  notes?: (string | null);
  active?: boolean;
};
}

export type SaveCompetitorResult = S.Competitor;

/** Name a competitor, or retire one */
export function saveCompetitor(args: SaveCompetitorArgs, options?: RequestOptions): Promise<SaveCompetitorResult> {
  return request<SaveCompetitorResult>({
    operationId: "saveCompetitor",
    method: "POST",
    path: "/competitors",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveComplaintPolicyArgs {
  body: {
  acknowledgeDays: number;
  respondDays: number;
};
}

export type SaveComplaintPolicyResult = {
  acknowledgeDays: number;
  respondDays: number;
};

/** The acknowledgement and final-response deadlines for new complaints */
export function saveComplaintPolicy(args: SaveComplaintPolicyArgs, options?: RequestOptions): Promise<SaveComplaintPolicyResult> {
  return request<SaveComplaintPolicyResult>({
    operationId: "saveComplaintPolicy",
    method: "POST",
    path: "/complaints/policy",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveContactPreferencesArgs {
  path: { contactId: string };
  body: {
  preferredLanguage: (string | null);
  preferredChannel: ("email" | "phone" | "sms" | "whatsapp" | "post" | "in-person" | null);
};
}

export type SaveContactPreferencesResult = {
  effective: S.EffectivePreferences;
};

/** Set how a person likes to be reached and in which language */
export function saveContactPreferences(args: SaveContactPreferencesArgs, options?: RequestOptions): Promise<SaveContactPreferencesResult> {
  return request<SaveContactPreferencesResult>({
    operationId: "saveContactPreferences",
    method: "PUT",
    path: "/contacts/{contactId}/preferences",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveContentArgs {
  body: {
  documentId?: (string | null);
  kind: "email" | "landing-page" | "article";
  name: string;
  subject?: (string | null);
  preheader?: (string | null);
  blocks: Array<S.ContentBlock>;
};
}

export type SaveContentResult = {
  documentId: string;
  problems: Array<S.BlockProblem>;
};

/** Create or change a piece of content */
export function saveContent(args: SaveContentArgs, options?: RequestOptions): Promise<SaveContentResult> {
  return request<SaveContentResult>({
    operationId: "saveContent",
    method: "POST",
    path: "/content",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveDataExportArgs {
  body: {
  name: string;
  objects: Array<string>;
  cron: (string | null);
};
}

export type SaveDataExportResult = S.DataExport;

/** A new export: which objects, and optionally a schedule */
export function saveDataExport(args: SaveDataExportArgs, options?: RequestOptions): Promise<SaveDataExportResult> {
  return request<SaveDataExportResult>({
    operationId: "saveDataExport",
    method: "POST",
    path: "/data-export",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveDealTemplateArgs {
  body: {
  templateId: (string | null);
  name: string;
  description: (string | null);
  pipelineId: (string | null);
  amount: (string | null);
  currency: (string | null);
  typicalDays: (number | null);
  contactRoles: Array<{
    role: string;
    note?: string;
  }>;
  team: Array<{
    userId: string;
    role: string;
    splitPercent?: number;
  }>;
  custom: {
    [key: string]: (string | number | boolean | null);
  };
  active: boolean;
};
}

export type SaveDealTemplateResult = {
  template: S.DealTemplate;
};

/** Create or change a deal template */
export function saveDealTemplate(args: SaveDealTemplateArgs, options?: RequestOptions): Promise<SaveDealTemplateResult> {
  return request<SaveDealTemplateResult>({
    operationId: "saveDealTemplate",
    method: "POST",
    path: "/deal-templates",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveDocumentTemplateArgs {
  body: {
  templateId?: (string | null);
  name: string;
  objectKey: string;
  format: string;
  blocks: Array<S.ContentBlock>;
};
}

export type SaveDocumentTemplateResult = S.DocumentTemplate;

/** Create or change a document template */
export function saveDocumentTemplate(args: SaveDocumentTemplateArgs, options?: RequestOptions): Promise<SaveDocumentTemplateResult> {
  return request<SaveDocumentTemplateResult>({
    operationId: "saveDocumentTemplate",
    method: "POST",
    path: "/setup/document-templates",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveDunningStepArgs {
  body: {
  stepId: (string | null);
  afterDays: number;
  action: "email-customer" | "notify-owner" | "escalate";
  subject: (string | null);
  body: (string | null);
  active: boolean;
};
}

export type SaveDunningStepResult = {
  step: S.DunningStep;
};

/** Create or change a rung of the ladder */
export function saveDunningStep(args: SaveDunningStepArgs, options?: RequestOptions): Promise<SaveDunningStepResult> {
  return request<SaveDunningStepResult>({
    operationId: "saveDunningStep",
    method: "POST",
    path: "/dunning/steps",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveEscalationRuleArgs {
  body: {
  ruleId?: string;
  name: string;
  object: "task" | "deal" | "lead" | "case";
  afterHours: number;
  notify: "owner-manager" | "named";
  targetUserId?: (string | null);
  active?: boolean;
};
}

export type SaveEscalationRuleResult = S.EscalationRule;

/** Add an escalation rule, or change one */
export function saveEscalationRule(args: SaveEscalationRuleArgs, options?: RequestOptions): Promise<SaveEscalationRuleResult> {
  return request<SaveEscalationRuleResult>({
    operationId: "saveEscalationRule",
    method: "PUT",
    path: "/setup/escalations",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveFlowArgs {
  path: { flowId: string };
  body: {
  name?: string;
  graph: S.FlowGraph;
  status?: "draft" | "active" | "paused" | "archived";
};
}

export type SaveFlowResult = {
  flowId: string;
  version: number;
  status: string;
  problems: Array<S.FlowProblem>;
};

/** Save a graph */
export function saveFlow(args: SaveFlowArgs, options?: RequestOptions): Promise<SaveFlowResult> {
  return request<SaveFlowResult>({
    operationId: "saveFlow",
    method: "PUT",
    path: "/flows/{flowId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveFrequencyCapArgs {
  body: {
  active: boolean;
  maxMessages: number;
  perDays: number;
};
}

export type SaveFrequencyCapResult = {
  active: boolean;
  maxMessages: (number | null);
  perDays: (number | null);
};

/** Set the limit on marketing messages per person per period */
export function saveFrequencyCap(args: SaveFrequencyCapArgs, options?: RequestOptions): Promise<SaveFrequencyCapResult> {
  return request<SaveFrequencyCapResult>({
    operationId: "saveFrequencyCap",
    method: "POST",
    path: "/marketing/frequency-cap",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveLandingPageArgs {
  body: {
  pageId: (string | null);
  name: string;
  slug: string;
  title: string;
  description: (string | null);
  formEmbedId: (string | null);
  blocks: Array<S.ContentBlock>;
};
}

export type SaveLandingPageResult = S.LandingPage;

/** Create or change a landing page (the draft; what is live does not change) */
export function saveLandingPage(args: SaveLandingPageArgs, options?: RequestOptions): Promise<SaveLandingPageResult> {
  return request<SaveLandingPageResult>({
    operationId: "saveLandingPage",
    method: "POST",
    path: "/landing-pages",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveLeadScoreRuleArgs {
  body: {
  ruleId?: string;
  label?: string;
  conditions?: S.RuleDocument;
  points?: number;
  active?: boolean;
  sortOrder?: number;
};
}

export type SaveLeadScoreRuleResult = S.LeadScoreRule;

/** Add a scoring rule, change one, or retire it */
export function saveLeadScoreRule(args: SaveLeadScoreRuleArgs, options?: RequestOptions): Promise<SaveLeadScoreRuleResult> {
  return request<SaveLeadScoreRuleResult>({
    operationId: "saveLeadScoreRule",
    method: "POST",
    path: "/leads/score-rules",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveMarketingEventArgs {
  body: {
  eventId: (string | null);
  name: string;
  kind: "webinar" | "in-person";
  slug: string;
  description: (string | null);
  startsAt: string;
  endsAt: string;
  location: (string | null);
  capacity: (number | null);
  formEmbedId: (string | null);
  reminderHours: Array<number>;
  attendedFlowId: (string | null);
  noShowFlowId: (string | null);
  open: boolean;
};
}

export type SaveMarketingEventResult = S.MarketingEvent;

/** Create or change an event */
export function saveMarketingEvent(args: SaveMarketingEventArgs, options?: RequestOptions): Promise<SaveMarketingEventResult> {
  return request<SaveMarketingEventResult>({
    operationId: "saveMarketingEvent",
    method: "POST",
    path: "/marketing-events",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveModelProviderArgs {
  body: {
  providerId: (string | null);
  label: string;
  kind: "cerebras" | "openai" | "anthropic" | "custom";
  baseUrl: (string | null);
  apiKey: (string | null);
  enabled: boolean;
};
}

export type SaveModelProviderResult = {
  provider: S.ModelProvider;
};

/** Add or change a provider account this workspace pays for */
export function saveModelProvider(args: SaveModelProviderArgs, options?: RequestOptions): Promise<SaveModelProviderResult> {
  return request<SaveModelProviderResult>({
    operationId: "saveModelProvider",
    method: "POST",
    path: "/setup/model-routing/providers",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveMqlSettingsArgs {
  body: {
  threshold: (number | null);
  slaHours: number;
  newReason?: (string | null);
  retireReasonIds?: Array<string>;
};
}

export type SaveMqlSettingsResult = S.MqlSettings;

/** Change the threshold, the answer time, or the rejection reasons */
export function saveMqlSettings(args: SaveMqlSettingsArgs, options?: RequestOptions): Promise<SaveMqlSettingsResult> {
  return request<SaveMqlSettingsResult>({
    operationId: "saveMqlSettings",
    method: "POST",
    path: "/setup/mql",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveNurtureArgs {
  body: {
  flowId: (string | null);
  enrolAfterDays: (number | null);
  enrolOnRejection: boolean;
  rejectionReasonIds: Array<string>;
};
}

export type SaveNurtureResult = {
  settings: S.NurtureSettings;
};

/** Choose the nurture journey and what puts a lead into it */
export function saveNurture(args: SaveNurtureArgs, options?: RequestOptions): Promise<SaveNurtureResult> {
  return request<SaveNurtureResult>({
    operationId: "saveNurture",
    method: "PUT",
    path: "/nurture",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveObjectLayoutArgs {
  path: { objectKey: string };
  body: {
  layout: S.LayoutSpec;
};
}

export type SaveObjectLayoutResult = {
  fieldCount: number;
  budget: number;
  overBudget: number;
};

/** Save a record page layout */
export function saveObjectLayout(args: SaveObjectLayoutArgs, options?: RequestOptions): Promise<SaveObjectLayoutResult> {
  return request<SaveObjectLayoutResult>({
    operationId: "saveObjectLayout",
    method: "PUT",
    path: "/setup/objects/{objectKey}/layout",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveOnboardingTemplateArgs {
  body: {
  steps: Array<S.OnboardingTemplateStep>;
};
}

export type SaveOnboardingTemplateResult = {
  steps: Array<S.OnboardingTemplateStep>;
};

/** Change the onboarding steps */
export function saveOnboardingTemplate(args: SaveOnboardingTemplateArgs, options?: RequestOptions): Promise<SaveOnboardingTemplateResult> {
  return request<SaveOnboardingTemplateResult>({
    operationId: "saveOnboardingTemplate",
    method: "PUT",
    path: "/onboarding/template",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SavePartnerCollateralArgs {
  body: {
  title: string;
  url: string;
};
}

export type SavePartnerCollateralResult = S.PartnerCollateral;

/** Share a link with every partner */
export function savePartnerCollateral(args: SavePartnerCollateralArgs, options?: RequestOptions): Promise<SavePartnerCollateralResult> {
  return request<SavePartnerCollateralResult>({
    operationId: "savePartnerCollateral",
    method: "POST",
    path: "/setup/partners/collateral",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SavePipelineArgs {
  body: {
  pipelineId?: string;
  label: string;
  isDefault?: boolean;
};
}

export type SavePipelineResult = S.PipelineShape;

/** Create a pipeline, or rename one */
export function savePipeline(args: SavePipelineArgs, options?: RequestOptions): Promise<SavePipelineResult> {
  return request<SavePipelineResult>({
    operationId: "savePipeline",
    method: "PUT",
    path: "/setup/pipelines",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SavePlanArgs {
  body: {
  planId: (string | null);
  key: string;
  name: string;
  description: (string | null);
  billingPeriod: "monthly" | "quarterly" | "annual";
  active: boolean;
};
}

export type SavePlanResult = {
  plan: S.Plan;
};

/** Create or change a plan */
export function savePlan(args: SavePlanArgs, options?: RequestOptions): Promise<SavePlanResult> {
  return request<SavePlanResult>({
    operationId: "savePlan",
    method: "POST",
    path: "/plans",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveProductAttributesArgs {
  path: { productId: string };
  body: {
  attributes: Array<{
    name: string;
    values: Array<string>;
  }>;
};
}

export type SaveProductAttributesResult = {
  attributes: Array<S.SavedAttribute>;
};

/** Replace the attributes a product varies by */
export function saveProductAttributes(args: SaveProductAttributesArgs, options?: RequestOptions): Promise<SaveProductAttributesResult> {
  return request<SaveProductAttributesResult>({
    operationId: "saveProductAttributes",
    method: "PUT",
    path: "/products/{productId}/attributes",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveQualificationFrameworkArgs {
  body: {
  frameworkId: (string | null);
  name: string;
  objectKey: "lead" | "deal";
  criteria: Array<S.QualificationCriterion>;
  active: boolean;
};
}

export type SaveQualificationFrameworkResult = {
  framework: S.QualificationFramework;
};

/** Create or change a framework */
export function saveQualificationFramework(args: SaveQualificationFrameworkArgs, options?: RequestOptions): Promise<SaveQualificationFrameworkResult> {
  return request<SaveQualificationFrameworkResult>({
    operationId: "saveQualificationFramework",
    method: "POST",
    path: "/qualification/frameworks",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveQuoteLinesArgs {
  path: { quoteId: string };
  body: {
  lines: Array<{
    lineId?: (string | null);
    productId?: (string | null);
    description: string;
    quantity: string;
    unitPrice: string;
    discountPercent?: (string | null);
    optional?: boolean;
    unitId?: (string | null);
    sortOrder: number;
  }>;
};
}

export type SaveQuoteLinesResult = S.Quote;

/** Replace a quote-s lines with exactly these */
export function saveQuoteLines(args: SaveQuoteLinesArgs, options?: RequestOptions): Promise<SaveQuoteLinesResult> {
  return request<SaveQuoteLinesResult>({
    operationId: "saveQuoteLines",
    method: "PUT",
    path: "/quotes/{quoteId}/lines",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveQuoteTemplateArgs {
  body: {
  templateId: (string | null);
  expectedVersion: (number | null);
  name: string;
  currency: string;
  validityDays: number;
  terms: (string | null);
  documentTemplateId: (string | null);
  lines: Array<{
    productId: (string | null);
    description: string;
    quantity: string;
    unitPrice: string;
    discountPercent: (string | null);
    optional: boolean;
  }>;
};
}

export type SaveQuoteTemplateResult = S.QuoteTemplate;

/** Create a quotation template, or save its next version */
export function saveQuoteTemplate(args: SaveQuoteTemplateArgs, options?: RequestOptions): Promise<SaveQuoteTemplateResult> {
  return request<SaveQuoteTemplateResult>({
    operationId: "saveQuoteTemplate",
    method: "POST",
    path: "/quote-templates",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveRecordFieldsArgs {
  path: { objectKey: string; recordId: string };
  body: {
  changes: {
    [key: string]: S.FieldChange;
  };
};
}

export type SaveRecordFieldsResult = {
  saved: Array<string>;
  conflicts: Array<S.FieldConflict>;
  record: {
    [key: string]: S.FieldValue;
  };
  warnings: Array<S.ValidationFinding>;
};

/** Save changed fields, one at a time */
export function saveRecordFields(args: SaveRecordFieldsArgs, options?: RequestOptions): Promise<SaveRecordFieldsResult> {
  return request<SaveRecordFieldsResult>({
    operationId: "saveRecordFields",
    method: "PATCH",
    path: "/records/{objectKey}/{recordId}/fields",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveReferralProgrammeArgs {
  body: {
  active: boolean;
  rewardKind: "fixed" | "percent";
  rewardAmount: string;
  currency: string;
};
}

export type SaveReferralProgrammeResult = S.ReferralProgramme;

/** Change the referral programme */
export function saveReferralProgramme(args: SaveReferralProgrammeArgs, options?: RequestOptions): Promise<SaveReferralProgrammeResult> {
  return request<SaveReferralProgrammeResult>({
    operationId: "saveReferralProgramme",
    method: "POST",
    path: "/referral-programme",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveReportArgs {
  body: {
  reportId?: (string | null);
  name: string;
  description?: (string | null);
  spec: S.ReportSpec;
  visibility?: "private" | "workspace";
};
}

export type SaveReportResult = {
  reportId: string;
};

/** Create or update a report */
export function saveReport(args: SaveReportArgs, options?: RequestOptions): Promise<SaveReportResult> {
  return request<SaveReportResult>({
    operationId: "saveReport",
    method: "POST",
    path: "/reports",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveReportSubscriptionArgs {
  path: { reportId: string };
  body: {
  subscriptionId: (string | null);
  cron: string;
  recipients: Array<string>;
  includeRows: boolean;
  active: boolean;
};
}

export type SaveReportSubscriptionResult = S.ReportSubscription;

/** Send a report on a schedule to anyone, colleague or not; it runs as you */
export function saveReportSubscription(args: SaveReportSubscriptionArgs, options?: RequestOptions): Promise<SaveReportSubscriptionResult> {
  return request<SaveReportSubscriptionResult>({
    operationId: "saveReportSubscription",
    method: "POST",
    path: "/reports/{reportId}/subscriptions",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveRoutingRuleArgs {
  body: {
  ruleId?: string;
  object?: "lead" | "support_case";
  name: string;
  conditions: S.RuleDocument;
  strategy: "round-robin" | "load-balanced" | "named";
  targetUserId?: (string | null);
  targetTeamId?: (string | null);
  active?: boolean;
  responseMinutes?: (number | null);
};
}

export type SaveRoutingRuleResult = S.LeadRoutingRule;

/** Add a routing rule, or change one */
export function saveRoutingRule(args: SaveRoutingRuleArgs, options?: RequestOptions): Promise<SaveRoutingRuleResult> {
  return request<SaveRoutingRuleResult>({
    operationId: "saveRoutingRule",
    method: "PUT",
    path: "/setup/routing",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveSmsTemplateArgs {
  body: {
  templateId: (string | null);
  name: string;
  body: string;
  dltTemplateId: (string | null);
  dltStatus: "not-needed" | "pending" | "approved" | "rejected";
};
}

export type SaveSmsTemplateResult = S.SmsTemplate;

/** Create or change an SMS template, with its DLT registration */
export function saveSmsTemplate(args: SaveSmsTemplateArgs, options?: RequestOptions): Promise<SaveSmsTemplateResult> {
  return request<SaveSmsTemplateResult>({
    operationId: "saveSmsTemplate",
    method: "POST",
    path: "/marketing/sms/templates",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveSsoSettingsArgs {
  body: {
  protocol: "oidc" | "saml";
  issuer?: string;
  clientId?: string;
  clientSecret?: string;
  authorizeUrl?: string;
  tokenUrl?: string;
  jwksUrl?: string;
  entityId?: string;
  ssoUrl?: string;
  certificate?: string;
  domains: Array<string>;
  jitProvisioning?: boolean;
  jitRoleId?: (string | null);
};
}

export type SaveSsoSettingsResult = S.SsoConnection;

/** Save the workspace’s identity provider settings */
export function saveSsoSettings(args: SaveSsoSettingsArgs, options?: RequestOptions): Promise<SaveSsoSettingsResult> {
  return request<SaveSsoSettingsResult>({
    operationId: "saveSsoSettings",
    method: "PUT",
    path: "/settings/sso",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveStageArgs {
  path: { pipelineId: string };
  body: {
  stageId?: string;
  label: string;
  probability: number;
  outcome: "open" | "won" | "lost";
};
}

export type SaveStageResult = S.PipelineShape;

/** Add a stage, or change its name, probability or meaning */
export function saveStage(args: SaveStageArgs, options?: RequestOptions): Promise<SaveStageResult> {
  return request<SaveStageResult>({
    operationId: "saveStage",
    method: "PUT",
    path: "/setup/pipelines/{pipelineId}/stages",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveStageGuidanceArgs {
  path: { stageId: string };
  body: {
  guidance?: (string | null);
  exitRequires?: Array<string>;
};
}

export type SaveStageGuidanceResult = {
  ok: true;
};

/** What a stage means, and what it takes to leave it */
export function saveStageGuidance(args: SaveStageGuidanceArgs, options?: RequestOptions): Promise<SaveStageGuidanceResult> {
  return request<SaveStageGuidanceResult>({
    operationId: "saveStageGuidance",
    method: "PUT",
    path: "/pipelines/stages/{stageId}/guidance",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveStagePlaybookArgs {
  path: { stageId: string };
  body: {
  questions: Array<string>;
  objections: Array<{
    objection: string;
    response: string;
  }>;
  guidance?: (string | null);
  exitRequires?: Array<string>;
};
}

export type SaveStagePlaybookResult = S.StagePlaybook;

/** Change the questions and the objection handling */
export function saveStagePlaybook(args: SaveStagePlaybookArgs, options?: RequestOptions): Promise<SaveStagePlaybookResult> {
  return request<SaveStagePlaybookResult>({
    operationId: "saveStagePlaybook",
    method: "PUT",
    path: "/pipelines/stages/{stageId}/playbook",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveSuccessObjectiveArgs {
  path: { accountId: string };
  body: {
  objectiveId?: (string | null);
  title: string;
  measure: string;
  dueOn?: (string | null);
  status?: "on-track" | "at-risk" | "achieved" | "dropped";
};
}

export type SaveSuccessObjectiveResult = S.SuccessPlan;

/** Add or change an objective */
export function saveSuccessObjective(args: SaveSuccessObjectiveArgs, options?: RequestOptions): Promise<SaveSuccessObjectiveResult> {
  return request<SaveSuccessObjectiveResult>({
    operationId: "saveSuccessObjective",
    method: "POST",
    path: "/accounts/{accountId}/success-plan/objectives",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveSurveySettingArgs {
  body: {
  kind: "csat" | "nps" | "ces";
  active: boolean;
};
}

export type SaveSurveySettingResult = {
  kind: "csat" | "nps" | "ces";
  active: boolean;
};

/** Choose the satisfaction measure, or switch surveys off */
export function saveSurveySetting(args: SaveSurveySettingArgs, options?: RequestOptions): Promise<SaveSurveySettingResult> {
  return request<SaveSurveySettingResult>({
    operationId: "saveSurveySetting",
    method: "POST",
    path: "/service/survey",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveTaxRateArgs {
  body: {
  rateId: (string | null);
  name: string;
  jurisdiction: string;
  kind: "standard" | "reduced" | "zero-rated" | "exempt";
  percent: string;
  effectiveFrom: string;
  effectiveTo: (string | null);
  active: boolean;
};
}

export type SaveTaxRateResult = {
  rate: S.TaxRate;
};

/** Add a rate, or change one */
export function saveTaxRate(args: SaveTaxRateArgs, options?: RequestOptions): Promise<SaveTaxRateResult> {
  return request<SaveTaxRateResult>({
    operationId: "saveTaxRate",
    method: "POST",
    path: "/tax-rates",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveTerritoryArgs {
  body: {
  territoryId: (string | null);
  name: string;
  criteria: S.RuleDocument;
  ownerId: (string | null);
  teamId: (string | null);
  priority: number;
  active: boolean;
  namedAccountIds: Array<string>;
};
}

export type SaveTerritoryResult = S.Territory;

/** Create or change a territory */
export function saveTerritory(args: SaveTerritoryArgs, options?: RequestOptions): Promise<SaveTerritoryResult> {
  return request<SaveTerritoryResult>({
    operationId: "saveTerritory",
    method: "POST",
    path: "/setup/territories",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveUsageMeterArgs {
  body: {
  meterId: (string | null);
  key: string;
  name: string;
  unit: string;
  aggregation: "sum" | "max" | "last";
  active: boolean;
};
}

export type SaveUsageMeterResult = {
  meter: S.UsageMeter;
};

/** Create or change a meter */
export function saveUsageMeter(args: SaveUsageMeterArgs, options?: RequestOptions): Promise<SaveUsageMeterResult> {
  return request<SaveUsageMeterResult>({
    operationId: "saveUsageMeter",
    method: "POST",
    path: "/usage/meters",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveValidationRuleArgs {
  path: { objectKey: string };
  body: {
  ruleId?: (string | null);
  name: string;
  fieldKey: string;
  conditions: S.RuleDocument;
  severity: "warn" | "block";
  message: string;
  scope: "everyone" | "roles";
  roleIds: Array<string>;
  active?: boolean;
};
}

export type SaveValidationRuleResult = S.ValidationRule;

/** Add a validation rule, or change one */
export function saveValidationRule(args: SaveValidationRuleArgs, options?: RequestOptions): Promise<SaveValidationRuleResult> {
  return request<SaveValidationRuleResult>({
    operationId: "saveValidationRule",
    method: "POST",
    path: "/setup/objects/{objectKey}/validation-rules",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveVisitFormArgs {
  body: {
  formId: (string | null);
  name: string;
  questions: Array<S.VisitQuestion>;
  active: boolean;
};
}

export type SaveVisitFormResult = {
  form: S.VisitForm;
};

/** Create or change a visit checklist */
export function saveVisitForm(args: SaveVisitFormArgs, options?: RequestOptions): Promise<SaveVisitFormResult> {
  return request<SaveVisitFormResult>({
    operationId: "saveVisitForm",
    method: "POST",
    path: "/visit-forms",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SaveWebhookArgs {
  body: {
  endpointId: (string | null);
  name: string;
  url: string;
  events: Array<string>;
  active: boolean;
  rotateSecret?: boolean;
};
}

export type SaveWebhookResult = {
  endpoint: S.WebhookEndpoint;
  secret: (string | null);
};

/** Create or change a webhook endpoint; the signing secret is returned once, on create or rotate */
export function saveWebhook(args: SaveWebhookArgs, options?: RequestOptions): Promise<SaveWebhookResult> {
  return request<SaveWebhookResult>({
    operationId: "saveWebhook",
    method: "POST",
    path: "/developer/webhooks",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ScheduleJobArgs {
  path: { caseId: string };
  body: {
  technicianId?: (string | null);
  startsAt: string;
  endsAt: string;
  location?: (string | null);
};
}

export type ScheduleJobResult = S.FieldJob;

/** Book an on-site visit for this case */
export function scheduleJob(args: ScheduleJobArgs, options?: RequestOptions): Promise<ScheduleJobResult> {
  return request<ScheduleJobResult>({
    operationId: "scheduleJob",
    method: "POST",
    path: "/cases/{caseId}/jobs",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ScheduleNextStepArgs {
  body: {
  relatedType: string;
  relatedId: string;
  typeId: string;
  dueDate: string;
  note?: (string | null);
  ownerId?: (string | null);
};
}

export type ScheduleNextStepResult = {
  id: string;
};

/** Schedule the next step on a record */
export function scheduleNextStep(args: ScheduleNextStepArgs, options?: RequestOptions): Promise<ScheduleNextStepResult> {
  return request<ScheduleNextStepResult>({
    operationId: "scheduleNextStep",
    method: "POST",
    path: "/work",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ScimCreateGroupArgs {
  body: S.ScimGroupInput;
}

export type ScimCreateGroupResult = S.ScimGroup;

/** Create a team from a directory group (SCIM 2.0) */
export function scimCreateGroup(args: ScimCreateGroupArgs, options?: RequestOptions): Promise<ScimCreateGroupResult> {
  return request<ScimCreateGroupResult>({
    operationId: "scimCreateGroup",
    method: "POST",
    path: "/scim/v2/Groups",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ScimCreateUserArgs {
  body: {
  schemas?: Array<string>;
  userName: string;
  externalId?: (string | null);
  displayName?: string;
  name?: {
    givenName?: string;
    familyName?: string;
    formatted?: string;
  };
  active?: boolean;
  emails?: Array<{
    value: string;
    primary?: boolean;
  }>;
};
}

export type ScimCreateUserResult = S.ScimUser;

/** Create a person from the directory (SCIM 2.0) */
export function scimCreateUser(args: ScimCreateUserArgs, options?: RequestOptions): Promise<ScimCreateUserResult> {
  return request<ScimCreateUserResult>({
    operationId: "scimCreateUser",
    method: "POST",
    path: "/scim/v2/Users",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ScimDeleteGroupArgs {
  path: { groupId: string };
}

export type ScimDeleteGroupResult = {
  ok: true;
};

/** Delete a team the directory created (SCIM 2.0) */
export function scimDeleteGroup(args: ScimDeleteGroupArgs, options?: RequestOptions): Promise<ScimDeleteGroupResult> {
  return request<ScimDeleteGroupResult>({
    operationId: "scimDeleteGroup",
    method: "DELETE",
    path: "/scim/v2/Groups/{groupId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ScimDeleteUserArgs {
  path: { userId: string };
}

export type ScimDeleteUserResult = {
  ok: true;
};

/** Remove a person from the directory (SCIM 2.0) */
export function scimDeleteUser(args: ScimDeleteUserArgs, options?: RequestOptions): Promise<ScimDeleteUserResult> {
  return request<ScimDeleteUserResult>({
    operationId: "scimDeleteUser",
    method: "DELETE",
    path: "/scim/v2/Users/{userId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ScimGetGroupArgs {
  path: { groupId: string };
}

export type ScimGetGroupResult = S.ScimGroup;

/** Read one team and who is in it (SCIM 2.0) */
export function scimGetGroup(args: ScimGetGroupArgs, options?: RequestOptions): Promise<ScimGetGroupResult> {
  return request<ScimGetGroupResult>({
    operationId: "scimGetGroup",
    method: "GET",
    path: "/scim/v2/Groups/{groupId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ScimGetUserArgs {
  path: { userId: string };
}

export type ScimGetUserResult = S.ScimUser;

/** Read one person (SCIM 2.0) */
export function scimGetUser(args: ScimGetUserArgs, options?: RequestOptions): Promise<ScimGetUserResult> {
  return request<ScimGetUserResult>({
    operationId: "scimGetUser",
    method: "GET",
    path: "/scim/v2/Users/{userId}",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface ScimListGroupsArgs {
  query?: { filter?: string; startIndex?: number; count?: number };
}

export type ScimListGroupsResult = S.ScimGroupListResponse;

/** List or search the workspace’s teams (SCIM 2.0) */
export function scimListGroups(args: ScimListGroupsArgs, options?: RequestOptions): Promise<ScimListGroupsResult> {
  return request<ScimListGroupsResult>({
    operationId: "scimListGroups",
    method: "GET",
    path: "/scim/v2/Groups",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ScimListUsersArgs {
  query?: { filter?: string; startIndex?: number; count?: number };
}

export type ScimListUsersResult = S.ScimListResponse;

/** List or search the workspace’s people (SCIM 2.0) */
export function scimListUsers(args: ScimListUsersArgs, options?: RequestOptions): Promise<ScimListUsersResult> {
  return request<ScimListUsersResult>({
    operationId: "scimListUsers",
    method: "GET",
    path: "/scim/v2/Users",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface ScimPatchGroupArgs {
  path: { groupId: string };
  body: {
  schemas?: Array<string>;
  Operations: Array<{
    op: string;
    path?: string;
    value?: (string | {
      value?: string;
      displayName?: string;
    } | Array<{
      value: string;
    }>);
  }>;
};
}

export type ScimPatchGroupResult = S.ScimGroup;

/** Add or remove members (SCIM 2.0) */
export function scimPatchGroup(args: ScimPatchGroupArgs, options?: RequestOptions): Promise<ScimPatchGroupResult> {
  return request<ScimPatchGroupResult>({
    operationId: "scimPatchGroup",
    method: "PATCH",
    path: "/scim/v2/Groups/{groupId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ScimPatchUserArgs {
  path: { userId: string };
  body: {
  schemas?: Array<string>;
  Operations: Array<{
    op: string;
    path?: string;
    value?: (boolean | string | {
      [key: string]: (boolean | string);
    });
  }>;
};
}

export type ScimPatchUserResult = S.ScimUser;

/** Change one attribute, usually to deprovision (SCIM 2.0) */
export function scimPatchUser(args: ScimPatchUserArgs, options?: RequestOptions): Promise<ScimPatchUserResult> {
  return request<ScimPatchUserResult>({
    operationId: "scimPatchUser",
    method: "PATCH",
    path: "/scim/v2/Users/{userId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ScimReplaceGroupArgs {
  path: { groupId: string };
  body: S.ScimGroupInput;
}

export type ScimReplaceGroupResult = S.ScimGroup;

/** Replace a team’s name and membership (SCIM 2.0) */
export function scimReplaceGroup(args: ScimReplaceGroupArgs, options?: RequestOptions): Promise<ScimReplaceGroupResult> {
  return request<ScimReplaceGroupResult>({
    operationId: "scimReplaceGroup",
    method: "PUT",
    path: "/scim/v2/Groups/{groupId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface ScimReplaceUserArgs {
  path: { userId: string };
  body: {
  schemas?: Array<string>;
  userName: string;
  externalId?: (string | null);
  displayName?: string;
  name?: {
    givenName?: string;
    familyName?: string;
    formatted?: string;
  };
  active?: boolean;
  emails?: Array<{
    value: string;
    primary?: boolean;
  }>;
};
}

export type ScimReplaceUserResult = S.ScimUser;

/** Replace a person from the directory (SCIM 2.0) */
export function scimReplaceUser(args: ScimReplaceUserArgs, options?: RequestOptions): Promise<ScimReplaceUserResult> {
  return request<ScimReplaceUserResult>({
    operationId: "scimReplaceUser",
    method: "PUT",
    path: "/scim/v2/Users/{userId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export type SdkQuickstartResult = {
  baseUrl: string;
  apiVersion: string;
  libraries: Array<{
    language: "javascript" | "python";
    label: string;
    packageName: string;
    published: boolean;
    quickstart: string;
  }>;
};

/** The client libraries and how to make a first call with each */
export function sdkQuickstart(options?: RequestOptions): Promise<SdkQuickstartResult> {
  return request<SdkQuickstartResult>({
    operationId: "sdkQuickstart",
    method: "GET",
    path: "/setup/sdks",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface SendCampaignArgs {
  path: { campaignId: string };
}

export type SendCampaignResult = {
  sent: number;
  refused: number;
  heldForWinner: number;
  heldForWarmup: number;
  refusals: Array<S.SendRefusal>;
};

/** Send a campaign */
export function sendCampaign(args: SendCampaignArgs, options?: RequestOptions): Promise<SendCampaignResult> {
  return request<SendCampaignResult>({
    operationId: "sendCampaign",
    method: "POST",
    path: "/campaigns/{campaignId}/send",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface SendChatReplyArgs {
  path: { conversationId: string };
  body: {
  body: string;
};
}

export type SendChatReplyResult = {
  messageId: string;
};

/** Reply to a visitor */
export function sendChatReply(args: SendChatReplyArgs, options?: RequestOptions): Promise<SendChatReplyResult> {
  return request<SendChatReplyResult>({
    operationId: "sendChatReply",
    method: "POST",
    path: "/chat/conversations/{conversationId}/messages",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SendQuoteArgs {
  path: { quoteId: string };
  body: Record<string, never>;
}

export type SendQuoteResult = S.Quote;

/** Send a quote, and stop it changing */
export function sendQuote(args: SendQuoteArgs, options?: RequestOptions): Promise<SendQuoteResult> {
  return request<SendQuoteResult>({
    operationId: "sendQuote",
    method: "POST",
    path: "/quotes/{quoteId}/send",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SendReportSubscriptionNowArgs {
  path: { reportId: string; subscriptionId: string };
  body: Record<string, never>;
}

export type SendReportSubscriptionNowResult = {
  detail: string;
};

/** Send it now, and see what was sent */
export function sendReportSubscriptionNow(args: SendReportSubscriptionNowArgs, options?: RequestOptions): Promise<SendReportSubscriptionNowResult> {
  return request<SendReportSubscriptionNowResult>({
    operationId: "sendReportSubscriptionNow",
    method: "POST",
    path: "/reports/{reportId}/subscriptions/{subscriptionId}/send",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetAccountPricelistArgs {
  path: { accountId: string };
  body: {
  pricelistId: (string | null);
};
}

export type SetAccountPricelistResult = {
  pricelistId: (string | null);
};

/** Put a company on its own price list, or take it off one */
export function setAccountPricelist(args: SetAccountPricelistArgs, options?: RequestOptions): Promise<SetAccountPricelistResult> {
  return request<SetAccountPricelistResult>({
    operationId: "setAccountPricelist",
    method: "PUT",
    path: "/accounts/{accountId}/pricelist",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetAgentAutonomyArgs {
  path: { agentId: string };
  body: {
  tool: string;
  autonomy: "propose" | "auto" | "read-only";
  enabled: boolean;
};
}

export type SetAgentAutonomyResult = {
  saved: boolean;
};

/** Set what an agent may do without asking */
export function setAgentAutonomy(args: SetAgentAutonomyArgs, options?: RequestOptions): Promise<SetAgentAutonomyResult> {
  return request<SetAgentAutonomyResult>({
    operationId: "setAgentAutonomy",
    method: "POST",
    path: "/agents/{agentId}/actions",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetAgentSourcesArgs {
  path: { agentId: string };
  body: {
  sources: Array<{
    kind: "knowledge" | "records" | "documents";
    reference: (string | null);
    enabled: boolean;
  }>;
};
}

export type SetAgentSourcesResult = {
  sources: Array<S.AgentSource>;
  groundable: Array<string>;
};

/** What this agent may read to ground an answer */
export function setAgentSources(args: SetAgentSourcesArgs, options?: RequestOptions): Promise<SetAgentSourcesResult> {
  return request<SetAgentSourcesResult>({
    operationId: "setAgentSources",
    method: "POST",
    path: "/agents/{agentId}/sources",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetApprovalDelegationArgs {
  body: {
  delegation: ({
    delegateId: string;
    startsOn: string;
    endsOn: string;
  } | null);
};
}

export type SetApprovalDelegationResult = {
  delegation: ({
    delegateId: string;
    startsOn: string;
    endsOn: string;
  } | null);
};

/** While I am away, send my approvals to somebody else - or stop */
export function setApprovalDelegation(args: SetApprovalDelegationArgs, options?: RequestOptions): Promise<SetApprovalDelegationResult> {
  return request<SetApprovalDelegationResult>({
    operationId: "setApprovalDelegation",
    method: "POST",
    path: "/approvals/delegation",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetBackupRetentionArgs {
  body: {
  days: number;
};
}

export type SetBackupRetentionResult = S.BackupRetention;

/** How long backups are kept */
export function setBackupRetention(args: SetBackupRetentionArgs, options?: RequestOptions): Promise<SetBackupRetentionResult> {
  return request<SetBackupRetentionResult>({
    operationId: "setBackupRetention",
    method: "PUT",
    path: "/setup/backups/retention",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetBillingScheduleArgs {
  body: {
  subscriptionId: string;
  nextInvoiceOn: string;
  taxJurisdiction?: (string | null);
};
}

export type SetBillingScheduleResult = {
  nextInvoiceOn: string;
  billingAnchorDay: number;
  taxJurisdiction: (string | null);
};

/** Set when a subscription is billed, and whose tax applies */
export function setBillingSchedule(args: SetBillingScheduleArgs, options?: RequestOptions): Promise<SetBillingScheduleResult> {
  return request<SetBillingScheduleResult>({
    operationId: "setBillingSchedule",
    method: "POST",
    path: "/invoices/schedule",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetBreakGlassAdministratorArgs {
  body: {
  userId: string;
  isBreakGlass: boolean;
};
}

export type SetBreakGlassAdministratorResult = {
  ok: true;
};

/** Name the account that can always sign in with a password */
export function setBreakGlassAdministrator(args: SetBreakGlassAdministratorArgs, options?: RequestOptions): Promise<SetBreakGlassAdministratorResult> {
  return request<SetBreakGlassAdministratorResult>({
    operationId: "setBreakGlassAdministrator",
    method: "POST",
    path: "/settings/sso/break-glass",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetCampaignBudgetArgs {
  path: { campaignId: string };
  body: {
  budget: (string | null);
};
}

export type SetCampaignBudgetResult = S.CampaignBudget;

/** Set or clear the planned budget */
export function setCampaignBudget(args: SetCampaignBudgetArgs, options?: RequestOptions): Promise<SetCampaignBudgetResult> {
  return request<SetCampaignBudgetResult>({
    operationId: "setCampaignBudget",
    method: "POST",
    path: "/campaigns/{campaignId}/budget",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetCampaignSmsTemplateArgs {
  path: { campaignId: string };
  body: {
  templateId: (string | null);
};
}

export type SetCampaignSmsTemplateResult = {
  templateId: (string | null);
};

/** Choose the SMS template a draft SMS campaign sends */
export function setCampaignSmsTemplate(args: SetCampaignSmsTemplateArgs, options?: RequestOptions): Promise<SetCampaignSmsTemplateResult> {
  return request<SetCampaignSmsTemplateResult>({
    operationId: "setCampaignSmsTemplate",
    method: "POST",
    path: "/campaigns/{campaignId}/sms-template",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetCampaignTrackingCodeArgs {
  path: { campaignId: string };
  body: {
  code: (string | null);
};
}

export type SetCampaignTrackingCodeResult = {
  campaignId: string;
  code: (string | null);
};

/** The utm_campaign value a campaign is tracked by, compared exactly */
export function setCampaignTrackingCode(args: SetCampaignTrackingCodeArgs, options?: RequestOptions): Promise<SetCampaignTrackingCodeResult> {
  return request<SetCampaignTrackingCodeResult>({
    operationId: "setCampaignTrackingCode",
    method: "PATCH",
    path: "/campaigns/{campaignId}/tracking-code",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetCaseAssetArgs {
  path: { caseId: string };
  body: {
  assetId: (string | null);
};
}

export type SetCaseAssetResult = {
  asset: (S.CustomerAsset | null);
};

/** Say which of the customer’s assets a case is about */
export function setCaseAsset(args: SetCaseAssetArgs, options?: RequestOptions): Promise<SetCaseAssetResult> {
  return request<SetCaseAssetResult>({
    operationId: "setCaseAsset",
    method: "POST",
    path: "/cases/{caseId}/asset",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetCaseStatusArgs {
  path: { caseId: string };
  body: {
  status: "new" | "open" | "waiting-on-customer" | "resolved" | "closed";
};
}

export type SetCaseStatusResult = {
  status: "new" | "open" | "waiting-on-customer" | "resolved" | "closed";
};

/** Move a case along */
export function setCaseStatus(args: SetCaseStatusArgs, options?: RequestOptions): Promise<SetCaseStatusResult> {
  return request<SetCaseStatusResult>({
    operationId: "setCaseStatus",
    method: "POST",
    path: "/cases/{caseId}/status",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetChatScriptArgs {
  path: { embedId: string };
  body: {
  flowId: (string | null);
};
}

export type SetChatScriptResult = {
  done: true;
};

/** Which chat script a chat embed runs */
export function setChatScript(args: SetChatScriptArgs, options?: RequestOptions): Promise<SetChatScriptResult> {
  return request<SetChatScriptResult>({
    operationId: "setChatScript",
    method: "POST",
    path: "/embeds/{embedId}/chat-script",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetDealCompetitorArgs {
  path: { dealId: string; competitorId: string };
  body: {
  outcome?: "unknown" | "won-against" | "lost-to" | "withdrew";
  note?: (string | null);
};
}

export type SetDealCompetitorResult = {
  ok: true;
};

/** Put a competitor on a deal, or change what happened against them */
export function setDealCompetitor(args: SetDealCompetitorArgs, options?: RequestOptions): Promise<SetDealCompetitorResult> {
  return request<SetDealCompetitorResult>({
    operationId: "setDealCompetitor",
    method: "PUT",
    path: "/deals/{dealId}/competitors/{competitorId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetDealContactArgs {
  path: { dealId: string; contactId: string };
  body: {
  role: "decision-maker" | "champion" | "blocker" | "influencer" | "user" | "technical";
};
}

export type SetDealContactResult = {
  ok: true;
};

/** Put a contact on a deal, or change what they are to it */
export function setDealContact(args: SetDealContactArgs, options?: RequestOptions): Promise<SetDealContactResult> {
  return request<SetDealContactResult>({
    operationId: "setDealContact",
    method: "PUT",
    path: "/deals/{dealId}/contacts/{contactId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetDealDocumentKindArgs {
  path: { dealId: string; attachmentId: string };
  body: {
  kind: ("proposal" | "drawing" | "specification" | "contract" | "other" | null);
};
}

export type SetDealDocumentKindResult = {
  documents: Array<S.DealDocument>;
};

/** Say what kind of document a file is */
export function setDealDocumentKind(args: SetDealDocumentKindArgs, options?: RequestOptions): Promise<SetDealDocumentKindResult> {
  return request<SetDealDocumentKindResult>({
    operationId: "setDealDocumentKind",
    method: "POST",
    path: "/deals/{dealId}/documents/{attachmentId}/kind",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetDealForecastCategoryArgs {
  path: { dealId: string };
  body: {
  category: "commit" | "best-case" | "pipeline" | "omitted";
};
}

export type SetDealForecastCategoryResult = {
  dealId: string;
  category: "commit" | "best-case" | "pipeline" | "omitted";
};

/** What the person working a deal says will happen to it */
export function setDealForecastCategory(args: SetDealForecastCategoryArgs, options?: RequestOptions): Promise<SetDealForecastCategoryResult> {
  return request<SetDealForecastCategoryResult>({
    operationId: "setDealForecastCategory",
    method: "PUT",
    path: "/deals/{dealId}/forecast-category",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetDealTeamArgs {
  path: { dealId: string };
  body: {
  members: Array<{
    userId: string;
    role: "owner" | "contributor" | "specialist" | "manager" | "introducer";
    splitPercent: number;
  }>;
};
}

export type SetDealTeamResult = {
  ok: true;
};

/** Set who shares this deal, and for how much */
export function setDealTeam(args: SetDealTeamArgs, options?: RequestOptions): Promise<SetDealTeamResult> {
  return request<SetDealTeamResult>({
    operationId: "setDealTeam",
    method: "PUT",
    path: "/deals/{dealId}/team",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetDefaultAccountAddressArgs {
  path: { accountId: string; addressId: string };
  body: Record<string, never>;
}

export type SetDefaultAccountAddressResult = {
  address: S.AccountAddress;
};

/** Make an address the default of its kind */
export function setDefaultAccountAddress(args: SetDefaultAccountAddressArgs, options?: RequestOptions): Promise<SetDefaultAccountAddressResult> {
  return request<SetDefaultAccountAddressResult>({
    operationId: "setDefaultAccountAddress",
    method: "POST",
    path: "/accounts/{accountId}/addresses/{addressId}/default",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetDiscountCapArgs {
  body: {
  percent: (string | null);
};
}

export type SetDiscountCapResult = {
  capPercent: (string | null);
};

/** Change the discount cap, or remove it */
export function setDiscountCap(args: SetDiscountCapArgs, options?: RequestOptions): Promise<SetDiscountCapResult> {
  return request<SetDiscountCapResult>({
    operationId: "setDiscountCap",
    method: "PUT",
    path: "/setup/discount-cap",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetExchangeRateArgs {
  body: {
  fromCurrency: string;
  toCurrency: string;
  effectiveOn: string;
  rate: string;
};
}

export type SetExchangeRateResult = {
  rates: Array<{
    fromCurrency: string;
    toCurrency: string;
    effectiveOn: string;
    rate: string;
    source: "manual" | "imported";
  }>;
};

/** Record what a currency was worth, from a date */
export function setExchangeRate(args: SetExchangeRateArgs, options?: RequestOptions): Promise<SetExchangeRateResult> {
  return request<SetExchangeRateResult>({
    operationId: "setExchangeRate",
    method: "PUT",
    path: "/setup/currencies/rates",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetFieldRuleArgs {
  path: { permissionSetId: string; objectKey: string; fieldKey: string };
  body: {
  access: "hidden" | "read" | "edit";
};
}

export type SetFieldRuleResult = {
  rules: Array<{
    objectKey: string;
    fieldKey: string;
    access: "hidden" | "read" | "edit";
  }>;
  peopleAffected: number;
};

/** Hide a field, make it read-only, or give it back */
export function setFieldRule(args: SetFieldRuleArgs, options?: RequestOptions): Promise<SetFieldRuleResult> {
  return request<SetFieldRuleResult>({
    operationId: "setFieldRule",
    method: "PUT",
    path: "/setup/permission-sets/{permissionSetId}/fields/{objectKey}/{fieldKey}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetFiscalYearStartArgs {
  body: {
  month: number;
};
}

export type SetFiscalYearStartResult = {
  month: number;
};

/** The month this workspace-s financial year starts in */
export function setFiscalYearStart(args: SetFiscalYearStartArgs, options?: RequestOptions): Promise<SetFiscalYearStartResult> {
  return request<SetFiscalYearStartResult>({
    operationId: "setFiscalYearStart",
    method: "PUT",
    path: "/setup/fiscal-year",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetInboundMailboxActiveArgs {
  path: { mailboxId: string };
  body: {
  active: boolean;
};
}

export type SetInboundMailboxActiveResult = {
  mailbox: S.InboundMailbox;
};

/** Switch a mailbox on or off */
export function setInboundMailboxActive(args: SetInboundMailboxActiveArgs, options?: RequestOptions): Promise<SetInboundMailboxActiveResult> {
  return request<SetInboundMailboxActiveResult>({
    operationId: "setInboundMailboxActive",
    method: "POST",
    path: "/inbound-mailboxes/{mailboxId}/active",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetInboundSourceActiveArgs {
  path: { sourceId: string };
  body: {
  active: boolean;
};
}

export type SetInboundSourceActiveResult = {
  active: boolean;
};

/** Switch an inbound source on or off */
export function setInboundSourceActive(args: SetInboundSourceActiveArgs, options?: RequestOptions): Promise<SetInboundSourceActiveResult> {
  return request<SetInboundSourceActiveResult>({
    operationId: "setInboundSourceActive",
    method: "POST",
    path: "/developer/inbound-sources/{sourceId}/active",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetIpRetentionArgs {
  body: {
  days: number;
};
}

export type SetIpRetentionResult = S.IpRetention;

/** How long a raw visitor address is kept */
export function setIpRetention(args: SetIpRetentionArgs, options?: RequestOptions): Promise<SetIpRetentionResult> {
  return request<SetIpRetentionResult>({
    operationId: "setIpRetention",
    method: "PUT",
    path: "/setup/tracking/ip-retention",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetLeadScoreDecayArgs {
  body: {
  pointsPerWeek: number;
};
}

export type SetLeadScoreDecayResult = {
  decayPerWeek: number;
};

/** How fast a lead nobody has touched cools */
export function setLeadScoreDecay(args: SetLeadScoreDecayArgs, options?: RequestOptions): Promise<SetLeadScoreDecayResult> {
  return request<SetLeadScoreDecayResult>({
    operationId: "setLeadScoreDecay",
    method: "PUT",
    path: "/leads/score-decay",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetModelRoutesArgs {
  path: { task: "agent" | "draft" | "summarise" };
  body: {
  steps: Array<{
    providerId: (string | null);
    model: (string | null);
    enabled: boolean;
  }>;
};
}

export type SetModelRoutesResult = {
  routes: Array<S.ModelRouteStep>;
};

/** Set the order models are tried in for one kind of request */
export function setModelRoutes(args: SetModelRoutesArgs, options?: RequestOptions): Promise<SetModelRoutesResult> {
  return request<SetModelRoutesResult>({
    operationId: "setModelRoutes",
    method: "POST",
    path: "/setup/model-routing/{task}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetModuleStateArgs {
  path: { flagKey: string };
  body: {
  enabled: boolean;
};
}

export type SetModuleStateResult = S.EntitlementState;

/** Switch a module or feature on or off */
export function setModuleState(args: SetModuleStateArgs, options?: RequestOptions): Promise<SetModuleStateResult> {
  return request<SetModuleStateResult>({
    operationId: "setModuleState",
    method: "PATCH",
    path: "/setup/modules/{flagKey}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetMyAvailabilityArgs {
  body: {
  hours: Array<S.WeeklyHours>;
};
}

export type SetMyAvailabilityResult = {
  hours: Array<S.WeeklyHours>;
};

/** Replace your working hours */
export function setMyAvailability(args: SetMyAvailabilityArgs, options?: RequestOptions): Promise<SetMyAvailabilityResult> {
  return request<SetMyAvailabilityResult>({
    operationId: "setMyAvailability",
    method: "PUT",
    path: "/availability",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetNotificationPreferenceArgs {
  body: {
  kind: "record.assigned" | "record.mentioned" | "record.followed.changed" | "approval.requested" | "agent.proposed" | "case.breached" | "lead.unworked" | "lead.mql" | "system.failing" | "task.due" | "record.escalated";
  channel: string;
  enabled: boolean;
};
}

export type SetNotificationPreferenceResult = {
  preferences: Array<S.NotificationPreference>;
};

/** Switch an event on or off */
export function setNotificationPreference(args: SetNotificationPreferenceArgs, options?: RequestOptions): Promise<SetNotificationPreferenceResult> {
  return request<SetNotificationPreferenceResult>({
    operationId: "setNotificationPreference",
    method: "PUT",
    path: "/me/notifications/preferences",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetObjectSharingArgs {
  path: { objectKey: string };
  body: {
  mode: "private" | "team" | "everyone" | "criteria";
  access: "private" | "read" | "read-write";
  criteria?: {
    conditions: S.RuleDocument;
    access: "read" | "read-write";
    grantTeamId: (string | null);
    grantRoleId: (string | null);
  };
};
}

export type SetObjectSharingResult = {
  objects: Array<{
    objectKey: string;
    label: string;
    mode: "private" | "team" | "everyone" | "criteria";
    access: "private" | "read" | "read-write";
    owned: boolean;
    criteria: ({
      conditions: S.RuleDocument;
      access: "read" | "read-write";
      grantTeamId: (string | null);
      grantRoleId: (string | null);
    } | null);
  }>;
};

/** Choose who can see an object-s records */
export function setObjectSharing(args: SetObjectSharingArgs, options?: RequestOptions): Promise<SetObjectSharingResult> {
  return request<SetObjectSharingResult>({
    operationId: "setObjectSharing",
    method: "PUT",
    path: "/setup/sharing/{objectKey}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetOnboardingStepDoneArgs {
  body: {
  stepId: string;
  done: boolean;
};
}

export type SetOnboardingStepDoneResult = {
  done: true;
};

/** Tick or untick an onboarding step */
export function setOnboardingStepDone(args: SetOnboardingStepDoneArgs, options?: RequestOptions): Promise<SetOnboardingStepDoneResult> {
  return request<SetOnboardingStepDoneResult>({
    operationId: "setOnboardingStepDone",
    method: "POST",
    path: "/onboarding/steps/done",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetPartnerArgs {
  path: { accountId: string };
  body: {
  partner: boolean;
  protectionDays?: number;
};
}

export type SetPartnerResult = {
  accountId: string;
  partnerSince: (string | null);
  protectionDays: number;
};

/** Make a company a partner, change its protection period, or end the partnership */
export function setPartner(args: SetPartnerArgs, options?: RequestOptions): Promise<SetPartnerResult> {
  return request<SetPartnerResult>({
    operationId: "setPartner",
    method: "POST",
    path: "/setup/partners/{accountId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetPersonPermissionSetsArgs {
  path: { userId: string };
  body: {
  permissionSetIds: Array<string>;
};
}

export type SetPersonPermissionSetsResult = {
  permissionSetIds: Array<string>;
  effective: {
    objects: Array<{
      objectKey: string;
      actions: Array<"read" | "create" | "update" | "delete" | "export">;
    }>;
    capabilities: Array<string>;
  };
};

/** Replace the sets somebody holds */
export function setPersonPermissionSets(args: SetPersonPermissionSetsArgs, options?: RequestOptions): Promise<SetPersonPermissionSetsResult> {
  return request<SetPersonPermissionSetsResult>({
    operationId: "setPersonPermissionSets",
    method: "PUT",
    path: "/setup/people/{userId}/permission-sets",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetPersonRoleArgs {
  path: { userId: string };
  body: {
  roleId: (string | null);
};
}

export type SetPersonRoleResult = {
  userId: string;
  name: string;
  email: string;
  roleId: (string | null);
  roleName: (string | null);
  isWorkspaceAdmin: boolean;
  status: string;
};

/** Move somebody to another position in the reporting line */
export function setPersonRole(args: SetPersonRoleArgs, options?: RequestOptions): Promise<SetPersonRoleResult> {
  return request<SetPersonRoleResult>({
    operationId: "setPersonRole",
    method: "PUT",
    path: "/setup/people/{userId}/role",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetPersonStatusArgs {
  path: { userId: string };
  body: {
  status: "active" | "deactivated";
};
}

export type SetPersonStatusResult = {
  userId: string;
  name: string;
  email: string;
  roleId: (string | null);
  roleName: (string | null);
  isWorkspaceAdmin: boolean;
  status: string;
};

/** Deactivate somebody, or bring them back */
export function setPersonStatus(args: SetPersonStatusArgs, options?: RequestOptions): Promise<SetPersonStatusResult> {
  return request<SetPersonStatusResult>({
    operationId: "setPersonStatus",
    method: "PUT",
    path: "/setup/people/{userId}/status",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetPlanPriceArgs {
  body: {
  planId: string;
  currency: string;
  amount: string;
  effectiveFrom: string;
};
}

export type SetPlanPriceResult = {
  plan: S.Plan;
};

/** Price a plan from a date */
export function setPlanPrice(args: SetPlanPriceArgs, options?: RequestOptions): Promise<SetPlanPriceResult> {
  return request<SetPlanPriceResult>({
    operationId: "setPlanPrice",
    method: "POST",
    path: "/plans/prices",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetPlaybookRampArgs {
  body: {
  days: number;
};
}

export type SetPlaybookRampResult = S.PlaybookRamp;

/** Change how long the playbook opens by itself for */
export function setPlaybookRamp(args: SetPlaybookRampArgs, options?: RequestOptions): Promise<SetPlaybookRampResult> {
  return request<SetPlaybookRampResult>({
    operationId: "setPlaybookRamp",
    method: "PUT",
    path: "/setup/playbook-ramp",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetPluginEnabledArgs {
  path: { pluginId: string };
  body: {
  enabled: boolean;
  reason?: string;
};
}

export type SetPluginEnabledResult = S.Plugin;

/** Switch a plugin on or off */
export function setPluginEnabled(args: SetPluginEnabledArgs, options?: RequestOptions): Promise<SetPluginEnabledResult> {
  return request<SetPluginEnabledResult>({
    operationId: "setPluginEnabled",
    method: "POST",
    path: "/setup/plugins/{pluginId}/enabled",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetPortalPreferenceArgs {
  body: {
  token: string;
  channel: "email" | "sms" | "whatsapp" | "call";
  optedIn: boolean;
};
}

export type SetPortalPreferenceResult = {
  saved: boolean;
  refusal: (S.PortalRefusal | null);
};

/** Change one channel preference */
export function setPortalPreference(args: SetPortalPreferenceArgs, options?: RequestOptions): Promise<SetPortalPreferenceResult> {
  return request<SetPortalPreferenceResult>({
    operationId: "setPortalPreference",
    method: "POST",
    path: "/portal/preferences",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetProductBaseUnitArgs {
  path: { productId: string };
  body: {
  unitId: (string | null);
};
}

export type SetProductBaseUnitResult = {
  productId: string;
  unitId: (string | null);
};

/** The unit a product is counted in */
export function setProductBaseUnit(args: SetProductBaseUnitArgs, options?: RequestOptions): Promise<SetProductBaseUnitResult> {
  return request<SetProductBaseUnitResult>({
    operationId: "setProductBaseUnit",
    method: "PATCH",
    path: "/products/{productId}/base-unit",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetProductCompositionArgs {
  path: { productId: string };
  body: {
  kind: ("kit" | "bundle" | null);
  components: Array<{
    childId: string;
    quantity: string;
    optional: boolean;
  }>;
};
}

export type SetProductCompositionResult = {
  composition: S.ProductComposition;
};

/** Set the parts, and whether this is a kit or a bundle */
export function setProductComposition(args: SetProductCompositionArgs, options?: RequestOptions): Promise<SetProductCompositionResult> {
  return request<SetProductCompositionResult>({
    operationId: "setProductComposition",
    method: "POST",
    path: "/products/{productId}/composition",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetQuoteDiscountArgs {
  path: { quoteId: string };
  body: {
  discountPercent: string;
};
}

export type SetQuoteDiscountResult = S.Quote;

/** Set the discount on a whole quote */
export function setQuoteDiscount(args: SetQuoteDiscountArgs, options?: RequestOptions): Promise<SetQuoteDiscountResult> {
  return request<SetQuoteDiscountResult>({
    operationId: "setQuoteDiscount",
    method: "PATCH",
    path: "/quotes/{quoteId}/discount",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetQuoteTermsArgs {
  path: { quoteId: string };
  body: {
  terms: (string | null);
  validUntil: (string | null);
};
}

export type SetQuoteTermsResult = S.Quote;

/** Set a quote's terms and the date it is valid until */
export function setQuoteTerms(args: SetQuoteTermsArgs, options?: RequestOptions): Promise<SetQuoteTermsResult> {
  return request<SetQuoteTermsResult>({
    operationId: "setQuoteTerms",
    method: "PATCH",
    path: "/quotes/{quoteId}/terms",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetRecordFollowStateArgs {
  path: { objectKey: string; recordId: string };
  body: {
  following: boolean;
};
}

export type SetRecordFollowStateResult = {
  following: boolean;
  followers: number;
};

/** Follow or unfollow a record */
export function setRecordFollowState(args: SetRecordFollowStateArgs, options?: RequestOptions): Promise<SetRecordFollowStateResult> {
  return request<SetRecordFollowStateResult>({
    operationId: "setRecordFollowState",
    method: "PUT",
    path: "/records/{objectKey}/{recordId}/follow",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetRoleParentArgs {
  path: { roleId: string };
  body: {
  parentRoleId: (string | null);
};
}

export type SetRoleParentResult = {
  roles: Array<{
    roleId: string;
    name: string;
    parentRoleId: (string | null);
    depth: number;
    members: number;
  }>;
};

/** Move a role under another, or to the top of its own line */
export function setRoleParent(args: SetRoleParentArgs, options?: RequestOptions): Promise<SetRoleParentResult> {
  return request<SetRoleParentResult>({
    operationId: "setRoleParent",
    method: "PUT",
    path: "/setup/roles/{roleId}/parent",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetSandboxArgs {
  body: {
  isSandbox: boolean;
};
}

export type SetSandboxResult = {
  isSandbox: boolean;
};

/** Mark this workspace as a sandbox, or as production; a sandbox says so on every screen */
export function setSandbox(args: SetSandboxArgs, options?: RequestOptions): Promise<SetSandboxResult> {
  return request<SetSandboxResult>({
    operationId: "setSandbox",
    method: "PUT",
    path: "/setup/environment/sandbox",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetSmsSenderArgs {
  body: {
  country: string;
  sender: string;
  dltEntityId: (string | null);
};
}

export type SetSmsSenderResult = {
  senders: Array<{
    country: string;
    sender: string;
    dltEntityId: (string | null);
  }>;
};

/** The number or sender id to send from in one country */
export function setSmsSender(args: SetSmsSenderArgs, options?: RequestOptions): Promise<SetSmsSenderResult> {
  return request<SetSmsSenderResult>({
    operationId: "setSmsSender",
    method: "POST",
    path: "/marketing/sms/senders",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetSsoEnforcementArgs {
  body: {
  enforced: boolean;
};
}

export type SetSsoEnforcementResult = S.SsoConnection;

/** Turn single sign-on enforcement on or off */
export function setSsoEnforcement(args: SetSsoEnforcementArgs, options?: RequestOptions): Promise<SetSsoEnforcementResult> {
  return request<SetSsoEnforcementResult>({
    operationId: "setSsoEnforcement",
    method: "POST",
    path: "/settings/sso/enforcement",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetSubscriptionTermArgs {
  path: { subscriptionId: string };
  body: {
  endsOn: (string | null);
  termMonths?: (number | null);
  autoRenews?: boolean;
  renewalNoticeDays?: number;
};
}

export type SetSubscriptionTermResult = S.SubscriptionTerm;

/** Set when it ends, and what happens then */
export function setSubscriptionTerm(args: SetSubscriptionTermArgs, options?: RequestOptions): Promise<SetSubscriptionTermResult> {
  return request<SetSubscriptionTermResult>({
    operationId: "setSubscriptionTerm",
    method: "PUT",
    path: "/subscriptions/{subscriptionId}/term",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetTagGovernanceArgs {
  body: {
  governed: boolean;
};
}

export type SetTagGovernanceResult = {
  governed: boolean;
};

/** Decide who may invent a tag */
export function setTagGovernance(args: SetTagGovernanceArgs, options?: RequestOptions): Promise<SetTagGovernanceResult> {
  return request<SetTagGovernanceResult>({
    operationId: "setTagGovernance",
    method: "POST",
    path: "/setup/tags/governance",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetTargetsArgs {
  body: {
  entries: Array<S.TargetEntry>;
};
}

export type SetTargetsResult = {
  targets: Array<S.StoredTarget>;
};

/** Set one or many targets at once */
export function setTargets(args: SetTargetsArgs, options?: RequestOptions): Promise<SetTargetsResult> {
  return request<SetTargetsResult>({
    operationId: "setTargets",
    method: "PUT",
    path: "/targets",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetTeamMembershipArgs {
  path: { teamId: string; userId: string };
  body: {
  member: boolean;
};
}

export type SetTeamMembershipResult = {
  teamId: string;
  name: string;
  description: (string | null);
  members: Array<{
    userId: string;
    name: string;
  }>;
};

/** Put somebody in a team, or take them out */
export function setTeamMembership(args: SetTeamMembershipArgs, options?: RequestOptions): Promise<SetTeamMembershipResult> {
  return request<SetTeamMembershipResult>({
    operationId: "setTeamMembership",
    method: "PUT",
    path: "/setup/teams/{teamId}/members/{userId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetUsagePriceArgs {
  body: {
  subscriptionId: string;
  meterId: string;
  currency: string;
  included: string;
  unitAmount: string;
  effectiveFrom: string;
};
}

export type SetUsagePriceResult = {
  usagePriceId: string;
};

/** Price a meter on one subscription, from a date */
export function setUsagePrice(args: SetUsagePriceArgs, options?: RequestOptions): Promise<SetUsagePriceResult> {
  return request<SetUsagePriceResult>({
    operationId: "setUsagePrice",
    method: "POST",
    path: "/usage/prices",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetWarmupArgs {
  body: {
  on: boolean;
};
}

export type SetWarmupResult = {
  days: number;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  bounceRate: number;
  complaintRate: number;
  bounceWarning: boolean;
  complaintWarning: boolean;
  suppressed: number;
  heldForWarmup: number;
  warmup: {
    cap: (number | null);
    sentToday: number;
    day: (number | null);
  };
};

/** Start or stop warming up campaign sending (50 a day, doubling) */
export function setWarmup(args: SetWarmupArgs, options?: RequestOptions): Promise<SetWarmupResult> {
  return request<SetWarmupResult>({
    operationId: "setWarmup",
    method: "POST",
    path: "/marketing/deliverability/warmup",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SetWorkingWeekArgs {
  body: {
  days: Array<number>;
  startMinute: number;
  endMinute: number;
};
}

export type SetWorkingWeekResult = {
  days: Array<number>;
  startMinute: number;
  endMinute: number;
  timeZone: string;
};

/** Change the days and hours this workspace works */
export function setWorkingWeek(args: SetWorkingWeekArgs, options?: RequestOptions): Promise<SetWorkingWeekResult> {
  return request<SetWorkingWeekResult>({
    operationId: "setWorkingWeek",
    method: "PUT",
    path: "/setup/working-week",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SignInArgs {
  body: {
  email: string;
  password: string;
};
}

export type SignInResult = S.SignInOutcome;

/** Sign in with an email address and password */
export function signIn(args: SignInArgs, options?: RequestOptions): Promise<SignInResult> {
  return request<SignInResult>({
    operationId: "signIn",
    method: "POST",
    path: "/auth/sign-in",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export type SignOutCurrentSessionResult = {
  status: "signed-out";
};

/** Sign out of this device */
export function signOutCurrentSession(options?: RequestOptions): Promise<SignOutCurrentSessionResult> {
  return request<SignOutCurrentSessionResult>({
    operationId: "signOutCurrentSession",
    method: "POST",
    path: "/auth/sign-out",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type SignOutOtherDevicesResult = {
  ended: number;
};

/** Sign out of every device but this one */
export function signOutOtherDevices(options?: RequestOptions): Promise<SignOutOtherDevicesResult> {
  return request<SignOutOtherDevicesResult>({
    operationId: "signOutOtherDevices",
    method: "POST",
    path: "/me/devices/sign-out-others",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface SignUpArgs {
  body: {
  workspaceName: string;
  email: string;
  password?: string;
  adminName?: string;
  language?: "en" | "ar";
  timezone?: string;
  region?: string;
};
}

export type SignUpResult = {
  status: "signed-in";
  user: S.UserSummary;
  workspaceSlug: string;
  steps: Array<S.ProvisionStep>;
  expiresAt: string;
};

/** Create a workspace and its first administrator */
export function signUp(args: SignUpArgs, options?: RequestOptions): Promise<SignUpResult> {
  return request<SignUpResult>({
    operationId: "signUp",
    method: "POST",
    path: "/auth/sign-up",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SignUpAvailabilityArgs {
  query: { email: string };
}

export type SignUpAvailabilityResult = {
  taken: boolean;
};

/** Check whether an email address already has an account */
export function signUpAvailability(args: SignUpAvailabilityArgs, options?: RequestOptions): Promise<SignUpAvailabilityResult> {
  return request<SignUpAvailabilityResult>({
    operationId: "signUpAvailability",
    method: "GET",
    path: "/auth/sign-up/availability",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type SimilarTagsResult = {
  pairs: Array<{
    a: S.Tag;
    b: S.Tag;
    reason: string;
  }>;
};

/** Tags that look like each other */
export function similarTags(options?: RequestOptions): Promise<SimilarTagsResult> {
  return request<SimilarTagsResult>({
    operationId: "similarTags",
    method: "GET",
    path: "/setup/tags/similar",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface SimulateRoutingArgs {
  query: { leadId: string };
}

export type SimulateRoutingResult = S.RoutingDecision;

/** Which rule would claim this lead, and who would get it */
export function simulateRouting(args: SimulateRoutingArgs, options?: RequestOptions): Promise<SimulateRoutingResult> {
  return request<SimulateRoutingResult>({
    operationId: "simulateRouting",
    method: "GET",
    path: "/setup/routing/simulate",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface SkipCallListEntryArgs {
  path: { entryId: string };
  body: Record<string, never>;
}

export type SkipCallListEntryResult = {
  list: S.CallList;
  next: (S.CallListEntry | null);
};

/** Pass over somebody, on purpose */
export function skipCallListEntry(args: SkipCallListEntryArgs, options?: RequestOptions): Promise<SkipCallListEntryResult> {
  return request<SkipCallListEntryResult>({
    operationId: "skipCallListEntry",
    method: "POST",
    path: "/call-lists/entries/{entryId}/skip",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SnoozeNextStepArgs {
  path: { stepId: string };
  body: {
  dueDate: string;
};
}

export type SnoozeNextStepResult = {
  moved: true;
};

/** Move a step to another day */
export function snoozeNextStep(args: SnoozeNextStepArgs, options?: RequestOptions): Promise<SnoozeNextStepResult> {
  return request<SnoozeNextStepResult>({
    operationId: "snoozeNextStep",
    method: "PATCH",
    path: "/work/{stepId}/due-date",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SourceRoiArgs {
  query: { from: string; to: string; by: "source" | "campaign" };
}

export type SourceRoiResult = {
  currency: string;
  rows: Array<{
    key: string;
    leads: number;
    converted: number;
    wonDeals: number;
    revenue: string;
    cost: (string | null);
    costPerLead: (string | null);
    costPerWin: (string | null);
    roi: (number | null);
  }>;
};

/** Leads, conversions, revenue and - where spend is recorded - cost and return, by first-touch source or campaign */
export function sourceRoi(args: SourceRoiArgs, options?: RequestOptions): Promise<SourceRoiResult> {
  return request<SourceRoiResult>({
    operationId: "sourceRoi",
    method: "GET",
    path: "/analytics/source-roi",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface SsoCallbackArgs {
  body: {
  state: string;
  code: string;
};
}

export type SsoCallbackResult = {
  status: "signed-in";
  returnTo: (string | null);
  provisioned: boolean;
  expiresAt: string;
};

/** Complete a single sign-on and open a session */
export function ssoCallback(args: SsoCallbackArgs, options?: RequestOptions): Promise<SsoCallbackResult> {
  return request<SsoCallbackResult>({
    operationId: "ssoCallback",
    method: "POST",
    path: "/auth/sso/callback",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export type SsoSettingsResult = {
  connection: (S.SsoConnection | null);
  scimTokens: Array<S.ScimToken>;
};

/** Read the workspace’s identity provider settings */
export function ssoSettings(options?: RequestOptions): Promise<SsoSettingsResult> {
  return request<SsoSettingsResult>({
    operationId: "ssoSettings",
    method: "GET",
    path: "/settings/sso",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface SsoStartArgs {
  body: {
  email: string;
  returnTo?: (string | null);
  redirectUri: string;
};
}

export type SsoStartResult = S.SsoStartOutcome;

/** Begin a single sign-on for an email address */
export function ssoStart(args: SsoStartArgs, options?: RequestOptions): Promise<SsoStartResult> {
  return request<SsoStartResult>({
    operationId: "ssoStart",
    method: "POST",
    path: "/auth/sso/start",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface StageChecklistArgs {
  path: { dealId: string };
}

export type StageChecklistResult = S.StageChecklist;

/** What this deal still needs before it can move on */
export function stageChecklist(args: StageChecklistArgs, options?: RequestOptions): Promise<StageChecklistResult> {
  return request<StageChecklistResult>({
    operationId: "stageChecklist",
    method: "GET",
    path: "/deals/{dealId}/stage-checklist",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface StartAccountOnboardingArgs {
  path: { accountId: string };
  body: Record<string, never>;
}

export type StartAccountOnboardingResult = {
  plan: (S.OnboardingPlan | null);
  reason: (string | null);
};

/** Start onboarding by hand, for a customer won before the checklist existed */
export function startAccountOnboarding(args: StartAccountOnboardingArgs, options?: RequestOptions): Promise<StartAccountOnboardingResult> {
  return request<StartAccountOnboardingResult>({
    operationId: "startAccountOnboarding",
    method: "POST",
    path: "/accounts/{accountId}/onboarding",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface StartChatArgs {
  body: {
  token: string;
  visitor: string;
};
}

export type StartChatResult = S.ChatTurn;

/** A visitor opens the chat; the chat script speaks first */
export function startChat(args: StartChatArgs, options?: RequestOptions): Promise<StartChatResult> {
  return request<StartChatResult>({
    operationId: "startChat",
    method: "POST",
    path: "/embed/chat/start",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface StartDealFromTemplateArgs {
  body: {
  templateId: string;
  name: string;
  accountId: (string | null);
};
}

export type StartDealFromTemplateResult = {
  dealId: string;
  closeDate: (string | null);
};

/** Start a deal from a template */
export function startDealFromTemplate(args: StartDealFromTemplateArgs, options?: RequestOptions): Promise<StartDealFromTemplateResult> {
  return request<StartDealFromTemplateResult>({
    operationId: "startDealFromTemplate",
    method: "POST",
    path: "/deals/from-template",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SubmitBulkJobArgs {
  body: {
  objectKey: string;
  operation: "create" | "update" | "delete";
  rows: Array<{
    [key: string]: (string | number | boolean | null);
  }>;
};
}

export type SubmitBulkJobResult = S.BulkJob;

/** Hand over high-volume work and get a job id */
export function submitBulkJob(args: SubmitBulkJobArgs, options?: RequestOptions): Promise<SubmitBulkJobResult> {
  return request<SubmitBulkJobResult>({
    operationId: "submitBulkJob",
    method: "POST",
    path: "/bulk/jobs",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SubmitCaptureFormArgs {
  body: {
  token: string;
  values: {
    [key: string]: string;
  };
  consent?: boolean;
  filledInSeconds?: number;
  utm?: {
    [key: string]: string;
  };
  referrer?: (string | null);
};
}

export type SubmitCaptureFormResult = {
  outcome: "created" | "matched" | "spam" | "no-consent" | "invalid";
  accepted: boolean;
  message: string;
  messageKey: (string | null);
  field: (string | null);
};

/** Somebody filled the form in */
export function submitCaptureForm(args: SubmitCaptureFormArgs, options?: RequestOptions): Promise<SubmitCaptureFormResult> {
  return request<SubmitCaptureFormResult>({
    operationId: "submitCaptureForm",
    method: "POST",
    path: "/embed/form",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SubmitForApprovalArgs {
  path: { objectKey: string; recordId: string };
  body: {
  processId: string;
  comment: (string | null);
};
}

export type SubmitForApprovalResult = S.ApprovalChain;

/** Submit a record to an approval process */
export function submitForApproval(args: SubmitForApprovalArgs, options?: RequestOptions): Promise<SubmitForApprovalResult> {
  return request<SubmitForApprovalResult>({
    operationId: "submitForApproval",
    method: "POST",
    path: "/records/{objectKey}/{recordId}/approvals",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SubmitForecastArgs {
  body: {
  userId?: string;
  periodKind: "month" | "quarter" | "year";
  periodStart: string;
  periodEnd: string;
  amount: string;
  note?: (string | null);
};
}

export type SubmitForecastResult = S.ForecastSubmission;

/** Submit a forecast for a period */
export function submitForecast(args: SubmitForecastArgs, options?: RequestOptions): Promise<SubmitForecastResult> {
  return request<SubmitForecastResult>({
    operationId: "submitForecast",
    method: "PUT",
    path: "/forecast/submissions",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SubmitPageFormArgs {
  path: { workspace: string; slug: string };
  body: {
  values: {
    [key: string]: string;
  };
  consent?: boolean;
  utm?: {
    [key: string]: string;
  };
  referrer?: (string | null);
  filledInSeconds?: number;
  visitor?: (string | null);
};
}

export type SubmitPageFormResult = {
  accepted: boolean;
  message: string;
  messageKey: (string | null);
  field: (string | null);
  visitor: (string | null);
  found: boolean;
};

/** A visitor sends a landing page's form, with the page's UTM parameters */
export function submitPageForm(args: SubmitPageFormArgs, options?: RequestOptions): Promise<SubmitPageFormResult> {
  return request<SubmitPageFormResult>({
    operationId: "submitPageForm",
    method: "POST",
    path: "/public/pages/{workspace}/{slug}/submit",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SubscribePushArgs {
  body: {
  endpoint: string;
  p256dh: string;
  auth: string;
  deviceLabel?: (string | null);
};
}

export type SubscribePushResult = {
  subscribed: true;
};

/** Get alerts on this device */
export function subscribePush(args: SubscribePushArgs, options?: RequestOptions): Promise<SubscribePushResult> {
  return request<SubscribePushResult>({
    operationId: "subscribePush",
    method: "POST",
    path: "/me/push/subscriptions",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SuccessPlanArgs {
  path: { accountId: string };
}

export type SuccessPlanResult = S.SuccessPlan;

/** A customer's objectives and business reviews, and when the next review is due */
export function successPlan(args: SuccessPlanArgs, options?: RequestOptions): Promise<SuccessPlanResult> {
  return request<SuccessPlanResult>({
    operationId: "successPlan",
    method: "GET",
    path: "/accounts/{accountId}/success-plan",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface SuggestedArticlesArgs {
  path: { caseId: string };
}

export type SuggestedArticlesResult = {
  articles: Array<{
    articleId: string;
    title: string;
    audience: "internal" | "public";
  }>;
};

/** Articles that might answer this case */
export function suggestedArticles(args: SuggestedArticlesArgs, options?: RequestOptions): Promise<SuggestedArticlesResult> {
  return request<SuggestedArticlesResult>({
    operationId: "suggestedArticles",
    method: "GET",
    path: "/cases/{caseId}/articles",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface SuggestImportMappingArgs {
  body: {
  source: "salesforce" | "hubspot" | "zoho" | "pipedrive" | "other";
  objectKey: "account" | "contact" | "lead";
  headers: Array<string>;
};
}

export type SuggestImportMappingResult = {
  mapping: {
    [key: string]: string;
  };
  unmatched: Array<string>;
};

/** Map another CRM's export columns onto this workspace's fields, naming what was left unmatched */
export function suggestImportMapping(args: SuggestImportMappingArgs, options?: RequestOptions): Promise<SuggestImportMappingResult> {
  return request<SuggestImportMappingResult>({
    operationId: "suggestImportMapping",
    method: "POST",
    path: "/setup/imports/suggest-mapping",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SuppressAddressArgs {
  body: {
  channel: "email" | "sms" | "whatsapp" | "push";
  address: string;
  reason: "unsubscribed" | "bounced" | "complained" | "manual" | "invalid";
  detail?: (string | null);
};
}

export type SuppressAddressResult = {
  suppressionId: string;
};

/** Stop sending to an address */
export function suppressAddress(args: SuppressAddressArgs, options?: RequestOptions): Promise<SuppressAddressResult> {
  return request<SuppressAddressResult>({
    operationId: "suppressAddress",
    method: "POST",
    path: "/suppressions",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface SurveyReportArgs {
  query?: { days?: number };
}

export type SurveyReportResult = {
  kind: "csat" | "nps" | "ces";
  sent: number;
  answered: number;
  score: (number | null);
  byAgent: Array<{
    agentId: (string | null);
    agentName: (string | null);
    answered: number;
    score: (number | null);
  }>;
};

/** How customers rated resolved cases, overall and per agent */
export function surveyReport(args: SurveyReportArgs, options?: RequestOptions): Promise<SurveyReportResult> {
  return request<SurveyReportResult>({
    operationId: "surveyReport",
    method: "GET",
    path: "/service/survey/report",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type SurveySettingResult = {
  kind: "csat" | "nps" | "ces";
  active: boolean;
};

/** Which satisfaction measure is asked after a case is resolved, if any */
export function surveySetting(options?: RequestOptions): Promise<SurveySettingResult> {
  return request<SurveySettingResult>({
    operationId: "surveySetting",
    method: "GET",
    path: "/service/survey",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type SweepRenewalsResult = {
  reminded: Array<{
    subscriptionId: string;
    number: string;
    daysLeft: number;
    autoRenews: boolean;
  }>;
  renewed: Array<{
    subscriptionId: string;
    number: string;
    endsOn: string;
  }>;
  ended: Array<{
    subscriptionId: string;
    number: string;
  }>;
};

/** Warn about what is coming, renew what renews, end what does not */
export function sweepRenewals(options?: RequestOptions): Promise<SweepRenewalsResult> {
  return request<SweepRenewalsResult>({
    operationId: "sweepRenewals",
    method: "POST",
    path: "/subscriptions/renewals",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface TargetAccountArgs {
  path: { listId: string };
  body: {
  accountId: string;
  tier: number;
};
}

export type TargetAccountResult = {
  done: true;
};

/** Put a company on the list, or change its tier */
export function targetAccount(args: TargetAccountArgs, options?: RequestOptions): Promise<TargetAccountResult> {
  return request<TargetAccountResult>({
    operationId: "targetAccount",
    method: "POST",
    path: "/target-lists/{listId}/accounts",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface TargetAudienceArgs {
  path: { listId: string };
  body: {
  maxTier?: number;
};
}

export type TargetAudienceResult = {
  segmentId: string;
  members: number;
};

/** Freeze the people at the list's companies into an audience */
export function targetAudience(args: TargetAudienceArgs, options?: RequestOptions): Promise<TargetAudienceResult> {
  return request<TargetAudienceResult>({
    operationId: "targetAudience",
    method: "POST",
    path: "/target-lists/{listId}/audience",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface TargetOverviewArgs {
  path: { listId: string };
}

export type TargetOverviewResult = {
  currency: string;
  accounts: Array<{
    accountId: string;
    name: string;
    tier: number;
    ownerName: (string | null);
    people: number;
    engagedPeople: number;
    lastTouchAt: (string | null);
    openPipeline: string;
  }>;
};

/** Each target company: people known, people engaged in 30 days, last touch, open pipeline */
export function targetOverview(args: TargetOverviewArgs, options?: RequestOptions): Promise<TargetOverviewResult> {
  return request<TargetOverviewResult>({
    operationId: "targetOverview",
    method: "GET",
    path: "/target-lists/{listId}/overview",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface TestAgentArgs {
  path: { agentId: string };
  body: {
  prompt: string;
};
}

export type TestAgentResult = {
  runId: string;
  status: "completed" | "failed" | "refused";
  answer: (string | null);
  steps: Array<S.AgentTraceStep>;
  proposalId: (string | null);
  cost: {
    inputTokens: number;
    outputTokens: number;
    amount: string;
    currency: string;
  };
};

/** Ask the agent something, in the builder */
export function testAgent(args: TestAgentArgs, options?: RequestOptions): Promise<TestAgentResult> {
  return request<TestAgentResult>({
    operationId: "testAgent",
    method: "POST",
    path: "/agents/{agentId}/test",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface TestFlowArgs {
  path: { flowId: string };
  body: {
  record: {
    [key: string]: S.ConfigValue;
  };
};
}

export type TestFlowResult = {
  runId: string;
  status: string;
  outcome: string;
  steps: Array<S.FlowRunStep>;
};

/** Run a flow against one record, changing nothing */
export function testFlow(args: TestFlowArgs, options?: RequestOptions): Promise<TestFlowResult> {
  return request<TestFlowResult>({
    operationId: "testFlow",
    method: "POST",
    path: "/flows/{flowId}/test-runs",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface TestPluginArgs {
  path: { pluginId: string };
  body: {
  value?: {
    [key: string]: (string | number | boolean | null);
  };
};
}

export type TestPluginResult = {
  outcome: "ok" | "failed" | "timed-out" | "refused";
  output: (string | null);
  logs: Array<string>;
  error: (string | null);
  durationMs: number;
};

/** Run a plugin once, with an input you choose */
export function testPlugin(args: TestPluginArgs, options?: RequestOptions): Promise<TestPluginResult> {
  return request<TestPluginResult>({
    operationId: "testPlugin",
    method: "POST",
    path: "/setup/plugins/{pluginId}/test",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface TestWebhookArgs {
  path: { endpointId: string };
  body: Record<string, never>;
}

export type TestWebhookResult = {
  status: "pending" | "succeeded" | "failed" | "dead";
};

/** Send a test event now */
export function testWebhook(args: TestWebhookArgs, options?: RequestOptions): Promise<TestWebhookResult> {
  return request<TestWebhookResult>({
    operationId: "testWebhook",
    method: "POST",
    path: "/developer/webhooks/{endpointId}/test",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface TrackBehaviourArgs {
  path: { workspace: string };
  body: ({
  type: "page_view";
  visitorId: string;
  url: string;
  title?: (string | null);
  referrer?: (string | null);
  utm?: {
    [key: string]: string;
  };
  seconds?: (number | null);
} | {
  type: "custom";
  visitorId: string;
  name: string;
  properties?: {
    [key: string]: string;
  };
} | {
  type: "identify";
  visitorId: string;
  email: string;
});
}

export type TrackBehaviourResult = {
  accepted: boolean;
};

/** The tracking contract: a page view, a custom event, or who the visitor is */
export function trackBehaviour(args: TrackBehaviourArgs, options?: RequestOptions): Promise<TrackBehaviourResult> {
  return request<TrackBehaviourResult>({
    operationId: "trackBehaviour",
    method: "POST",
    path: "/public/track/{workspace}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export type TrackingSettingsResult = S.TrackingSettings;

/** How locations are resolved, and how long addresses are kept */
export function trackingSettings(options?: RequestOptions): Promise<TrackingSettingsResult> {
  return request<TrackingSettingsResult>({
    operationId: "trackingSettings",
    method: "GET",
    path: "/setup/tracking",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface TrainPredictionArgs {
  body: Record<string, never>;
}

export type TrainPredictionResult = {
  ready: boolean;
  closed: number;
  positives: number;
  negatives: number;
  needed: number;
  trainedAt: (string | null);
  auc: (number | null);
};

/** Retrain now and rescore every open lead */
export function trainPrediction(args: TrainPredictionArgs, options?: RequestOptions): Promise<TrainPredictionResult> {
  return request<TrainPredictionResult>({
    operationId: "trainPrediction",
    method: "POST",
    path: "/leads/prediction/train",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UnitEconomicsArgs {
  query: { from: string; to: string };
}

export type UnitEconomicsResult = {
  economics: S.UnitEconomics;
};

/** Lifetime value, cost of acquisition, and what they do not include */
export function unitEconomics(args: UnitEconomicsArgs, options?: RequestOptions): Promise<UnitEconomicsResult> {
  return request<UnitEconomicsResult>({
    operationId: "unitEconomics",
    method: "GET",
    path: "/revenue/unit-economics",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export interface UnpublishLandingPageArgs {
  path: { pageId: string };
  body: Record<string, never>;
}

export type UnpublishLandingPageResult = S.LandingPage;

/** Take a page down; its address then answers as if it never existed */
export function unpublishLandingPage(args: UnpublishLandingPageArgs, options?: RequestOptions): Promise<UnpublishLandingPageResult> {
  return request<UnpublishLandingPageResult>({
    operationId: "unpublishLandingPage",
    method: "POST",
    path: "/landing-pages/{pageId}/unpublish",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UnsubscribeFromEverythingArgs {
  body: {
  token: string;
};
}

export type UnsubscribeFromEverythingResult = {
  unsubscribed: boolean;
  refusal: (S.PortalRefusal | null);
};

/** Stop everything, in one click */
export function unsubscribeFromEverything(args: UnsubscribeFromEverythingArgs, options?: RequestOptions): Promise<UnsubscribeFromEverythingResult> {
  return request<UnsubscribeFromEverythingResult>({
    operationId: "unsubscribeFromEverything",
    method: "POST",
    path: "/portal/unsubscribe",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UnsubscribePushArgs {
  body: {
  endpoint: string;
};
}

export type UnsubscribePushResult = {
  removed: number;
};

/** Stop alerts on this device */
export function unsubscribePush(args: UnsubscribePushArgs, options?: RequestOptions): Promise<UnsubscribePushResult> {
  return request<UnsubscribePushResult>({
    operationId: "unsubscribePush",
    method: "POST",
    path: "/me/push/unsubscribe",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UntargetAccountArgs {
  path: { listId: string };
  body: {
  accountId: string;
};
}

export type UntargetAccountResult = {
  done: true;
};

/** Take a company off the list */
export function untargetAccount(args: UntargetAccountArgs, options?: RequestOptions): Promise<UntargetAccountResult> {
  return request<UntargetAccountResult>({
    operationId: "untargetAccount",
    method: "POST",
    path: "/target-lists/{listId}/accounts/remove",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UpdateAccountAddressArgs {
  path: { accountId: string; addressId: string };
  body: {
  kind: "billing" | "shipping" | "site" | "branch";
  label?: (string | null);
  line1: string;
  line2?: (string | null);
  city?: (string | null);
  region?: (string | null);
  postcode?: (string | null);
  country: string;
  makeDefault?: boolean;
};
}

export type UpdateAccountAddressResult = {
  address: S.AccountAddress;
};

/** Change an address */
export function updateAccountAddress(args: UpdateAccountAddressArgs, options?: RequestOptions): Promise<UpdateAccountAddressResult> {
  return request<UpdateAccountAddressResult>({
    operationId: "updateAccountAddress",
    method: "PUT",
    path: "/accounts/{accountId}/addresses/{addressId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UpdateAssetArgs {
  path: { assetId: string };
  body: {
  name: string;
  tags: Array<string>;
  folderId: (string | null);
};
}

export type UpdateAssetResult = S.Asset;

/** Rename an asset, change its tags, or move it to another folder */
export function updateAsset(args: UpdateAssetArgs, options?: RequestOptions): Promise<UpdateAssetResult> {
  return request<UpdateAssetResult>({
    operationId: "updateAsset",
    method: "POST",
    path: "/assets/{assetId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UpdateContactArgs {
  path: { contactId: string };
  body: {
  firstName?: string;
  lastName?: string;
  title?: (string | null);
  accountId?: (string | null);
  ownerId?: (string | null);
};
}

export type UpdateContactResult = {
  updated: true;
};

/** Change a contact */
export function updateContact(args: UpdateContactArgs, options?: RequestOptions): Promise<UpdateContactResult> {
  return request<UpdateContactResult>({
    operationId: "updateContact",
    method: "PATCH",
    path: "/contacts/{contactId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UpdateJobScheduleArgs {
  path: { scheduleId: string };
  body: {
  cron: string;
  enabled: boolean;
};
}

export type UpdateJobScheduleResult = S.JobSchedule;

/** Change when a job runs, or switch it off */
export function updateJobSchedule(args: UpdateJobScheduleArgs, options?: RequestOptions): Promise<UpdateJobScheduleResult> {
  return request<UpdateJobScheduleResult>({
    operationId: "updateJobSchedule",
    method: "POST",
    path: "/setup/jobs/{scheduleId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UpdateMyPreferencesArgs {
  body: {
  language?: "en" | "ar";
  locale?: string;
  timezone?: string;
  numerals?: "latn" | "arab";
  theme?: "light" | "dark" | "system";
  density?: "simple" | "advanced";
  navOrder?: Array<string>;
  navCollapsed?: boolean;
};
}

export type UpdateMyPreferencesResult = S.Session;

/** Change language, theme, density or navigation order */
export function updateMyPreferences(args: UpdateMyPreferencesArgs, options?: RequestOptions): Promise<UpdateMyPreferencesResult> {
  return request<UpdateMyPreferencesResult>({
    operationId: "updateMyPreferences",
    method: "PATCH",
    path: "/me/preferences",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UpdatePermissionSetArgs {
  path: { permissionSetId: string };
  body: {
  name: string;
  description?: string;
  objects?: Array<{
    objectKey: string;
    actions: Array<"read" | "create" | "update" | "delete" | "export">;
  }>;
  capabilities?: Array<string>;
};
}

export type UpdatePermissionSetResult = S.PermissionSetSummary;

/** Change what a custom set allows */
export function updatePermissionSet(args: UpdatePermissionSetArgs, options?: RequestOptions): Promise<UpdatePermissionSetResult> {
  return request<UpdatePermissionSetResult>({
    operationId: "updatePermissionSet",
    method: "PUT",
    path: "/setup/permission-sets/{permissionSetId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UpdateProductArgs {
  path: { productId: string };
  body: {
  sku: string;
  name: string;
  description?: (string | null);
  family?: (string | null);
  listPrice: string;
  currency: string;
  unit: string;
  active?: boolean;
};
}

export type UpdateProductResult = {
  product: S.Product;
};

/** Change a product */
export function updateProduct(args: UpdateProductArgs, options?: RequestOptions): Promise<UpdateProductResult> {
  return request<UpdateProductResult>({
    operationId: "updateProduct",
    method: "PUT",
    path: "/products/{productId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UpdateProductVariantArgs {
  path: { productId: string; variantId: string };
  body: {
  sku?: string;
  priceAdjustment?: string;
  active?: boolean;
};
}

export type UpdateProductVariantResult = {
  variantId: string;
  sku: string;
  attributes: {
    [key: string]: string;
  };
  priceAdjustment: string;
  active: boolean;
};

/** Change one variant-s SKU, price adjustment or availability */
export function updateProductVariant(args: UpdateProductVariantArgs, options?: RequestOptions): Promise<UpdateProductVariantResult> {
  return request<UpdateProductVariantResult>({
    operationId: "updateProductVariant",
    method: "PATCH",
    path: "/products/{productId}/variants/{variantId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UpdateSegmentArgs {
  path: { segmentId: string };
  body: {
  name: string;
  objectKey: string;
  conditions: S.RuleDocument;
};
}

export type UpdateSegmentResult = {
  segment: S.Segment;
};

/** Change a saved audience */
export function updateSegment(args: UpdateSegmentArgs, options?: RequestOptions): Promise<UpdateSegmentResult> {
  return request<UpdateSegmentResult>({
    operationId: "updateSegment",
    method: "PUT",
    path: "/segments/{segmentId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UpdateViewArgs {
  path: { viewId: string };
  body: {
  name: string;
  definition: S.ViewDefinition;
  visibility: "private" | "team" | "everyone";
  teamId?: (string | null);
};
}

export type UpdateViewResult = S.SavedView;

/** Change a saved view */
export function updateView(args: UpdateViewArgs, options?: RequestOptions): Promise<UpdateViewResult> {
  return request<UpdateViewResult>({
    operationId: "updateView",
    method: "PATCH",
    path: "/views/detail/{viewId}",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface UploadAttachmentArgs {
  path: { objectKey: string; recordId: string };
  body: {
  filename: string;
  contentType: string;
  contentBase64: string;
};
}

export type UploadAttachmentResult = S.Attachment;

/** Attach a file to a record */
export function uploadAttachment(args: UploadAttachmentArgs, options?: RequestOptions): Promise<UploadAttachmentResult> {
  return request<UploadAttachmentResult>({
    operationId: "uploadAttachment",
    method: "POST",
    path: "/records/{objectKey}/{recordId}/files",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export type UsageMetersResult = {
  meters: Array<S.MeterReading>;
};

/** What this workspace is using, against what its plan allows */
export function usageMeters(options?: RequestOptions): Promise<UsageMetersResult> {
  return request<UsageMetersResult>({
    operationId: "usageMeters",
    method: "GET",
    path: "/setup/usage",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface VerifyBackupArgs {
  path: { backupId: string };
}

export type VerifyBackupResult = S.Backup;

/** Read a backup back and check it against what was recorded */
export function verifyBackup(args: VerifyBackupArgs, options?: RequestOptions): Promise<VerifyBackupResult> {
  return request<VerifyBackupResult>({
    operationId: "verifyBackup",
    method: "POST",
    path: "/setup/backups/{backupId}/verify",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface VerifyMfaCodeArgs {
  body: {
  challenge: string;
  code: string;
};
}

export type VerifyMfaCodeResult = S.MfaOutcome;

/** Complete a sign-in with a verification or recovery code */
export function verifyMfaCode(args: VerifyMfaCodeArgs, options?: RequestOptions): Promise<VerifyMfaCodeResult> {
  return request<VerifyMfaCodeResult>({
    operationId: "verifyMfaCode",
    method: "POST",
    path: "/auth/mfa",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface VerifySendingDomainArgs {
  path: { domainId: string };
  body: Record<string, never>;
}

export type VerifySendingDomainResult = {
  domainId: string;
  domain: string;
  status: "pending" | "verified" | "failed";
  lastCheckedAt: (string | null);
  records: Array<{
    recordId: string;
    kind: "spf" | "dkim" | "dmarc" | "return-path";
    host: string;
    recordType: "TXT" | "CNAME" | "MX";
    expectedValue: string;
    observedValue: (string | null);
    verified: boolean;
    checkedAt: (string | null);
    explanation: string;
  }>;
};

/** Check every record against DNS now, and say exactly which one is wrong */
export function verifySendingDomain(args: VerifySendingDomainArgs, options?: RequestOptions): Promise<VerifySendingDomainResult> {
  return request<VerifySendingDomainResult>({
    operationId: "verifySendingDomain",
    method: "POST",
    path: "/marketing/sending-domains/{domainId}/verify",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export type ViewingsNeedingOutcomeResult = {
  viewings: Array<S.Viewing>;
};

/** My past viewings nobody has said the outcome of */
export function viewingsNeedingOutcome(options?: RequestOptions): Promise<ViewingsNeedingOutcomeResult> {
  return request<ViewingsNeedingOutcomeResult>({
    operationId: "viewingsNeedingOutcome",
    method: "GET",
    path: "/viewings/needing-outcome",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface VisitAnswersArgs {
  path: { visitId: string };
}

export type VisitAnswersResult = {
  answered: Array<S.AnsweredVisitForm>;
};

/** What was filled in at this visit */
export function visitAnswers(args: VisitAnswersArgs, options?: RequestOptions): Promise<VisitAnswersResult> {
  return request<VisitAnswersResult>({
    operationId: "visitAnswers",
    method: "GET",
    path: "/visits/{visitId}/forms",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface VisitorChatArgs {
  body: {
  token: string;
  visitor: string;
  conversationId: string;
};
}

export type VisitorChatResult = S.ChatTurn;

/** The visitor's chat as it stands, for polling while a person answers */
export function visitorChat(args: VisitorChatArgs, options?: RequestOptions): Promise<VisitorChatResult> {
  return request<VisitorChatResult>({
    operationId: "visitorChat",
    method: "POST",
    path: "/embed/chat/read",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface VisitorSaysArgs {
  body: {
  token: string;
  visitor: string;
  conversationId: string;
  text: string;
};
}

export type VisitorSaysResult = S.ChatTurn;

/** The visitor says something; the bot answers while it is still handling the chat */
export function visitorSays(args: VisitorSaysArgs, options?: RequestOptions): Promise<VisitorSaysResult> {
  return request<VisitorSaysResult>({
    operationId: "visitorSays",
    method: "POST",
    path: "/embed/chat/say",
    pathParams: undefined,
    query: undefined,
    body: args.body,
  }, options);
}

export interface VoidInvoiceArgs {
  path: { invoiceId: string };
  body: {
  reason: string;
};
}

export type VoidInvoiceResult = {
  invoice: S.Invoice;
};

/** Void an invoice, with a reason */
export function voidInvoice(args: VoidInvoiceArgs, options?: RequestOptions): Promise<VoidInvoiceResult> {
  return request<VoidInvoiceResult>({
    operationId: "voidInvoice",
    method: "POST",
    path: "/invoices/{invoiceId}/void",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface VoidProformaArgs {
  path: { proformaId: string };
  body: {
  reason: string;
};
}

export type VoidProformaResult = {
  proforma: S.Proforma;
};

/** Withdraw a pro-forma, with a reason */
export function voidProforma(args: VoidProformaArgs, options?: RequestOptions): Promise<VoidProformaResult> {
  return request<VoidProformaResult>({
    operationId: "voidProforma",
    method: "POST",
    path: "/proformas/{proformaId}/void",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export interface WebhookDeliveriesArgs {
  path: { endpointId: string };
}

export type WebhookDeliveriesResult = {
  deliveries: Array<S.WebhookDelivery>;
};

/** The delivery log: payload, every attempt with status code, response and timing */
export function webhookDeliveries(args: WebhookDeliveriesArgs, options?: RequestOptions): Promise<WebhookDeliveriesResult> {
  return request<WebhookDeliveriesResult>({
    operationId: "webhookDeliveries",
    method: "GET",
    path: "/developer/webhooks/{endpointId}/deliveries",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface WebsiteActivityArgs {
  path: { objectKey: string; recordId: string };
}

export type WebsiteActivityResult = {
  sessions: Array<{
    sessionId: string;
    startedAt: string;
    lastAt: string;
    referrer: (string | null);
    pageCount: number;
    seconds: number;
    pages: Array<{
      url: string;
      title: (string | null);
      viewedAt: string;
      seconds: (number | null);
    }>;
  }>;
  firstSeen: (string | null);
  lastSeen: (string | null);
};

/** The website sessions of a contact or lead, including those from before they identified */
export function websiteActivity(args: WebsiteActivityArgs, options?: RequestOptions): Promise<WebsiteActivityResult> {
  return request<WebsiteActivityResult>({
    operationId: "websiteActivity",
    method: "GET",
    path: "/records/{objectKey}/{recordId}/website-activity",
    pathParams: args.path,
    query: undefined,
    body: undefined,
  }, options);
}

export interface WinLossArgs {
  query: { from: string; to: string };
}

export type WinLossResult = {
  won: number;
  lost: number;
  lossReasons: Array<{
    reason: string;
    count: number;
  }>;
  winReasons: Array<{
    reason: string;
    count: number;
  }>;
  competitors: Array<{
    name: string;
    wonAgainst: number;
    lostTo: number;
    winRate: (number | null);
  }>;
};

/** Why deals were won and lost, and the record against each competitor */
export function winLoss(args: WinLossArgs, options?: RequestOptions): Promise<WinLossResult> {
  return request<WinLossResult>({
    operationId: "winLoss",
    method: "GET",
    path: "/analytics/win-loss",
    pathParams: undefined,
    query: args.query,
    body: undefined,
  }, options);
}

export type WithdrawSupportAccessResult = S.SupportAccessWithdrawn;

/** Withdraw support access, ending any visit in progress */
export function withdrawSupportAccess(options?: RequestOptions): Promise<WithdrawSupportAccessResult> {
  return request<WithdrawSupportAccessResult>({
    operationId: "withdrawSupportAccess",
    method: "DELETE",
    path: "/setup/support-access",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export interface WorkCallListEntryArgs {
  path: { entryId: string };
  body: {
  outcome: "connected" | "no-answer" | "voicemail" | "wrong-number" | "busy" | "callback-requested";
  subject: string;
  notes: (string | null);
  seconds: (number | null);
};
}

export type WorkCallListEntryResult = {
  list: S.CallList;
  next: (S.CallListEntry | null);
};

/** Log the call and move on */
export function workCallListEntry(args: WorkCallListEntryArgs, options?: RequestOptions): Promise<WorkCallListEntryResult> {
  return request<WorkCallListEntryResult>({
    operationId: "workCallListEntry",
    method: "POST",
    path: "/call-lists/entries/{entryId}/worked",
    pathParams: args.path,
    query: undefined,
    body: args.body,
  }, options);
}

export type WorkspaceHealthResult = {
  signals: Array<S.HealthSignal>;
  windowHours: number;
};

/** What is failing in this workspace, and what is not measured */
export function workspaceHealth(options?: RequestOptions): Promise<WorkspaceHealthResult> {
  return request<WorkspaceHealthResult>({
    operationId: "workspaceHealth",
    method: "GET",
    path: "/setup/health",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}

export type WorkspaceSessionsResult = {
  sessions: Array<{
    sessionId: string;
    userId: string;
    userName: string;
    email: string;
    deviceLabel: (string | null);
    userAgent: (string | null);
    ip: (string | null);
    createdAt: string;
    lastSeenAt: string;
    impersonated: boolean;
    current: boolean;
  }>;
};

/** Everyone signed in: device, address, when first and last seen */
export function workspaceSessions(options?: RequestOptions): Promise<WorkspaceSessionsResult> {
  return request<WorkspaceSessionsResult>({
    operationId: "workspaceSessions",
    method: "GET",
    path: "/setup/sessions",
    pathParams: undefined,
    query: undefined,
    body: undefined,
  }, options);
}
